import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { announceSub } from '../../utils/navSignal';
import api from '../../utils/api';
import { EFFECT_IN_OPTIONS, EFFECT_STAY_OPTIONS, EFFECT_OUT_OPTIONS } from '../../utils/animationCatalog';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import BrandNav from '../../components/common/BrandNav';
import BrandSendToTVModal from '../../components/SendToTVModal';
import {
  DESIGN, isLightBg, makeTokens, fmtP, tr,
  adjustZones, parseZones, gridToFreeZones,
  ZoneItemList, ZoneItemGrid, ZonePreview, SlidePreview,
  renderSlideToCanvas,
} from '../../utils/slideRenderer';

// ── Color presets ─────────────────────────────────────────────────────────────
const COLOR_PRESETS = [
  { bg: '#ffffff', ac: '#2196F3' }, { bg: '#ffffff', ac: '#E53935' },
  { bg: '#ffffff', ac: '#FF6F00' }, { bg: '#ffffff', ac: '#2E7D32' },
  { bg: '#ffffff', ac: '#6A1B9A' }, { bg: '#ffffff', ac: '#00838F' },
  { bg: '#FFF8E1', ac: '#E65100' }, { bg: '#E8F5E9', ac: '#1B5E20' },
  { bg: '#E3F2FD', ac: '#0D47A1' }, { bg: '#FCE4EC', ac: '#880E4F' },
  { bg: '#111827', ac: '#F59E0B' }, { bg: '#0F172A', ac: '#38BDF8' },
  { bg: '#1A1A2E', ac: '#E94560' }, { bg: '#064E3B', ac: '#34D399' },
  { bg: '#0a0f1e', ac: '#FFD700' }, { bg: '#1C1C1C', ac: '#E5E5E5' },
  { bg: '#18120a', ac: '#FF6B35' }, { bg: '#1a0a1e', ac: '#F472B6' },
];

// (rendering helpers + components imported from ../../utils/slideRenderer)

function sanitize(s) {
  return (s || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '') || 'slide';
}

function computeSlideDisplayName(screenLabel, customName, idx) {
  if (customName && customName.trim()) return customName.trim();
  return `${screenLabel || 'slide'}-slide${String(idx + 1).padStart(2, '0')}`;
}

function computeSlideFileName(screenLabel, customName, idx) {
  const base = customName && customName.trim()
    ? sanitize(customName.trim())
    : `${sanitize(screenLabel)}-slide${String(idx + 1).padStart(2, '00')}`;
  return base + '.jpg';
}

function EMPTY_ZONE(idx) {
  return {
    id: `z${idx + 1}_${Date.now() + idx}`,
    name: '', name_ar: '',
    item_ids: [], cols: 1, rows: 4,
    display_mode: 'list',
    show_name: true, show_photos: true, show_price: true, show_desc: false, show_placeholder: true,
    name_font: '', price_font: '', desc_font: '', header_font: '',
    name_font_family: '',
    placeholder_opacity: 100,
    bg_image: '', bg_overlay: 50,
    // Phase 1 — free-layout fields (only used when slide.zone_mode === 'free')
    x: 0, y: 0, w: 0, h: 0, z_index: idx, locked: false,
    // Phase 2 — appearance (already wired)
    bg_color: '', bg_color_opacity: 100,
    border_color: '', border_width: 0, border_radius: 0,
    // Phase 3 — text layers (replaces zone name as on-canvas text)
    text_layers: [],
  };
}

function EMPTY_TEXT_LAYER(label) {
  return {
    id: `tl_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
    label: label || '', // t1, t2, … — set by caller when slide-level
    name: '',           // memo / identification only — not rendered on canvas
    text: 'Title',      // multi-line free text (auto-detects RTL)
    x: 12, y: 8, w: 200, h: 50,
    font_family: 'Cairo, sans-serif',
    font_size: 28,
    font_weight: 'bold',
    font_style: 'normal',
    color: '#000000',
    align: 'center',
    opacity: 100,
    letter_spacing: 0,
    text_shadow: '',
  };
}

// Next unused t<N> label, never re-using a number that was deleted.
function nextTextLabel(existing) {
  let maxN = 0;
  for (const l of existing || []) {
    const m = /^t(\d+)$/.exec(l?.label || '');
    if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
  }
  return `t${maxN + 1}`;
}

// ── Compact %-input (2026-05-16) ──────────────────────────────────────────────
// Replaces the various 0–100% sliders sprinkled around the builder with a
// tight numeric input. Same visual footprint as a 2-digit number + "%" suffix,
// roughly half the width of a slider and easier to type a precise value into.
function PctInput({ value, onChange, min = 0, max = 100, width = 38, label = '%' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <input type="number" min={min} max={max} step="1"
        value={Math.round(Number(value) || 0)}
        onChange={e => {
          if (e.target.value === '') return;
          const v = parseInt(e.target.value, 10);
          if (isNaN(v)) return;
          onChange(Math.max(min, Math.min(max, v)));
        }}
        onBlur={e => {
          if (e.target.value === '') onChange(0);
        }}
        style={{
          width, padding: '3px 4px', border: '1.5px solid #D0D0D0', borderRadius: 4,
          fontSize: 11, fontWeight: 600, textAlign: 'center', background: '#fff', color: '#222',
        }} />
      <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>{label}</span>
    </span>
  );
}

// ── AnimSection — compact per-layer animation inspector (Phase C MVP) ─────────
// Reused by slide-level text-layer cards AND the zone inspector. Persists
// fields directly into the layer/zone object via the parent's onPatch callback.
// time_in / time_out default to (0, slideDuration); effects default to 'none';
// opacity_start/end default to 100. hidden + locked have their own toggles in
// the parent's header row so they sit one click away.
//
// 2026-05-16 (revised) — two compact rows:
//   Row 1: 3 equal-width effect dropdowns                (in · stay · out)
//   Row 2: In [x] s · Out [x] s · [Apply] · [Reset]      (timing + utilities)
// Apply is contextual: for the zone inspector it propagates to all other
// zones + text layers; for individual text-layer cards it's a no-op
// (text edits are live anyway) so the parent passes `onApplyToAll={null}`.
function AnimSection({ layer, onPatch, onApplyToAll, onReset, slideDuration }) {
  const tIn  = Number.isFinite(layer.time_in)  ? layer.time_in  : 0;
  const tOut = Number.isFinite(layer.time_out) ? layer.time_out : slideDuration;
  const eIn   = layer.effect_in   || 'none';
  const eStay = layer.effect_stay || 'none';
  const eOut  = layer.effect_out  || 'none';
  const inputSx  = { width: 44, padding: '3px 4px', border: '1.5px solid #D0D0D0', borderRadius: 4, fontSize: 11, fontWeight: 600, textAlign: 'center', background: '#fff' };
  const selectSx = { flex: '1 1 0', minWidth: 0, height: 24, padding: '0 4px', border: '1.5px solid #D0D0D0', borderRadius: 4, fontSize: 11, background: '#fff', cursor: 'pointer' };
  return (
    <div style={{ padding: 6, border: '1px solid #E5E7EB', borderRadius: 6, background: '#FAFAFA' }}>
      {/* Row 1 — three equal-width effect dropdowns, locked single row */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
        <select value={eIn}   onChange={e => onPatch({ effect_in:   e.target.value })} title="Effect on entry"   style={selectSx}>
          {EFFECT_IN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={eStay} onChange={e => onPatch({ effect_stay: e.target.value })} title="Continuous effect" style={selectSx}>
          {EFFECT_STAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={eOut}  onChange={e => onPatch({ effect_out:  e.target.value })} title="Effect on exit"    style={selectSx}>
          {EFFECT_OUT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {/* Row 2 — In/Out timing + Apply + Reset */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 600 }}>In</span>
        <input type="number" min="0" max={slideDuration} step="0.1" value={tIn}
          onChange={e => onPatch({ time_in: Math.max(0, parseFloat(e.target.value) || 0) })}
          style={inputSx} />
        <span style={{ fontSize: 9, color: '#9CA3AF' }}>s</span>
        <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 600 }}>Out</span>
        <input type="number" min="0" max={slideDuration} step="0.1" value={tOut}
          onChange={e => onPatch({ time_out: Math.min(slideDuration, Math.max(0, parseFloat(e.target.value) || slideDuration)) })}
          style={inputSx} />
        <span style={{ fontSize: 9, color: '#9CA3AF' }}>s</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
          {onApplyToAll && (
            <button onClick={onApplyToAll} title="Copy this layer's animation to every other text + zone on the slide"
              style={{ padding: '3px 10px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: '1.5px solid #1565C0', background: '#E3F2FD', color: '#1565C0', cursor: 'pointer' }}>
              Apply
            </button>
          )}
          {onReset && (
            <button onClick={onReset} title="Reset this layer's animation to defaults"
              style={{ padding: '3px 10px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: '1.5px solid #E0E0E0', background: '#fff', color: '#666', cursor: 'pointer' }}>
              Reset
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

// ── Cross-slide clipboard (zones + text layers, 2026-05-16) ───────────────────
// Stored in localStorage so the designer can copy a zone in one slide, switch
// to another slide (or reload the page), and paste. Single-slot model (last
// copy wins) — matches OS clipboards and keeps the UX uncluttered.
//
// 2026-05-16 update — added a module-level cache as belt-and-braces. Some
// browser contexts (third-party-cookie restrictions, embedded frames, certain
// privacy modes) silently reject localStorage.setItem with a quota error or
// a SecurityError. The in-memory cache survives those failures so copy/paste
// still works for the duration of the session.
const SLIDE_CLIPBOARD_KEY = 'pm_slide_clipboard_v1';
let _memClipboard = null;
function writeSlideClipboard(payload) {
  _memClipboard = payload;
  try { localStorage.setItem(SLIDE_CLIPBOARD_KEY, JSON.stringify(payload)); }
  catch (e) { console.warn('[clipboard] localStorage.setItem failed:', e?.message); }
}
function readSlideClipboard() {
  if (_memClipboard) return _memClipboard;
  try {
    const raw = localStorage.getItem(SLIDE_CLIPBOARD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('[clipboard] localStorage.getItem failed:', e?.message);
    return null;
  }
}

// ── HelpTip — small (?) badge with hover tooltip ─────────────────────────────
// Rendered through a portal into document.body rather than inline. The tips sit
// inside the coloured section banners, and a banner further down the DOM paints
// over a tip belonging to one above it — z-index cannot help, because the tip is
// trapped in its banner's stacking context. Fixed-position + portal escapes both
// that and any ancestor overflow, the same trick the sidebar fly-outs use.
function HelpTip({ text, width = 240 }) {
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);

  const open = () => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const GAP = 8;
    // Keep it on screen: flip above when there is no room below, and pull it
    // left when it would run past the right edge.
    const below = window.innerHeight - r.bottom;
    const left  = Math.min(r.left, window.innerWidth - width - 12);
    setPos({
      left: Math.max(8, left),
      ...(below < 120
        ? { bottom: window.innerHeight - r.top + GAP }
        : { top: r.bottom + GAP }),
    });
  };

  return (
    <span ref={anchorRef}
      onMouseEnter={open} onMouseLeave={() => setPos(null)}
      style={{ display: 'inline-block', position: 'relative', marginLeft: 6, verticalAlign: 'middle' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: '50%', background: '#E0E0E0', color: '#666',
        fontSize: 9, fontWeight: 700, cursor: 'help', userSelect: 'none', lineHeight: 1, fontStyle: 'normal' }}>?</span>
      {pos && createPortal(
        <span style={{ position: 'fixed', ...pos,
          background: '#333', color: '#fff', padding: '7px 10px', borderRadius: 6,
          fontSize: 11, lineHeight: 1.45, whiteSpace: 'normal', width,
          zIndex: 100000, fontWeight: 400, fontStyle: 'normal', pointerEvents: 'none',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)', textTransform: 'none', letterSpacing: 0,
          fontFamily: 'inherit', textAlign: 'left', direction: 'ltr' }}>
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

// ── ColorPicker — color input that reads "no color" (checker + ✕) when empty ─
// When `value` is empty/falsy, shows a small checker pattern with a ✕ overlay
// instead of defaulting to black. Click anywhere on the swatch opens the OS
// color picker; once a color is chosen it switches to the normal swatch.
function ColorPicker({ value, onChange, defaultPick = '#888888', width = 30, height = 28, title, style: extraStyle }) {
  const isEmpty = !value;
  return (
    <span style={{ position: 'relative', width, height, display: 'inline-block', flexShrink: 0, ...extraStyle }}>
      <input type="color"
        value={value || defaultPick}
        onChange={e => onChange(e.target.value)}
        title={title || (isEmpty ? 'No color — click to choose' : value)}
        style={{
          width: '100%', height: '100%',
          border: '1.5px solid #E0E0E0', borderRadius: 6, padding: 0, cursor: 'pointer',
          background: 'none',
          opacity: isEmpty ? 0 : 1,
          display: 'block',
          boxSizing: 'border-box',
        }} />
      {isEmpty && (
        <span aria-hidden="true" style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundColor: '#fff',
          backgroundImage: 'linear-gradient(45deg,#ccc 25%,transparent 25%),linear-gradient(-45deg,#ccc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ccc 75%),linear-gradient(-45deg,transparent 75%,#ccc 75%)',
          backgroundSize: '6px 6px',
          backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0',
          border: '1.5px solid #E0E0E0', borderRadius: 6, boxSizing: 'border-box',
          color: '#888', fontSize: Math.min(width, height) * 0.55, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          ✕
        </span>
      )}
    </span>
  );
}

// System Latin fonts — always available on every browser (no server file needed)
const SYSTEM_LATIN_FONTS = [
  { family: 'Arial, sans-serif', label: 'Arial' },
  { family: '\'Arial Black\', sans-serif', label: 'Arial Black' },
  { family: '\'Helvetica Neue\', Helvetica, sans-serif', label: 'Helvetica' },
  { family: 'Verdana, sans-serif', label: 'Verdana' },
  { family: 'Tahoma, sans-serif', label: 'Tahoma' },
  { family: '\'Trebuchet MS\', sans-serif', label: 'Trebuchet MS' },
  { family: 'Georgia, serif', label: 'Georgia' },
  { family: '\'Times New Roman\', serif', label: 'Times New Roman' },
  { family: 'Impact, sans-serif', label: 'Impact' },
  { family: '\'Courier New\', monospace', label: 'Courier New' },
];

// Detect Arabic in a sample of text
function hasArabicText(s) { return /[؀-ۿ]/.test(s || ''); }

// useFonts — fetch the server font index once, inject @font-face rules globally so any
// font-family referenced anywhere in the editor / renderer auto-loads from the server.
let _fontsCache = null;
let _fontsPromise = null;
// Properly escape a font-family name for CSS string literals (handle \ and ")
function cssEscapeFamily(name) {
  return String(name).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
// Pick the format() hint for url() based on file extension — required for some browsers
function fontFormat(url) {
  const ext = String(url).split('?')[0].split('#')[0].split('.').pop().toLowerCase();
  return { ttf: 'truetype', otf: 'opentype', woff: 'woff', woff2: 'woff2', ttc: 'collection', otc: 'collection' }[ext] || null;
}
function buildFontFaceCss(families) {
  return (families || []).map(fam => {
    const v = (fam.variants || [])[0];
    if (!v) return '';
    const fname = cssEscapeFamily(fam.family);
    const fmt = fontFormat(v.url);
    const srcStr = fmt ? `url("${v.url}") format("${fmt}")` : `url("${v.url}")`;
    // Catch-all rule (any weight, any style) so browsers use this file regardless of
    // requested weight (most display fonts ship a single weight; synthesized bold is fine).
    let rule = `@font-face{font-family:"${fname}";src:${srcStr};font-weight:100 900;font-style:normal;font-display:swap;}`;
    // Real italic variant overrides the synthesized slant when present.
    for (const variant of (fam.variants || [])) {
      if (variant.style === 'italic' && variant.url !== v.url) {
        const f2 = fontFormat(variant.url);
        const s2 = f2 ? `url("${variant.url}") format("${f2}")` : `url("${variant.url}")`;
        rule += `@font-face{font-family:"${fname}";src:${s2};font-weight:100 900;font-style:italic;font-display:swap;}`;
      }
    }
    return rule;
  }).join('');
}
function useFonts() {
  const [data, setData] = useState(_fontsCache || { families: [], total: 0 });
  useEffect(() => {
    if (_fontsCache) {
      setData(_fontsCache);
      // Re-inject CSS in case the <style> tag was removed (HMR / route changes)
      let styleEl = document.getElementById('sb-server-fonts');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'sb-server-fonts';
        document.head.appendChild(styleEl);
        styleEl.textContent = buildFontFaceCss(_fontsCache.families);
      }
      return;
    }
    if (!_fontsPromise) {
      _fontsPromise = api.get('/fonts').then(res => {
        _fontsCache = res.data;
        let styleEl = document.getElementById('sb-server-fonts');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'sb-server-fonts';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = buildFontFaceCss(res.data.families);
        if (typeof console !== 'undefined') console.log(`[fonts] @font-face rules injected for ${res.data.families?.length || 0} families`);
        return res.data;
      }).catch(err => {
        console.warn('[fonts] failed to load index:', err.message);
        return { families: [], total: 0 };
      });
    }
    _fontsPromise.then(d => setData(d));
  }, []);
  return data;
}

// Explicitly trigger font download via the FontFace API. Browsers normally lazy-load fonts
// only when text using them is rendered — but for a dropdown previewing many fonts at once,
// the loads can stagger and look identical until each download finishes. This kicks them all.
async function preloadFontFamilies(families) {
  if (!families || !families.length) return;
  if (typeof document === 'undefined' || !document.fonts || !document.fonts.load) return;
  await Promise.all(families.map(f =>
    document.fonts.load(`16px "${f.replace(/"/g, '\\"')}"`).catch(() => null)
  ));
}

// FontSelect — auto-detects script of the active text and shows ALL matching fonts immediately.
// Each option's preview text is rendered IN that font, so the user sees the typeface's style at a glance.
// Arabic text → preview shown in Arabic glyphs. Latin text → preview shown as font name in Latin.
function FontSelect({ value, onChange, fonts, preferredScript, autoLabel = 'Auto', width = 130 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Scroll container for the dropdown list, so we can scroll the currently-
  // selected font into view when the picker opens — otherwise users always
  // see the alphabetically-first fonts and have to hunt down to find their
  // current selection. With this, switching font starts from where you are.
  const listRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  // When the dropdown opens, scroll the active row into view (centered).
  // requestAnimationFrame ensures the list is mounted before we measure.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const id = requestAnimationFrame(() => {
      const node = listRef.current?.querySelector('[data-font-active="1"]');
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const allFamilies = (fonts && fonts.families) || [];
  const isArabic = preferredScript === 'arabic';
  const isLatin  = preferredScript === 'latin';

  // Server fonts matching the active text's script
  const matchingServer = allFamilies.filter(f =>
    isArabic ? (f.scripts || []).includes('arabic') :
    isLatin  ? (f.scripts || []).includes('latin')  :
    true
  );
  // Show System Latin only when text is Latin or unknown — for Arabic text, designer almost always wants Arabic fonts
  const matchingSystem = isArabic ? [] : SYSTEM_LATIN_FONTS;

  // When the dropdown opens, kick off explicit font downloads so each preview renders in
  // its own typeface as soon as possible (instead of all defaulting to fallback).
  useEffect(() => {
    if (!open) return;
    preloadFontFamilies(matchingServer.map(f => f.family));
  }, [open, matchingServer.length]);

  const displayLabel = !value
    ? autoLabel
    : (() => {
        const first = String(value).split(',')[0].replace(/^['"]|['"]$/g, '').trim();
        return first || autoLabel;
      })();

  const groupLabel = isArabic ? 'Arabic Fonts' : isLatin ? 'Latin Fonts' : 'Fonts';
  // Sample text rendered IN each font as a preview of its style.
  // For Arabic fonts: a short Arabic phrase shows the typeface's character. For Latin: just the family name.
  const arabicSample = 'أبجد هوز نص عربي';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', width }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '4px 6px', fontSize: 12, background: '#fff', color: '#222', cursor: 'pointer', minWidth: 0, overflow: 'hidden' }}>
        <span style={{ fontFamily: value || 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
          {displayLabel}
        </span>
        <span style={{ fontSize: 9, color: '#888' }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999, background: '#fff', border: '1px solid #E0E0E0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 320, width: 'max-content', maxWidth: 420 }}>
          <div ref={listRef} style={{ maxHeight: 420, overflowY: 'auto' }}>
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              data-font-active={!value ? '1' : '0'}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: !value ? '#E3F2FD' : '#fff', cursor: 'pointer', fontSize: 13, color: !value ? '#1565C0' : '#444', fontWeight: !value ? 700 : 400, borderBottom: '1px solid #F0F0F0' }}>
              {autoLabel} <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>· auto-fits to language</span>
            </button>
            {matchingSystem.length > 0 && (
              <>
                <div style={{ padding: '6px 12px 4px', fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, background: '#FAFAFA', position: 'sticky', top: 0, zIndex: 1 }}>
                  System Latin — {matchingSystem.length}
                </div>
                {matchingSystem.map(f => (
                  <button key={f.family} type="button" onClick={() => { preloadFontFamilies([f.family]); onChange(f.family); setOpen(false); }}
                    data-font-active={value === f.family ? '1' : '0'}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: value === f.family ? '#E3F2FD' : '#fff', cursor: 'pointer', borderBottom: '1px solid #F8F8F8' }}>
                    <div style={{ fontFamily: f.family, fontSize: 16, color: value === f.family ? '#1565C0' : '#222' }}>
                      {f.label}
                    </div>
                  </button>
                ))}
              </>
            )}
            {matchingServer.length > 0 && (
              <>
                <div style={{ padding: '6px 12px 4px', fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, background: '#FAFAFA', position: 'sticky', top: 0, zIndex: 1 }}>
                  {groupLabel} — {matchingServer.length}
                </div>
                {matchingServer.map(f => {
                  const previewName = isArabic ? (f.familyAr || f.family) : f.family;
                  // For Arabic group, render preview as Arabic glyphs (so user sees the typeface's STYLE, not just a Latin family name)
                  const previewText = isArabic
                    ? (f.familyAr ? `${f.familyAr} — ${arabicSample}` : arabicSample)
                    : f.family;
                  return (
                    <button key={f.family} type="button" onClick={() => { preloadFontFamilies([f.family]); onChange(f.family); setOpen(false); }}
                      data-font-active={value === f.family ? '1' : '0'}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: value === f.family ? '#E3F2FD' : '#fff', cursor: 'pointer', borderBottom: '1px solid #F8F8F8' }}
                      title={f.family}>
                      <div style={{ fontFamily: `"${f.family}"`, fontSize: 19, lineHeight: 1.4, color: value === f.family ? '#1565C0' : '#222', direction: isArabic ? 'rtl' : 'ltr', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {previewText}
                      </div>
                      <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: 10, color: '#888', marginTop: 2, direction: 'ltr' }}>
                        {f.family}
                      </div>
                    </button>
                  );
                })}
              </>
            )}
            {matchingServer.length === 0 && matchingSystem.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
                Loading fonts…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const LAYOUT_PRESETS = [
  [1,1],[2,1],[3,1],[4,1],
  [1,2],[2,2],[3,2],[4,2],
  [1,3],[2,3],[3,3],[4,3],
  [1,4],[2,4],[3,4],[4,4],
];

// ── Slide mini thumbnail ──────────────────────────────────────────────────────
function SlideMiniThumb({ slide, orientation }) {
  const isV = orientation === 'V';
  const w = isV ? 28 : 44;
  const h = isV ? 44 : 28;
  const bg = slide.bg_color || '#111827';
  const ac = slide.accent_color || '#F59E0B';
  const zc = slide.zone_cols || 1;
  const zr = slide.zone_rows || 1;
  return (
    <div style={{ width: w, height: h, background: bg, borderRadius: 4, flexShrink: 0, display: 'grid', gridTemplateColumns: `repeat(${zc}, 1fr)`, gridTemplateRows: `repeat(${zr}, 1fr)`, overflow: 'hidden', gap: 1.5, padding: 2, boxSizing: 'border-box', border: `1px solid ${ac}60` }}>
      {Array.from({ length: zc * zr }).map((_, i) => <div key={i} style={{ background: `${ac}55`, borderRadius: 1 }} />)}
    </div>
  );
}


// ── Zone Layout Picker ────────────────────────────────────────────────────────
function ZoneLayoutPicker({ cols, rows, onChange }) {
  const [open, setOpen] = useState(false);
  const [rawCols, setRawCols] = useState(String(cols));
  const [rawRows, setRawRows] = useState(String(rows));
  const ref = useRef(null);
  useEffect(() => setRawCols(String(cols)), [cols]);
  useEffect(() => setRawRows(String(rows)), [rows]);
  const commitCols = () => { const v = Math.max(1, Math.min(12, parseInt(rawCols) || 1)); setRawCols(String(v)); onChange(v, rows); };
  const commitRows = () => { const v = Math.max(1, Math.min(12, parseInt(rawRows) || 1)); setRawRows(String(v)); onChange(cols, v); };
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{ position: 'relative', flexShrink: 0 }} ref={ref}>
        <button onClick={() => setOpen(p => !p)} title={`Zones: ${cols}×${rows}`}
          style={{ width: 30, height: 30, borderRadius: 7, border: '1.5px solid #1565C0', background: open ? '#BBDEFB' : '#E3F2FD', cursor: 'pointer', padding: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: Math.min(rows, 4) }).map((_, ri) => (
            <div key={ri} style={{ display: 'flex', flex: 1, gap: 2 }}>
              {Array.from({ length: Math.min(cols, 4) }).map((_, ci) => <div key={ci} style={{ flex: 1, borderRadius: 1, background: '#1565C0' }} />)}
            </div>
          ))}
        </button>
        {open && (
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1000, background: '#fff', borderRadius: 12, boxShadow: '0 6px 28px rgba(0,0,0,0.16)', border: '1px solid #E0E0E0', padding: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 6 }}>Zones</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 30px)', gap: 3 }}>
              {LAYOUT_PRESETS.map(([c, r]) => {
                const sel = c === cols && r === rows;
                return (
                  <button key={`${c}x${r}`} onClick={() => { onChange(c, r); setOpen(false); }} title={`${c}×${r}`}
                    style={{ width: 30, height: 30, borderRadius: 5, border: sel ? '2px solid #1565C0' : '1.5px solid #E0E0E0', background: sel ? '#E3F2FD' : '#FAFAFA', cursor: 'pointer', padding: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Array.from({ length: r }).map((_, ri) => (
                      <div key={ri} style={{ display: 'flex', flex: 1, gap: 2 }}>
                        {Array.from({ length: c }).map((_, ci) => <div key={ci} style={{ flex: 1, borderRadius: 1, background: sel ? '#1565C0' : '#C0C0C0' }} />)}
                      </div>
                    ))}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <input type="text" inputMode="numeric" pattern="[0-9]*" value={rawCols}
        onChange={e => setRawCols(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commitCols}
        style={{ width: 34, textAlign: 'center', border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '3px 2px', fontSize: 13 }} />
      <span style={{ color: '#999', fontSize: 12 }}>×</span>
      <input type="text" inputMode="numeric" pattern="[0-9]*" value={rawRows}
        onChange={e => setRawRows(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commitRows}
        style={{ width: 34, textAlign: 'center', border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '3px 2px', fontSize: 13 }} />
      <span style={{ color: '#bbb', fontSize: 11 }}>{cols * rows}z</span>
    </div>
  );
}

// ── Item Picker ───────────────────────────────────────────────────────────────
function ItemPicker({ categories, items, selectedIds, onChange, lang }) {
  const [expanded, setExpanded] = useState({});
  const isAr = lang === 'ar';
  const label = (en, ar) => isAr ? (ar || en || '') : (en || ar || '');
  const toggleItem = id => onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  const toggleCat = catId => {
    const ids = (items[catId] || []).map(i => i.id);
    const allSel = ids.every(id => selectedIds.includes(id));
    onChange(allSel ? selectedIds.filter(id => !ids.includes(id)) : [...new Set([...selectedIds, ...ids])]);
  };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        Items <span style={{ color: '#1565C0' }}>({selectedIds.length} selected)</span>
      </div>
      <div style={{ border: '1.5px solid #E0E0E0', borderRadius: 10, overflow: 'hidden', maxHeight: 300, overflowY: 'auto' }}>
        {(categories || []).filter(c => !c.is_offer).map((cat, ci, arr) => {
          const catItems = items[cat.id] || [];
          const catIds = catItems.map(i => i.id);
          const selCount = catIds.filter(id => selectedIds.includes(id)).length;
          const allSel = selCount === catIds.length && catIds.length > 0;
          const someSel = selCount > 0 && !allSel;
          const isOpen = expanded[cat.id];
          return (
            <div key={cat.id} style={{ borderBottom: ci < arr.length - 1 ? '1px solid #F0F0F0' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: isOpen ? '#F8FAFF' : '#fff', cursor: 'pointer', direction: isAr ? 'rtl' : 'ltr' }}>
                <input type="checkbox" checked={allSel} onChange={() => toggleCat(cat.id)} onClick={e => e.stopPropagation()}
                  ref={el => { if (el) el.indeterminate = someSel; }} style={{ width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }} />
                <div onClick={() => setExpanded(p => ({ ...p, [cat.id]: !p[cat.id] }))} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {cat.icon && <span style={{ fontSize: 16 }}>{cat.icon}</span>}
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#1A1A1A', flex: 1, fontFamily: isAr ? 'Cairo, sans-serif' : 'inherit' }}>{label(cat.name, cat.name_ar)}</span>
                  <span style={{ fontSize: 11, color: selCount > 0 ? '#1565C0' : '#bbb', fontWeight: 600 }}>{selCount}/{catItems.length}</span>
                  <span style={{ fontSize: 10, color: '#bbb' }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>
              {isOpen && catItems.map((item, ii) => {
                const isSel = selectedIds.includes(item.id);
                const itemLabel = label(item.name, item.name_ar);
                const itemSub = isAr ? item.name : item.name_ar;
                return (
                  <div key={item.id} onClick={() => toggleItem(item.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px 7px 32px', background: isSel ? '#E3F2FD' : ii % 2 ? '#fff' : '#FAFAFA', cursor: 'pointer', borderTop: '1px solid #F5F5F5', direction: isAr ? 'rtl' : 'ltr' }}>
                    <input type="checkbox" checked={isSel} onChange={() => {}} style={{ width: 14, height: 14, flexShrink: 0 }} />
                    {(item.image_thumb || item.image_path)
                      ? <img src={item.image_thumb || item.image_path} alt="" style={{ width: 26, height: 26, borderRadius: 4, objectFit: 'contain', flexShrink: 0, background: '#f0f0f0' }} />
                      : item.icon ? <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                      : <div style={{ width: 20, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: isSel ? 600 : 400, color: isSel ? '#1565C0' : '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: isAr ? 'Cairo, sans-serif' : 'inherit' }}>{itemLabel}</div>
                      {itemSub && <div style={{ fontSize: 10, color: '#bbb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: !isAr ? 'Cairo, sans-serif' : 'inherit' }}>{itemSub}</div>}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1565C0', flexShrink: 0 }}>
                      {item.variants?.length > 0 ? item.variants.map(v => v.price).join('/') : item.price}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Send to TV Modal ──────────────────────────────────────────────────────────
function SendToTVModal({ brandId, slideId, slideFilename, orientation, userPass, username, onClose }) {
  const [screens,  setScreens]  = useState([]);
  const [selected, setSelected] = useState({});
  const [sending,  setSending]  = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    api.get(`/menuboard/screens/brand/${brandId}`)
      .then(res => {
        const filtered = res.data.filter(s => (s.orientation || 'H') === orientation);
        setScreens(filtered);
        setSelected(Object.fromEntries(filtered.map(s => [s.id, false])));
      })
      .catch(() => toast.error('Failed to load TV list'))
      .finally(() => setLoading(false));
  }, [brandId, orientation]);

  const doSend = async (overwriteIds = []) => {
    setSending(true);
    try {
      const targets = screens.filter(s => selected[s.id]).map(s => ({
        branch_folder: s.folder_name, screen_label: s.screen_label,
        filename: slideFilename, overwrite: overwriteIds.includes(s.id),
      }));
      if (!targets.length) { toast.error('Select at least one TV'); setSending(false); return; }
      const res = await api.post(`/menuboard/slides/${slideId}/send-to-tv`, {
        brand_id: brandId, owner_username: username, dav_pass: userPass, targets,
      });
      const newConflicts = res.data.results
        .filter(r => r.exists)
        .map(r => screens.find(s => s.folder_name === r.branch_folder && s.screen_label === r.screen_label)?.id)
        .filter(Boolean);
      if (newConflicts.length > 0) { setConflicts(newConflicts); setSending(false); return; }
      const okCount = res.data.results.filter(r => r.ok).length;
      const errCount = res.data.results.filter(r => r.error).length;
      if (okCount)  toast.success(`✅ Sent to ${okCount} TV${okCount > 1 ? 's' : ''}!`);
      if (errCount) {
        const firstErr = res.data.results.find(r => r.error);
        toast.error(`Failed on ${errCount} TV: ${firstErr?.error || 'unknown'}`);
      }
      if (!errCount) onClose();
    } catch (err) {
      toast.error('Send failed: ' + (err.response?.data?.error || err.message));
    } finally { setSending(false); }
  };

  const toggleAll = () => {
    const allOn = screens.every(s => selected[s.id]);
    setSelected(Object.fromEntries(screens.map(s => [s.id, !allOn])));
  };

  const selCount = screens.filter(s => selected[s.id]).length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 420, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>
        {conflicts.length > 0 ? (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>⚠️ File already exists</div>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
              <b>"{slideFilename}"</b> already exists on {conflicts.length} TV{conflicts.length > 1 ? 's' : ''}. Overwrite?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConflicts([])} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={() => { setConflicts([]); doSend(conflicts); }} disabled={sending}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                {sending ? 'Overwriting…' : 'Overwrite'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>📺 Send to TV</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {orientation === 'H' ? '⟺ Horizontal' : '⇕ Vertical'} screens · "{slideFilename}"
                </div>
              </div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999', padding: 4 }}>✕</button>
            </div>
            {loading ? <div style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>Loading TVs…</div>
            : screens.length === 0 ? <div style={{ textAlign: 'center', padding: 24, color: '#aaa', fontSize: 13 }}>No {orientation === 'H' ? 'horizontal' : 'vertical'} TVs found for this brand.</div>
            : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <button onClick={toggleAll} style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                    {screens.every(s => selected[s.id]) ? 'Deselect all' : 'Select all'}
                  </button>
                  <span style={{ fontSize: 11, color: '#bbb' }}>· {selCount} selected</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', border: '1.5px solid #E0E0E0', borderRadius: 10, marginBottom: 16 }}>
                  {screens.map((s, idx) => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderBottom: idx < screens.length - 1 ? '1px solid #F0F0F0' : 'none', background: selected[s.id] ? '#E3F2FD' : '#fff' }}>
                      <input type="checkbox" checked={!!selected[s.id]} onChange={e => setSelected(p => ({ ...p, [s.id]: e.target.checked }))}
                        style={{ width: 15, height: 15, flexShrink: 0, cursor: 'pointer', accentColor: '#1565C0' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.branch_name}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{s.screen_label}</div>
                      </div>
                      <span style={{ fontSize: 10, background: '#F0F0F0', borderRadius: 4, padding: '2px 6px', color: '#666', flexShrink: 0 }}>{s.folder_name}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={() => doSend([])} disabled={sending || selCount === 0 || loading}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1565C0', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: (sending || selCount === 0) ? 0.5 : 1 }}>
                {sending ? '📡 Sending…' : '📺 Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Screen Selector (shown when no screenId) ──────────────────────────────────
function ScreenSelector({ brandId, screens, restaurant, navigate }) {
  const grouped = screens.reduce((acc, s) => {
    const key = s.branch_name || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});
  return (
    <div className="page">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          {/* Legacy screen-mode selector — reached only through the old
              /slides/:screenId route, never from Info Screens. */}
          <BrandNav brandId={brandId} subPath="slides" backLabel="Menu Editor" backPath={`/brands/${brandId}/menu`} backKey="menu" />
          <h1 className="page-title">🎨 Slide Builder — {restaurant?.name}</h1>
        </div>
      </div>
      {screens.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📺</div>
          <h3>No TVs configured</h3>
          <p>Add TVs from the Branches page, then come back here to design slides.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate(`/brands/${brandId}/branches`)}>
            Go to Branches
          </button>
        </div>
      ) : (
        <div>
          {Object.entries(grouped).map(([branchName, bScreens]) => (
            <div key={branchName} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{branchName}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {bScreens.map(s => {
                  const isH = s.orientation !== 'V';
                  const color = isH ? '#1565C0' : '#7B1FA2';
                  const bg = isH ? '#E3F2FD' : '#F3E5F5';
                  return (
                    <div key={s.id} onClick={() => navigate(`/brands/${brandId}/slides/${s.id}`)}
                      style={{ background: '#fff', border: '1.5px solid #EBEBEB', borderRadius: 14, padding: '18px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#EBEBEB'; e.currentTarget.style.boxShadow = 'none'; }}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>📺</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.screen_label}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg, color }}>{isH ? '⟺ H' : '⇕ V'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Strip {prefix}- from slide name for display in the input field.
// Tries slug first (longer/more specific, e.g. "alex-196") for backwards compat with old names,
// then brand name (e.g. "alex") for new names. Longest match wins to avoid "alex-" eating "alex-196-".
function stripBrandPrefix(slideName, brandName, slug) {
  if (slug && slideName.startsWith(slug + '-')) return slideName.slice(slug.length + 1);
  if (brandName && slideName.startsWith(brandName + '-')) return slideName.slice(brandName.length + 1);
  return slideName;
}

// ── Main SlideBuilder ─────────────────────────────────────────────────────────
export default function SlideBuilder() {
  const { brandId, screenId, slideId } = useParams();
  // Where the back button should land. The builder is reached from Slides and
  // from Info Screens, and sending an Info Screens visitor to the Menu Editor
  // is simply wrong. The opener stamps this; it survives a reload.
  const [origin] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('from');
      if (q) { sessionStorage.setItem('sb_origin', q); return q; }
      // No stored origin means we cannot know — fall through to the template's
      // own kind below rather than guessing 'slides'.
      return sessionStorage.getItem('sb_origin') || null;
    } catch { return null; }
  });
  // How you arrived is a hint; what you are editing is the fact. A door-sign
  // template belongs to Info Screens no matter which door you came through, and
  // unlike the query string this survives a reload, a new tab, and the
  // replace-navigation that save() does after creating a slide.
  const [isSignTpl, setIsSignTpl] = useState(false);
  const DEST = {
    'info-screens': { subPath: 'info-screens', label: 'Info Screens', path: `/brands/${brandId}/info-screens`,          key: 'info-screens' },
    templates:      { subPath: 'slides',       label: 'Templates',    path: `/brands/${brandId}/slides?kind=doorsign`, key: 'info-screens' },
    slides:         { subPath: 'slides',       label: 'Slides',       path: `/brands/${brandId}/slides`,               key: 'slides' },
  };
  // Where you came from wins — the same template is reachable from Info Screens
  // and from the Templates tab, and either one is a correct place to go back
  // to. Only when that is unknown (new tab, cleared session) does the
  // template's own kind decide.
  const BACK = DEST[origin] || (isSignTpl ? DEST['info-screens'] : DEST.slides);
  // What is open decides. Only a brand-new unsaved slide has no answer of its
  // own, and then how we got here is the best signal available.
  const isTemplateCtx = (slideId && slideId !== 'new')
    ? isSignTpl
    : (origin === 'info-screens' || origin === 'templates');
  // Keep the sidebar honest about which entry this editor belongs to.
  useEffect(() => { announceSub(isTemplateCtx ? 'info-screens' : 'slides'); }, [isTemplateCtx]);
  const navigate = useNavigate();
  const { user, userPass } = useAuth();

  // Brand-slide mode: accessed via /brands/:brandId/slide-builder/:slideId?
  const isBrandMode = !screenId; // route /slide-builder/* has no screenId

  const [allScreens,    setAllScreens]    = useState([]);
  const [restaurant,    setRestaurant]    = useState(null);
  const [menuData,      setMenuData]      = useState({ categories: [], items: {}, allItems: [] });
  const [slides,        setSlides]        = useState([]);
  const [selectedId,    setSelectedId]    = useState(null);
  const [form, setForm] = useState({
    // Phase 5: empty bg_color → transparent slide (checkered preview, alpha in export).
    bg_color: '', accent_color: '#F59E0B',
    zone_cols: 1, zone_rows: 1, zones: [EMPTY_ZONE(0)],
    zone_mode: 'grid',
    slide_name: '', orientation: 'H',
    slide_bg_image: '', slide_bg_overlay: 50,
    // Custom per-slide output resolution. Phase A (2026-05-16) bumped the
    // default to 1280x720 (H) / 720x1280 (V) — sweeter spot for fluid editing.
    // Designers can still type any resolution into the W×H inputs to override.
    width_px: 1280, height_px: 720,
    // Slide-level free text layers (Phase 3, 2026-05-14)
    text_layers: [],
    // Video-background slide (Phase A, 2026-05-16). slide_kind 'image' is the
    // default; uploading a video flips it to 'video' on the server. The two
    // *_path fields are populated by the /upload-video endpoint, not by Save.
    slide_kind: 'image',
    video_path: '', video_preview_path: '',
    duration_sec: 15, loop_mode: 'loop',
    has_audio: false, auto_darken: 0,
  });
  const [branchTheme,    setBranchTheme]    = useState({ bg: '#111827', ac: '#F59E0B' });
  const [activeZoneIdx,  setActiveZoneIdx]  = useState(0);
  const [rawZoneCols,    setRawZoneCols]    = useState('1');
  const [rawZoneRows,    setRawZoneRows]    = useState('4');
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [exportOverlay,  setExportOverlay]  = useState('');
  // 2026-05-17 — compose-time progress. null = no compose running (use the
  // spinner overlay), 0..100 = show a real progress bar fed by polling the
  // backend's /compose-progress endpoint.
  const [composePct,     setComposePct]     = useState(null);
  // English is the default on every entry and the app stays LTR until the
  // user presses the language button — the choice is deliberately not persisted.
  const [lang,           setLang]           = useState('en');
  const [showPresets,    setShowPresets]    = useState(false);
  const [showSlidesDrop, setShowSlidesDrop] = useState(false);
  const [showHamburger,  setShowHamburger]  = useState(false);
  const [showSendTV,     setShowSendTV]     = useState(false);
  const [showBrandSendTV, setShowBrandSendTV] = useState(false);
  const [allBrandSlides, setAllBrandSlides] = useState([]);
  const [winW,           setWinW]           = useState(() => window.innerWidth);
  const [winH,           setWinH]           = useState(() => window.innerHeight);
  const [zoomPct,        setZoomPct]        = useState(100);
  const [bgUploading, setBgUploading] = useState(false);
  const [slideBgUploading, setSlideBgUploading] = useState(false);
  // Phase A (2026-05-16) — video upload progress flag for the SLIDE-section
  // 🎬 Background button. Separate from slideBg so the two flows can coexist
  // (slide could have both a fallback image bg and a video bg).
  const [videoUploading, setVideoUploading] = useState(false);
  // 2026-05-18 — natural duration of the bg video, probed from the preview's
  // <video> element on `loadedmetadata`. Drives the auto-reset rule: when the
  // designer's slide duration matches the bg's playable length within ±2s,
  // we force loop_mode='reset'. That avoids both the looping cut and the
  // freeze-pad — the bg just plays end-to-end at its natural length, no
  // visible fade at the end. Null = not yet probed (or no bg uploaded).
  const [bgVideoDuration, setBgVideoDuration] = useState(null);
  // Phase C MVP (2026-05-16) — preview-pane playhead. Drives per-layer
  // animation (time_in/out, effects, opacity interp) in SlidePreview. Looping
  // is implicit — currentTime wraps to 0 when it reaches duration_sec.
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const playFrameRef = useRef(null);
  const playLastTsRef = useRef(0);
  const [expandedLayerIdx, setExpandedLayerIdx] = useState(null);
  const [textLayersExpanded, setTextLayersExpanded] = useState(false);
  // Slide-level (top-level) text layers — Phase 3. Separate from zone-nested layers.
  const [expandedSlideTextIdx, setExpandedSlideTextIdx] = useState(null);
  const [selectedSlideTextIdx, setSelectedSlideTextIdx] = useState(null);

  // 2026-05-18 — auto-reset rule. When the bg video's natural duration matches
  // the designer's slide duration within ±2s, force loop_mode='reset' so the
  // saved MP4 plays the bg once at its natural length (no looping cut, no
  // freeze pad, no end fade). The designer can still manually override by
  // clicking 🔁 or ⏸ — we only auto-set on durations-match + non-freeze.
  const durationsMatch = Number.isFinite(bgVideoDuration) && bgVideoDuration > 0
    && Math.abs(Number(form.duration_sec || 0) - bgVideoDuration) < 2;
  useEffect(() => {
    if (form.slide_kind !== 'video') return;
    if (!durationsMatch) return;
    if (form.loop_mode === 'reset') return;       // already correct
    if (form.loop_mode === 'freeze') return;      // respect explicit freeze
    setForm(f => ({ ...f, loop_mode: 'reset' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationsMatch, form.slide_kind]);

  // Font index from server — fetched once per session, all @font-face rules injected globally
  const fontIndex = useFonts();

  // Foldable section state — Slide open by default, Zones / Text / Items collapsed
  const [openSections, setOpenSections] = useState({ slide: true, text: false, zones: false, items: false });
  // Phase 4: once the user manually toggles a section, auto-fold/unfold stops touching it.
  const [userTouchedSections, setUserTouchedSections] = useState(() => new Set());
  // 2026-05-16: right-click context menu for zones / text / preview background.
  // Shape: { x, y, kind: 'zone'|'text'|'background', idx?: number } | null
  const [contextMenu, setContextMenu] = useState(null);
  const toggleSection = (key) => {
    setUserTouchedSections(prev => { const next = new Set(prev); next.add(key); return next; });
    setOpenSections(s => ({ ...s, [key]: !s[key] }));
  };

  // Fullscreen live preview overlay
  const [showFullPreview, setShowFullPreview] = useState(false);
  // ESC closes the fullscreen preview
  useEffect(() => {
    if (!showFullPreview) return;
    const onKey = e => { if (e.key === 'Escape') setShowFullPreview(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showFullPreview]);
  const dropRef          = useRef(null);
  const hamburgerRef     = useRef(null);
  const bgFileRef        = useRef(null);
  const slideBgFileRef   = useRef(null);
  const videoFileRef     = useRef(null);
  const sbRestoreFileRef = useRef(null);

  // The chooser must list the siblings of what is open. A template dropped in
  // a list of menu slides offers to switch you into a different product, and
  // picking one silently changes which page you are editing.
  useEffect(() => {
    if (!isBrandMode || !brandId) return;
    let dead = false;
    api.get(`/brand-slides?brand_id=${brandId}&kind=${isTemplateCtx ? 'doorsign' : 'products'}`)
      .then(r => { if (!dead) setAllBrandSlides(r.data || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, [brandId, isBrandMode, isTemplateCtx]);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = async () => {
    try {
      if (isBrandMode) {
        // Brand-slide mode: load brand + menu data
        const [rRes, mdRes, allSRes] = await Promise.all([
          api.get(`/brands/${brandId}`),
          api.get(`/menuboard/menu-data/${brandId}`),
          api.get(`/brand-slides?brand_id=${brandId}`),
        ]);
        // Provisional — the effect below replaces this with the siblings of
        // whatever kind actually loaded.
        setAllBrandSlides(allSRes.data || []);
        setRestaurant(rRes.data);
        setMenuData(mdRes.data);

        // Brand theme colors — used as defaults for every new slide
        const themeBg = rRes.data.theme_bg_color || '#111827';
        const themeAc = rRes.data.theme_accent_color || '#F59E0B';
        setBranchTheme({ bg: themeBg, ac: themeAc });

        if (slideId && slideId !== 'new') {
          // Load existing brand slide
          const slRes = await api.get(`/brand-slides/${slideId}`);
          const slide = slRes.data;
          setSelectedId(slide.id);
          setSlides([slide]);
          setIsSignTpl(slide.menu_kind === 'doorsign');
          const zc = slide.zone_cols || 1, zr = slide.zone_rows || 1;
          const zMode = slide.zone_mode === 'free' ? 'free' : 'grid';
          const zParsed = parseZones(slide.zones);
          // Append a cache-buster derived from updated_at so the browser
          // re-validates the bg images instead of using a stale cached copy.
          // The backend rename step often overwrites a file at the SAME URL,
          // so the URL alone isn't enough to invalidate the browser cache.
          const cacheVer = slide.updated_at
            ? new Date(slide.updated_at).getTime()
            : Date.now();
          const bust = (url) => {
            if (!url) return url;
            const clean = url.replace(/\?.*$/, '');
            return clean + '?v=' + cacheVer;
          };
          const zonesBusted = (zMode === 'free' ? zParsed : adjustZones(zParsed, zc, zr))
            .map(z => z.bg_image ? { ...z, bg_image: bust(z.bg_image) } : z);
          const sOri = slide.orientation || 'H';
          setForm({
            // Phase 5: preserve explicit empty string (= transparent). Only fall back to theme when null/undefined.
            bg_color: slide.bg_color != null ? slide.bg_color : themeBg,
            accent_color: slide.accent_color || themeAc,
            zone_cols: zc, zone_rows: zr,
            zone_mode: zMode,
            zones: zonesBusted,
            slide_name: stripBrandPrefix(slide.slide_name || '', sanitize(rRes.data?.name || ''), rRes.data?.slug || ''),
            orientation: sOri,
            slide_bg_image: bust(slide.slide_bg_image || ''),
            slide_bg_overlay: slide.slide_bg_overlay ?? 50,
            // Existing rows keep their saved resolution; brand-new rows default
            // to the Phase A 1280×720 (H) / 720×1280 (V) baseline.
            width_px:  slide.width_px  || (sOri === 'V' ? 720  : 1280),
            height_px: slide.height_px || (sOri === 'V' ? 1280 : 720),
            text_layers: parseZones(slide.text_layers),
            // Phase A (2026-05-16) — video-slide fields. Both *_path values are
            // cache-busted like the image bg so the editor's <video> element
            // reloads after a re-upload.
            slide_kind:         slide.slide_kind === 'video' ? 'video' : 'image',
            video_path:         bust(slide.video_path || ''),
            video_preview_path: bust(slide.video_preview_path || ''),
            duration_sec:       slide.duration_sec || 15,
            loop_mode:          (slide.loop_mode === 'freeze' || slide.loop_mode === 'reset') ? slide.loop_mode : 'loop',
            has_audio:          !!slide.has_audio,
            auto_darken:        slide.auto_darken || 0,
          });
          // 2026-05-18 — clear cached bg duration on slide switch; the new
          // preview's onLoadedMetadata will repopulate. Without this, stale
          // bgVideoDuration from the previous slide could trigger the wrong
          // auto-reset decision until the new <video> loads.
          setBgVideoDuration(null);
          // Restore saved per-slide language so designer opens an Arabic-first slide in Arabic.
          if (slide.lang === 'en' || slide.lang === 'ar') setLang(slide.lang);
        } else {
          // New slide: inherit theme from last brand slide, or fall back to brand theme
          try {
            const sRes = await api.get(`/brand-slides?brand_id=${brandId}`);
            const last = sRes.data[sRes.data.length - 1];
            setForm(f => ({
              ...f,
              bg_color: last?.bg_color || themeBg,
              accent_color: last?.accent_color || themeAc,
            }));
          } catch {
            setForm(f => ({ ...f, bg_color: themeBg, accent_color: themeAc }));
          }
        }
      } else {
        // Legacy screen mode
        const [rRes, screensRes, mdRes] = await Promise.all([
          api.get(`/brands/${brandId}`),
          api.get(`/menuboard/screens/brand/${brandId}`),
          api.get(`/menuboard/menu-data/${brandId}`),
        ]);
        setRestaurant(rRes.data);
        setAllScreens(screensRes.data);
        setMenuData(mdRes.data);
        if (screensRes.data[0]) setBranchTheme({ bg: '#111827', ac: '#F59E0B' });
        if (screenId) {
          const sc = screensRes.data.find(s => s.id === parseInt(screenId));
          const ori = sc?.orientation === 'V' ? 'V' : 'H';
          const slRes = await api.get(`/menuboard/slides?screen_id=${screenId}`);
          setSlides(slRes.data);
          if (slRes.data.length > 0) { setSelectedId(slRes.data[0].id); populateForm(slRes.data[0], ori); }
          else setForm(f => ({ ...f, orientation: ori }));
        }
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [brandId, screenId, slideId]); // eslint-disable-line

  // Phase C — reset the preview playhead whenever we switch slides so each
  // slide opens at t=0 (its natural "first frame" state).
  useEffect(() => { setCurrentTime(0); setIsPlaying(false); }, [selectedId]);

  useEffect(() => {
    const fn = () => { setWinW(window.innerWidth); setWinH(window.innerHeight); };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    const h = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowSlidesDrop(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => {
    const h = e => { if (hamburgerRef.current && !hamburgerRef.current.contains(e.target)) setShowHamburger(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const populateForm = (slide, fallbackOri) => {
    const zc = slide.zone_cols || 1, zr = slide.zone_rows || 1;
    const sc = allScreens.find(s => s.id === parseInt(screenId));
    const ori = slide.slide_orientation || fallbackOri || sc?.orientation || 'H';
    const zMode = slide.zone_mode === 'free' ? 'free' : 'grid';
    const zParsed = parseZones(slide.zones);
    // Cache-bust bg URLs so the browser revalidates instead of serving a
    // stale cached copy when switching back to a slide whose backing file
    // was overwritten by a previous save.
    const cacheVer = slide.updated_at ? new Date(slide.updated_at).getTime() : Date.now();
    const bust = (url) => {
      if (!url) return url;
      const clean = url.replace(/\?.*$/, '');
      return clean + '?v=' + cacheVer;
    };
    const zonesArranged = zMode === 'free' ? zParsed : adjustZones(zParsed, zc, zr);
    const zonesBusted = zonesArranged.map(z => z.bg_image ? { ...z, bg_image: bust(z.bg_image) } : z);
    setForm({
      // Phase 5: preserve explicit empty string (= transparent).
      bg_color: slide.bg_color != null ? slide.bg_color : '#111827',
      accent_color: slide.accent_color || '#F59E0B',
      zone_cols: zc, zone_rows: zr,
      zone_mode: zMode,
      zones: zonesBusted,
      slide_name: slide.custom_label_en || '',
      orientation: ori,
      slide_bg_image: bust(slide.slide_bg_image || ''),
      slide_bg_overlay: slide.slide_bg_overlay ?? 50,
      width_px:  slide.width_px  || (ori === 'V' ? 720  : 1280),
      height_px: slide.height_px || (ori === 'V' ? 1280 : 720),
      text_layers: parseZones(slide.text_layers),
    });
    if (slide.lang === 'en' || slide.lang === 'ar') setLang(slide.lang);
    setActiveZoneIdx(0);
  };

  const buildPayload = useCallback((slideOrder) => ({
    bg_color: form.bg_color, accent_color: form.accent_color,
    zone_cols: form.zone_cols, zone_rows: form.zone_rows,
    zone_mode: form.zone_mode || 'grid',
    zones: form.zones, custom_label_en: form.slide_name,
    slide_orientation: form.orientation, slide_order: slideOrder,
    slide_bg_image: form.slide_bg_image || '', slide_bg_overlay: form.slide_bg_overlay ?? 50,
    template: 'list-view', item_ids: [], grid_cols: 1, grid_rows: 1,
  }), [form]);

  const updateZone = (field, value) =>
    setForm(f => ({ ...f, zones: f.zones.map((z, i) => i === activeZoneIdx ? { ...z, [field]: value } : z) }));

  // ── Free-mode zone CRUD ─────────────────────────────────────────────────────
  const addFreeZone = () => {
    const dimNow = DESIGN[form.orientation] || DESIGN.H;
    const w = Math.round(dimNow.w * 0.35);
    const h = Math.round(dimNow.h * 0.35);
    const x = Math.round((dimNow.w - w) / 2);
    const y = Math.round((dimNow.h - h) / 2);
    const maxZ = form.zones.reduce((m, z) => Math.max(m, z.z_index || 0), 0);
    const newZone = { ...EMPTY_ZONE(form.zones.length), x, y, w, h, z_index: maxZ + 1 };
    setForm(f => ({ ...f, zones: [...f.zones, newZone] }));
    setActiveZoneIdx(form.zones.length);
  };

  const deleteZoneAt = (idx) => {
    if (form.zones.length <= 1) {
      toast.error('Cannot delete the last zone');
      return;
    }
    if (!window.confirm('Delete this zone?')) return;
    setForm(f => ({ ...f, zones: f.zones.filter((_, i) => i !== idx) }));
    setActiveZoneIdx(prev => Math.max(0, Math.min(prev, form.zones.length - 2)));
  };

  const duplicateZoneAt = (idx) => {
    const src = form.zones[idx];
    if (!src) return;
    const maxZ = form.zones.reduce((m, z) => Math.max(m, z.z_index || 0), 0);
    const clone = {
      ...src,
      id: `z_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
      x: (src.x || 0) + 24,
      y: (src.y || 0) + 24,
      z_index: maxZ + 1,
      // deep-copy text_layers so edits don't bleed across copies
      text_layers: (src.text_layers || []).map(l => ({ ...l, id: `tl_${Date.now()}_${Math.floor(Math.random() * 9999)}` })),
    };
    setForm(f => ({ ...f, zones: [...f.zones, clone] }));
    setActiveZoneIdx(form.zones.length);
    toast.success('Zone duplicated');
  };

  const setZoneZOrder = (idx, action /* 'forward' | 'back' | 'front' | 'bottom' */) => {
    setForm(f => {
      const zones = [...f.zones];
      const target = zones[idx];
      if (!target) return f;
      const others = zones.filter((_, i) => i !== idx).map(z => z.z_index || 0);
      const maxZ = others.length ? Math.max(...others) : 0;
      const minZ = others.length ? Math.min(...others) : 0;
      const cur = target.z_index || 0;
      let newZ = cur;
      if (action === 'forward') newZ = cur + 1;
      else if (action === 'back') newZ = cur - 1;
      else if (action === 'front') newZ = maxZ + 1;
      else if (action === 'bottom') newZ = minZ - 1;
      zones[idx] = { ...target, z_index: newZ };
      return { ...f, zones };
    });
  };

  // ── Switch zone_mode ─ grid → free converts coords; free → grid keeps zones but reverts ─
  const switchZoneMode = (newMode) => {
    if (newMode === form.zone_mode) return;
    if (newMode === 'free') {
      const dimNow = DESIGN[form.orientation] || DESIGN.H;
      const converted = gridToFreeZones(form.zones, form.zone_cols, form.zone_rows, dimNow);
      setForm(f => ({ ...f, zone_mode: 'free', zones: converted }));
      toast.success('Switched to Free Layout — zones can now be moved freely');
    } else {
      // Free → Grid: snap zones back into a cols×rows grid (keep the existing zone count)
      const count = Math.max(1, form.zones.length);
      const cols = Math.ceil(Math.sqrt(count));
      const rows = Math.ceil(count / cols);
      setForm(f => ({
        ...f,
        zone_mode: 'grid',
        zone_cols: cols,
        zone_rows: rows,
        zones: adjustZones(f.zones, cols, rows),
      }));
      toast('Switched to Grid Layout', { icon: '🔲' });
    }
  };

  // ── Text-layer CRUD on the active zone ─────────────────────────────────────
  const addTextLayer = () => {
    const layer = EMPTY_TEXT_LAYER();
    const cur = form.zones[activeZoneIdx];
    if (!cur) return;
    updateZone('text_layers', [...(cur.text_layers || []), layer]);
  };
  const updateTextLayer = (layerIdx, patch) => {
    const cur = form.zones[activeZoneIdx];
    if (!cur) return;
    const layers = (cur.text_layers || []).map((l, i) => i === layerIdx ? { ...l, ...patch } : l);
    updateZone('text_layers', layers);
  };
  const deleteTextLayer = (layerIdx) => {
    const cur = form.zones[activeZoneIdx];
    if (!cur) return;
    updateZone('text_layers', (cur.text_layers || []).filter((_, i) => i !== layerIdx));
  };

  // ── Slide-level text-layer CRUD (Phase 3) ──────────────────────────────────
  const addSlideTextLayer = () => {
    const existing = form.text_layers || [];
    const label = nextTextLabel(existing);
    const _wPx = parseInt(form.width_px, 10);
    const _hPx = parseInt(form.height_px, 10);
    const _ori = form.orientation || 'H';
    const dimW = (_wPx > 0) ? _wPx / 2 : DESIGN[_ori].w;
    const dimH = (_hPx > 0) ? _hPx / 2 : DESIGN[_ori].h;
    const layer = {
      ...EMPTY_TEXT_LAYER(label),
      x: Math.round(dimW / 2 - 100),
      y: Math.round(dimH / 2 - 25),
      w: 200, h: 50,
    };
    setForm(f => ({ ...f, text_layers: [...(f.text_layers || []), layer] }));
    return existing.length;
  };
  const updateSlideTextLayer = (layerIdx, patch) => {
    setForm(f => ({
      ...f,
      text_layers: (f.text_layers || []).map((l, i) => i === layerIdx ? { ...l, ...patch } : l),
    }));
  };
  const deleteSlideTextLayer = (layerIdx) => {
    setForm(f => ({
      ...f,
      text_layers: (f.text_layers || []).filter((_, i) => i !== layerIdx),
    }));
    setSelectedSlideTextIdx(s => (s === layerIdx ? null : s > layerIdx ? s - 1 : s));
    setExpandedSlideTextIdx(s => (s === layerIdx ? null : s > layerIdx ? s - 1 : s));
  };
  const duplicateSlideTextLayer = (layerIdx) => {
    const existing = form.text_layers || [];
    const src = existing[layerIdx];
    if (!src) return;
    const label = nextTextLabel(existing);
    const copy = {
      ...src,
      id: `tl_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
      label,
      x: (src.x || 0) + 20,
      y: (src.y || 0) + 20,
    };
    setForm(f => ({ ...f, text_layers: [...(f.text_layers || []), copy] }));
  };

  // Renders one expandable text-layer card. Used by both zone-nested and slide-level lists.
  const renderTextLayerCard = ({ layer, li, isOpen, onToggleOpen, onPatch, onDelete, onDuplicate }) => {
    const layerTextRaw = (layer.text != null && layer.text !== '')
      ? layer.text
      : (layer.text_en || layer.text_ar || '');
    const previewTxt = (layerTextRaw.split('\n')[0] || '(empty)');
    return (
      <div key={layer.id || li} style={{ marginBottom: 6, border: '1px solid #EEE', borderRadius: 6, background: isOpen ? '#F8FAFF' : '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', cursor: 'pointer' }}
          onClick={onToggleOpen}>
          {layer.label && <span style={{ fontSize: 9, fontWeight: 700, color: '#999', minWidth: 18 }}>{layer.label}</span>}
          {/* Always render the editor preview in BLACK regardless of the layer's color —
              white/light text would be invisible against the editor's white card background.
              Font family + style/weight still reflect the actual layer settings. */}
          <span style={{ fontSize: 11, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: layer.font_family || 'inherit', fontStyle: layer.font_style || 'normal', color: '#222' }}>
            {previewTxt}
          </span>
          <span style={{ fontSize: 10, color: '#999' }}>{layer.font_size || 24}px</span>
          {/* Phase C — hide/lock toggles, header-level so they're always one click away */}
          <button onClick={e => { e.stopPropagation(); onPatch({ hidden: !layer.hidden }); }}
            title={layer.hidden ? 'Layer hidden — click to show' : 'Hide layer'}
            style={{ padding: '2px 5px', fontSize: 11, borderRadius: 5, border: '1.5px solid #E0E0E0', background: layer.hidden ? '#FEF3C7' : '#fff', color: layer.hidden ? '#92400E' : '#666', cursor: 'pointer', opacity: layer.hidden ? 1 : 0.7 }}>
            {layer.hidden ? '🙈' : '👁'}
          </button>
          <button onClick={e => { e.stopPropagation(); onPatch({ locked: !layer.locked }); }}
            title={layer.locked ? 'Layer locked — click to unlock' : 'Lock position/size'}
            style={{ padding: '2px 5px', fontSize: 11, borderRadius: 5, border: '1.5px solid #E0E0E0', background: layer.locked ? '#E0E7FF' : '#fff', color: layer.locked ? '#3730A3' : '#666', cursor: 'pointer', opacity: layer.locked ? 1 : 0.7 }}>
            {layer.locked ? '🔒' : '🔓'}
          </button>
          {onDuplicate && (
            <button onClick={e => { e.stopPropagation(); onDuplicate(); }} title="Duplicate"
              style={{ padding: '2px 6px', fontSize: 10, borderRadius: 5, border: '1.5px solid #D0D7DE', background: '#F6F8FA', color: '#444', cursor: 'pointer' }}>⎘</button>
          )}
          <button onClick={e => { e.stopPropagation(); onDelete(); }} title="Delete"
            style={{ padding: '2px 6px', fontSize: 10, borderRadius: 5, border: '1.5px solid #FCA5A5', background: '#FEF2F2', color: '#DC2626', cursor: 'pointer' }}>✕</button>
          {/* 2026-05-16 — fold/unfold chevron moved past the icon buttons so
              accidental mis-clicks don't toggle the wrong action. */}
          <span style={{ fontSize: 11, color: '#bbb', marginLeft: 2 }}>{isOpen ? '▲' : '▼'}</span>
        </div>
        {isOpen && (
          <div style={{ padding: '8px 10px', borderTop: '1px solid #EEE', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <textarea value={layerTextRaw}
              onChange={e => onPatch({ text: e.target.value, text_en: undefined, text_ar: undefined })}
              rows={2}
              placeholder="Type your text… press Enter for a new line"
              style={{ width: '100%', border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '6px 8px', fontSize: 13, boxSizing: 'border-box', fontFamily: layer.font_family || 'Cairo, sans-serif', resize: 'vertical', minHeight: 44, color: '#222', direction: hasArabicText(layerTextRaw) ? 'rtl' : 'ltr' }} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 6, overflow: 'hidden', height: 28, flexShrink: 0 }}>
                {[{ v: 'left', l: '⇤' }, { v: 'center', l: '═' }, { v: 'right', l: '⇥' }].map(({ v, l }) => {
                  const a = (layer.align || 'center') === v;
                  return (
                    <button key={v} onClick={() => onPatch({ align: v })} title={`Align ${v}`}
                      style={{ padding: '0 10px', border: 'none', background: a ? '#1565C0' : '#fff', color: a ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{l}</button>
                  );
                })}
              </div>
              <ColorPicker value={layer.color} onChange={c => onPatch({ color: c })}
                width={30} height={28} title="Text color" />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 9, color: '#999', fontWeight: 700 }}>OPACITY</span>
                <PctInput value={layer.opacity ?? 100} onChange={v => onPatch({ opacity: v })} />
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#666', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}>
                <input type="checkbox" checked={!!layer.text_shadow}
                  onChange={e => onPatch({ text_shadow: e.target.checked ? '0 2px 8px rgba(0,0,0,0.55)' : '' })}
                  style={{ accentColor: '#1565C0', margin: 0 }} />
                Shadow
              </label>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <FontSelect value={layer.font_family || ''}
                  onChange={f => onPatch({ font_family: f })}
                  fonts={fontIndex}
                  preferredScript={hasArabicText(layerTextRaw) ? 'arabic' : 'latin'}
                  width="100%" />
              </div>
              <input type="number" min="6" step="1" value={layer.font_size || 24}
                onChange={e => onPatch({ font_size: parseInt(e.target.value) || 24 })}
                title="Font size (px)"
                style={{ width: 44, height: 28, border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '0 5px', fontSize: 12, textAlign: 'center', boxSizing: 'border-box', flexShrink: 0 }} />
              <select value={layer.font_weight || 'bold'} onChange={e => onPatch({ font_weight: e.target.value })}
                title="Font weight"
                style={{ width: 70, height: 28, border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '0 4px', fontSize: 11, background: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
                <option value="900">Black</option>
                <option value="300">Light</option>
              </select>
              <select value={layer.font_style || 'normal'} onChange={e => onPatch({ font_style: e.target.value })}
                title="Font style"
                style={{ width: 64, height: 28, border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '0 4px', fontSize: 11, background: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </select>
            </div>
            {/* Phase C — Animation strip on text-layer cards. No "Apply to all"
                button (text layers are independent; each carries its own
                animation). Reset still clears the current layer's animation. */}
            <AnimSection layer={layer} onPatch={onPatch}
              onApplyToAll={null}
              onReset={() => onPatch(animDefaults())}
              slideDuration={parseInt(form.duration_sec, 10) || 15} />
          </div>
        )}
      </div>
    );
  };

  // ── Multi-zone selection (free mode) ───────────────────────────────────────
  // Set<string> of zone.id (preferred) — falls back to idx-as-string for legacy zones
  // without a stable id. activeZoneIdx is still the "primary" selected zone shown in
  // the editor panel (so per-zone fields like text layers, BG, border keep working).
  const [selectedZoneIds, setSelectedZoneIds] = useState(new Set());
  const zoneKey = (z, idx) => z?.id || `__idx_${idx}`;
  const isZoneSelected = (idx) => {
    const z = form.zones[idx];
    if (!z) return false;
    if (selectedZoneIds.size === 0) return idx === activeZoneIdx; // single-select fallback
    return selectedZoneIds.has(zoneKey(z, idx));
  };
  const replaceZoneSelection = (idx) => {
    const z = form.zones[idx];
    if (!z) return;
    setSelectedZoneIds(new Set([zoneKey(z, idx)]));
    setActiveZoneIdx(idx);
  };
  const toggleZoneSelection = (idx) => {
    const z = form.zones[idx];
    if (!z) return;
    setSelectedZoneIds(prev => {
      const next = new Set(prev);
      // If selection is empty (relying on single-select via activeZoneIdx), seed with current active
      if (next.size === 0 && activeZoneIdx != null && form.zones[activeZoneIdx]) {
        next.add(zoneKey(form.zones[activeZoneIdx], activeZoneIdx));
      }
      const k = zoneKey(z, idx);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
    setActiveZoneIdx(idx);
  };
  // Resolve current selection to an array of zone indices (always includes activeZoneIdx as fallback)
  const getSelectedZoneIndices = () => {
    if (selectedZoneIds.size === 0) {
      return activeZoneIdx != null ? [activeZoneIdx] : [];
    }
    const result = [];
    form.zones.forEach((z, i) => {
      if (selectedZoneIds.has(zoneKey(z, i))) result.push(i);
    });
    return result;
  };
  // Reset multi-selection when slide or zone count changes meaningfully
  useEffect(() => { setSelectedZoneIds(new Set()); }, [selectedId, form.zone_mode]);

  // Delete the currently selected zone(s). Used by the Delete key + the editor's ✕ button.
  // 2026-05-16: dropped the "≥1 zone" floor — designers asked to be able to
  // build slides with text layers and video bg only, no zones at all.
  const deleteSelectedZones = () => {
    const indices = getSelectedZoneIndices();
    if (!indices.length) return;
    if (indices.length > 1) {
      if (!window.confirm(`Delete ${indices.length} selected zones?`)) return;
    } else {
      if (!window.confirm('Delete this zone?')) return;
    }
    const idxSet = new Set(indices);
    setForm(f => ({ ...f, zones: f.zones.filter((_, i) => !idxSet.has(i)) }));
    setActiveZoneIdx(prev => {
      const remaining = form.zones.length - indices.length;
      return remaining > 0 ? Math.max(0, Math.min(prev, remaining - 1)) : 0;
    });
    setSelectedZoneIds(new Set());
  };

  // ── Clipboard handlers (cut / copy / paste) — 2026-05-16 ───────────────────
  // Works across slides (localStorage). Paste regenerates ids so the same
  // zone/text can be pasted multiple times without duplicates clashing.
  const copyZoneAt = (idx) => {
    const z = form.zones[idx];
    if (!z) return;
    writeSlideClipboard({ kind: 'zone', data: { ...z, id: null } });
    toast.success(`Copied ${z.name || `Zone ${idx + 1}`}`);
  };
  const cutZoneAt = (idx) => {
    const z = form.zones[idx];
    if (!z) return;
    writeSlideClipboard({ kind: 'zone', data: { ...z, id: null } });
    setForm(f => ({ ...f, zones: f.zones.filter((_, i) => i !== idx) }));
    setActiveZoneIdx(prev => {
      const remaining = form.zones.length - 1;
      return remaining > 0 ? Math.max(0, Math.min(prev, remaining - 1)) : 0;
    });
    setSelectedZoneIds(new Set());
    toast.success(`Cut ${z.name || `Zone ${idx + 1}`}`);
  };
  const copySlideTextAt = (idx) => {
    const t = (form.text_layers || [])[idx];
    if (!t) return;
    writeSlideClipboard({ kind: 'text', data: { ...t, id: null, label: null } });
    toast.success(`Copied ${t.label || 'text'}`);
  };
  const cutSlideTextAt = (idx) => {
    const t = (form.text_layers || [])[idx];
    if (!t) return;
    writeSlideClipboard({ kind: 'text', data: { ...t, id: null, label: null } });
    setForm(f => ({ ...f, text_layers: (f.text_layers || []).filter((_, i) => i !== idx) }));
    setSelectedSlideTextIdx(null);
    setExpandedSlideTextIdx(null);
    toast.success(`Cut ${t.label || 'text'}`);
  };
  // Phase C — animation helpers shared by zone + text-layer AnimSection callers.
  // ANIM_DEFAULT keys are what "Reset" writes back; ANIM_KEYS is the projection
  // copied by "Apply to all" so we don't smear unrelated fields (text content,
  // size, etc.) onto other layers.
  const ANIM_KEYS = ['time_in', 'time_out', 'effect_in', 'effect_stay', 'effect_out', 'opacity_start', 'opacity_end'];
  const animDefaults = () => ({
    time_in: 0,
    time_out: parseInt(form.duration_sec, 10) || 15,
    effect_in: 'none', effect_stay: 'none', effect_out: 'none',
    opacity_start: 100, opacity_end: 100,
  });
  // Pull just the animation fields from a source layer (filling with defaults
  // for any field the source has never set).
  const extractAnim = (src) => {
    const d = animDefaults();
    const out = {};
    for (const k of ANIM_KEYS) out[k] = src[k] !== undefined ? src[k] : d[k];
    return out;
  };
  // Smear `anim` onto every text layer + free-mode zone on the slide except
  // the source layer (identified by id).
  const applyAnimToAll = (sourceId, anim) => {
    setForm(f => ({
      ...f,
      text_layers: (f.text_layers || []).map(l => l.id === sourceId ? l : { ...l, ...anim }),
      zones:       (f.zones || []).map(z => z.id === sourceId ? z : { ...z, ...anim }),
    }));
    toast.success('Animation applied to all layers');
  };

  const pasteClipboard = () => {
    const c = readSlideClipboard();
    if (!c || !c.data) return toast('Nothing to paste', { icon: 'ℹ️' });
    if (c.kind === 'zone') {
      const maxZ = form.zones.reduce((m, z) => Math.max(m, z.z_index || 0), 0);
      const newZone = {
        ...c.data,
        id: `z_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        x: (c.data.x || 0) + 24,
        y: (c.data.y || 0) + 24,
        z_index: maxZ + 1,
        // deep-copy nested text layers so edits don't bleed across pastes
        text_layers: (c.data.text_layers || []).map(l => ({
          ...l, id: `tl_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        })),
      };
      setForm(f => ({ ...f, zones: [...f.zones, newZone] }));
      setActiveZoneIdx(form.zones.length);
      toast.success('Zone pasted');
    } else if (c.kind === 'text') {
      const newLayer = {
        ...c.data,
        id: `tl_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        label: nextTextLabel(form.text_layers || []),
        x: (c.data.x || 0) + 24,
        y: (c.data.y || 0) + 24,
      };
      setForm(f => ({ ...f, text_layers: [...(f.text_layers || []), newLayer] }));
      setSelectedSlideTextIdx((form.text_layers || []).length);
      toast.success('Text pasted');
    }
  };

  // ── Free-mode drag/resize state ────────────────────────────────────────────
  const [dragState, setDragState] = useState(null);
  const previewWrapRef = useRef(null);
  // Live measurement of the preview pane.
  //
  // The Sidebar layout is `display: flex` with the sidebar fixed-positioned and
  // .sb-main as `flex: 1`. .sb-main's BOX width stays at 100vw regardless of the
  // sidebar's margin-left — only its visible-on-screen area shrinks (clipped by
  // the parent's overflow:hidden). So a ResizeObserver on the preview pane never
  // fires when you toggle the sidebar.
  //
  // Instead we observe the .sb-sidebar element itself (its width really does
  // change between SB_WIDTH and SB_RAIL) and compute the visible pane width as
  // `winW − sidebarWidth − editor − gaps`. Falls back to a pane ResizeObserver
  // for any other layout cause (e.g. window resize already covered by winW).
  const previewPaneRef = useRef(null);
  const [paneSize, setPaneSize] = useState({ w: 0, h: 0 });
  const [sidebarW, setSidebarW] = useState(() => {
    if (typeof window === 'undefined') return 260;
    const sb = document.querySelector('.sb-sidebar');
    return sb ? sb.getBoundingClientRect().width : 260;
  });
  useLayoutEffect(() => {
    const sb = document.querySelector('.sb-sidebar');
    if (!sb || typeof ResizeObserver === 'undefined') return;
    setSidebarW(sb.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const w = e.contentRect.width;
        setSidebarW(prev => Math.abs(prev - w) > 1 ? w : prev);
      }
    });
    ro.observe(sb);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    const el = previewPaneRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const r0 = el.getBoundingClientRect();
    setPaneSize({ w: r0.width, h: r0.height });
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const cr = e.contentRect;
        setPaneSize(prev => (Math.abs(prev.w - cr.width) > 1 || Math.abs(prev.h - cr.height) > 1)
          ? { w: cr.width, h: cr.height } : prev);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Live measurement of the page header height — used to offset the sticky preview so
  // it sits just below the frozen header (no overlap regardless of header height changes).
  const pageHeaderRef = useRef(null);
  const [headerH, setHeaderH] = useState(60);
  useLayoutEffect(() => {
    const el = pageHeaderRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    setHeaderH(el.getBoundingClientRect().height || 60);
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const h = e.contentRect.height || el.offsetHeight;
        setHeaderH(prev => Math.abs(prev - h) > 1 ? h : prev);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Undo / Redo (multi-step history of `form`) ─────────────────────────────
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const isRevertingRef = useRef(false);
  const [historyTick, setHistoryTick] = useState(0); // forces button enabled state to re-render
  // Push form snapshot to history (debounced 300ms so rapid edits coalesce)
  useEffect(() => {
    if (isRevertingRef.current) { isRevertingRef.current = false; return; }
    if (loading) return;
    const t = setTimeout(() => {
      // Truncate any "redo" tail before pushing new state
      historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1);
      const snap = JSON.parse(JSON.stringify(form));
      historyRef.current.push(snap);
      if (historyRef.current.length > 60) historyRef.current.shift();
      historyIdxRef.current = historyRef.current.length - 1;
      setHistoryTick(t => t + 1);
    }, 300);
    return () => clearTimeout(t);
  }, [form, loading]);
  // Reset history when slide selection changes
  useEffect(() => {
    historyRef.current = [];
    historyIdxRef.current = -1;
    setHistoryTick(t => t + 1);
  }, [selectedId]);
  const doUndo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current -= 1;
    isRevertingRef.current = true;
    setForm(JSON.parse(JSON.stringify(historyRef.current[historyIdxRef.current])));
    setHistoryTick(t => t + 1);
  }, []);
  const doRedo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current += 1;
    isRevertingRef.current = true;
    setForm(JSON.parse(JSON.stringify(historyRef.current[historyIdxRef.current])));
    setHistoryTick(t => t + 1);
  }, []);
  // Ctrl+Z = undo, Ctrl+R / Ctrl+Y / Ctrl+Shift+Z = redo, Delete = remove selected zone(s)
  useEffect(() => {
    const onKey = (e) => {
      // Ignore if user is typing into a text field (except contenteditable)
      const tag = (e.target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      const k = (e.key || '').toLowerCase();
      // Delete / Backspace deletes the active (or multi-selected) zone(s) in free mode
      if ((e.key === 'Delete' || (e.key === 'Backspace' && (e.metaKey || e.shiftKey))) && !isEditable) {
        // Slide-level text takes precedence over zones when selected
        if (selectedSlideTextIdx != null && (form.text_layers || [])[selectedSlideTextIdx]) {
          e.preventDefault();
          if (window.confirm('Delete this text layer?')) deleteSlideTextLayer(selectedSlideTextIdx);
          return;
        }
        if (form.zone_mode === 'free') {
          e.preventDefault();
          deleteSelectedZones();
          return;
        }
      }
      if (!ctrlOrMeta) return;
      if (k === 'z' && !e.shiftKey) {
        if (isEditable) return; // let browser handle text-field undo
        e.preventDefault();
        doUndo();
      } else if (k === 'y' || k === 'r' || (e.shiftKey && k === 'z')) {
        // Always intercept Ctrl+R (override page-reload) for redo
        e.preventDefault();
        doRedo();
      } else if (!isEditable && (k === 'c' || k === 'x' || k === 'v' || k === 'd')) {
        // 2026-05-16: Ctrl+C/X/V/D for the selected zone/text. Slide-level
        // text wins precedence so the designer can clipboard a t1 without
        // first deselecting it. Falls through to free-mode zone when no text
        // is selected. Grid-mode zones aren't clipboard-able (they're an
        // implicit grid — no per-zone identity worth carrying across slides).
        const txtIdx  = selectedSlideTextIdx;
        const zoneIdx = (form.zone_mode === 'free') ? activeZoneIdx : null;
        const targetText = (txtIdx != null && (form.text_layers || [])[txtIdx]);
        const targetZone = (zoneIdx != null && form.zones[zoneIdx]);

        if (k === 'v') {
          e.preventDefault();
          pasteClipboard();
        } else if (targetText) {
          e.preventDefault();
          if      (k === 'c') copySlideTextAt(txtIdx);
          else if (k === 'x') cutSlideTextAt(txtIdx);
          else if (k === 'd') duplicateSlideTextLayer(txtIdx);
        } else if (targetZone) {
          e.preventDefault();
          if      (k === 'c') copyZoneAt(zoneIdx);
          else if (k === 'x') cutZoneAt(zoneIdx);
          else if (k === 'd') duplicateZoneAt(zoneIdx);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [doUndo, doRedo, form.zones, form.text_layers, form.zone_mode, activeZoneIdx, selectedZoneIds, selectedSlideTextIdx]);

  // Phase C MVP (2026-05-16) — playback loop. requestAnimationFrame advances
  // currentTime by the real delta between frames so the playhead tracks wall
  // time even when the tab throttles to 30 fps. Wraps to 0 at duration_sec.
  useEffect(() => {
    if (!isPlaying) {
      if (playFrameRef.current) cancelAnimationFrame(playFrameRef.current);
      playFrameRef.current = null;
      return;
    }
    playLastTsRef.current = 0;
    const tick = (ts) => {
      const dur = parseInt(form.duration_sec, 10) || 15;
      if (playLastTsRef.current === 0) { playLastTsRef.current = ts; playFrameRef.current = requestAnimationFrame(tick); return; }
      const dt = (ts - playLastTsRef.current) / 1000;
      playLastTsRef.current = ts;
      setCurrentTime(t => {
        const nx = t + dt;
        return nx >= dur ? 0 : nx;
      });
      playFrameRef.current = requestAnimationFrame(tick);
    };
    playFrameRef.current = requestAnimationFrame(tick);
    return () => { if (playFrameRef.current) cancelAnimationFrame(playFrameRef.current); };
  }, [isPlaying, form.duration_sec]);

  // Spacebar toggles play/pause when no input is focused. Same edit-field
  // guard as the Ctrl-shortcut handler — typing into an input shouldn't
  // hijack the spacebar.
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'Space') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      e.preventDefault();
      setIsPlaying(p => !p);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Arrow keys nudge the selected object by 1 design-unit (≈ 2 slide-px), Shift = 10.
  // Slide-level text takes precedence when selected; otherwise nudges zone(s) in free mode.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if (isEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key;
      if (k !== 'ArrowUp' && k !== 'ArrowDown' && k !== 'ArrowLeft' && k !== 'ArrowRight') return;
      const step = e.shiftKey ? 10 : 1;
      let dx = 0, dy = 0;
      if (k === 'ArrowLeft')  dx = -step;
      if (k === 'ArrowRight') dx =  step;
      if (k === 'ArrowUp')    dy = -step;
      if (k === 'ArrowDown')  dy =  step;
      const _wPx = parseInt(form.width_px, 10);
      const _hPx = parseInt(form.height_px, 10);
      const _ori = form.orientation || 'H';
      const dimW = (_wPx > 0) ? _wPx / 2 : DESIGN[_ori].w;
      const dimH = (_hPx > 0) ? _hPx / 2 : DESIGN[_ori].h;
      // Slide-level text wins over zone selection
      if (selectedSlideTextIdx != null && (form.text_layers || [])[selectedSlideTextIdx]) {
        e.preventDefault();
        setForm(f => ({
          ...f,
          text_layers: (f.text_layers || []).map((l, i) => {
            if (i !== selectedSlideTextIdx) return l;
            const w = typeof l.w === 'number' ? l.w : 0;
            const h = typeof l.h === 'number' && l.h > 0 ? l.h : 0;
            const nx = Math.max(0, Math.min(Math.max(0, dimW - w), (l.x || 0) + dx));
            const ny = Math.max(0, Math.min(Math.max(0, dimH - h), (l.y || 0) + dy));
            return { ...l, x: Math.round(nx), y: Math.round(ny) };
          }),
        }));
        return;
      }
      if (form.zone_mode !== 'free') return;
      const indices = getSelectedZoneIndices();
      if (!indices.length) return;
      if (indices.every(i => form.zones[i]?.locked)) return;
      e.preventDefault();
      setForm(f => ({
        ...f,
        zones: f.zones.map((z, i) => {
          if (!indices.includes(i) || z.locked) return z;
          const w = z.w || 0, h = z.h || 0;
          const nx = Math.max(0, Math.min(Math.max(0, dimW - w), (z.x || 0) + dx));
          const ny = Math.max(0, Math.min(Math.max(0, dimH - h), (z.y || 0) + dy));
          return { ...z, x: Math.round(nx), y: Math.round(ny) };
        }),
      }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [form.zones, form.text_layers, form.zone_mode, form.width_px, form.height_px, form.orientation, activeZoneIdx, selectedZoneIds, selectedSlideTextIdx]);

  const canUndo = historyIdxRef.current > 0;
  const canRedo = historyIdxRef.current < historyRef.current.length - 1;
  // Reference historyTick so React keeps button states fresh
  void historyTick;

  // ── Reorder zones (drag tab L/R) — also re-assigns z_index by tab order ────
  const reorderZones = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setForm(f => {
      const zones = [...f.zones];
      const [moved] = zones.splice(fromIdx, 1);
      zones.splice(toIdx, 0, moved);
      // Re-assign z_index = tab position (left = bottom, right = top)
      const zoned = zones.map((z, i) => ({ ...z, z_index: i }));
      return { ...f, zones: zoned };
    });
    // Keep activeZoneIdx pointed at the moved zone
    setActiveZoneIdx(toIdx);
  };
  const [tabDragIdx, setTabDragIdx] = useState(null);
  const [tabDropIdx, setTabDropIdx] = useState(null);
  // Inline tab name edit
  const [tabEditIdx, setTabEditIdx] = useState(null);

  const handleZoneBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgUploading(true);
    updateZone('bg_image', '');
    const isPng = (file.type || '').toLowerCase() === 'image/png' || (file.name || '').toLowerCase().endsWith('.png');

    // Read the image dimensions BEFORE upload so we can auto-fit the zone to its aspect ratio.
    // We do this in parallel with the upload so it doesn't slow anything down.
    const aspectPromise = new Promise(resolve => {
      try {
        const img = new window.Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          const a = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : null;
          URL.revokeObjectURL(url);
          resolve(a);
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      } catch { resolve(null); }
    });

    setExportOverlay('Uploading zone background…');
    setComposePct(0);
    try {
      const brandSlug = sanitize(restaurant?.name || '') || 'slide';
      const slideName = form.slide_name ? `${brandSlug}-${form.slide_name}` : brandSlug;
      const formData = new FormData();
      formData.append('image', file);
      formData.append('slide_name', slideName);
      formData.append('zone_num', String(activeZoneIdx + 1));
      if (brandId) formData.append('brand_id', brandId);
      if (selectedId) formData.append('slide_id', selectedId);
      const [res, aspect] = await Promise.all([
        api.post('/upload/zone-bg', formData, {
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 95);
            setComposePct(pct);
            if (pct >= 95) setExportOverlay('Processing…');
          },
        }),
        aspectPromise,
      ]);
      const newUrl = res.data.url + '?t=' + Date.now();

      // Auto-fit zone to image's aspect ratio (only in Free Layout mode — Grid mode
      // sizes zones from cols/rows, so user-controlled aspect doesn't apply there).
      // Strategy: keep the zone's current width, change height so w/h matches image.
      // User can still resize freely afterwards via the corner handles.
      const dimNow = DESIGN[form.orientation] || DESIGN.H;
      const fitInFreeMode = form.zone_mode === 'free' && aspect && aspect > 0;

      setForm(f => ({
        ...f,
        zones: f.zones.map((z, i) => {
          if (i !== activeZoneIdx) return z;
          const next = { ...z, bg_image: newUrl };
          if (isPng) next.bg_overlay = 0;
          if (fitInFreeMode) {
            const curW = Math.max(40, z.w || 200);
            let newH = Math.round(curW / aspect);
            // Clamp to canvas height — if the proportional height would overflow the slide,
            // shrink width instead so the whole zone stays inside the canvas.
            if (newH > dimNow.h) {
              newH = dimNow.h;
              const newW = Math.round(newH * aspect);
              return { ...next, w: newW, h: newH };
            }
            return { ...next, h: newH };
          }
          return next;
        }),
      }));
      if (res.data.warning) toast(`⚠️ ${res.data.warning}`, { icon: '🔄' });
      else if (fitInFreeMode) toast.success(`Background uploaded — zone resized to image aspect (${aspect.toFixed(2)}:1)`);
      else toast.success(isPng ? 'Transparent PNG uploaded — DIM set to 0' : 'Background uploaded!');
    } catch { toast.error('Upload failed'); }
    finally {
      setComposePct(null);
      setExportOverlay('');
      setBgUploading(false);
      if (bgFileRef.current) bgFileRef.current.value = '';
    }
  };

  const handleSlideBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSlideBgUploading(true);
    setExportOverlay('Uploading background…');
    setComposePct(0);
    setForm(f => ({ ...f, slide_bg_image: '' }));
    const isPng = (file.type || '').toLowerCase() === 'image/png' || (file.name || '').toLowerCase().endsWith('.png');
    try {
      const brandSlug = sanitize(restaurant?.name || '') || 'slide';
      const slideName = form.slide_name ? `${brandSlug}-${form.slide_name}` : brandSlug;
      const formData = new FormData();
      formData.append('image', file);
      formData.append('slide_name', slideName);
      if (brandId) formData.append('brand_id', brandId);
      if (selectedId) formData.append('slide_id', selectedId);
      const res = await api.post('/upload/slide-bg', formData, {
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 95);  // cap 95 — server still processes
          setComposePct(pct);
          if (pct >= 95) setExportOverlay('Processing…');
        },
      });
      setForm(f => ({
        ...f,
        slide_bg_image: res.data.url + '?t=' + Date.now(),
        // PNG defaults to DIM 0 to preserve transparency
        slide_bg_overlay: isPng ? 0 : (f.slide_bg_overlay ?? 50),
      }));
      if (res.data.warning) toast(`⚠️ ${res.data.warning}`, { icon: '🔄' });
      else toast.success(isPng ? 'Transparent PNG uploaded — DIM set to 0' : 'Slide background uploaded!');
    } catch { toast.error('Upload failed'); }
    finally {
      setComposePct(null);
      setExportOverlay('');
      setSlideBgUploading(false);
      if (slideBgFileRef.current) slideBgFileRef.current.value = '';
    }
  };

  // ── Phase A (2026-05-16): video-slide background upload ───────────────────
  // POSTs the raw video to /brand-slides/:id/upload-video, which ffmpeg-encodes
  // the final s{id}-bg.mp4 (slide W×H, stretch-fill) and a 640-edge preview
  // file used by the in-editor <video> element. The endpoint also flips
  // slide_kind to 'video' and sets duration_sec from the probed clip on the
  // first upload only.
  //
  // Requires the slide to be saved first because the URL contains :id —
  // a fresh slide has no row yet.
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedId) {
      toast.error('Save the slide first, then upload the video');
      if (videoFileRef.current) videoFileRef.current.value = '';
      return;
    }
    setVideoUploading(true);
    setExportOverlay('Uploading video…');
    setComposePct(0);
    // Phase 1: axios upload bytes 0→50%. Phase 2: backend ffmpeg encode 50→99%
    // via the same /compose-progress poll we use for compose. 100% on response.
    let encPoll = null;
    try {
      const formData = new FormData();
      formData.append('video', file);
      const res = await api.post(`/brand-slides/${selectedId}/upload-video`, formData, {
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const uploadPct = Math.round((evt.loaded / evt.total) * 100);
          setComposePct(Math.min(50, Math.round(uploadPct * 0.5)));
          if (uploadPct >= 100 && !encPoll) {
            setExportOverlay('Encoding video…');
            encPoll = setInterval(async () => {
              try {
                const r = await api.get(`/brand-slides/${selectedId}/compose-progress`);
                if (typeof r.data?.percent === 'number') {
                  setComposePct(50 + Math.round(r.data.percent * 0.49));
                }
              } catch {}
            }, 800);
          }
        },
      });
      const ts  = Date.now();
      setForm(f => ({
        ...f,
        slide_kind:         'video',
        video_path:         (res.data.video_path         || '') + '?t=' + ts,
        video_preview_path: (res.data.video_preview_path || '') + '?t=' + ts,
        // Duration: server returns the (possibly probed) authoritative value.
        duration_sec:       res.data.duration_sec || f.duration_sec || 15,
      }));
      // Reflect the new paths in the in-memory slides[] list so subsequent
      // populateForm() (e.g. switching slides and back) reads the fresh URLs.
      setSlides(prev => prev.map(s => s.id === selectedId
        ? { ...s, slide_kind: 'video',
            video_path: res.data.video_path || '',
            video_preview_path: res.data.video_preview_path || '',
            duration_sec: res.data.duration_sec || s.duration_sec }
        : s));
      toast.success(`Video uploaded (${res.data.probed_duration?.toFixed?.(1) || res.data.duration_sec}s)`);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Upload failed';
      toast.error(`Video upload failed: ${msg}`);
    } finally {
      if (encPoll) clearInterval(encPoll);
      setComposePct(null);
      setExportOverlay('');
      setVideoUploading(false);
      if (videoFileRef.current) videoFileRef.current.value = '';
    }
  };

  // 2026-05-16: clear the video bg without re-uploading. Hits the DELETE
  // endpoint, which removes the on-disk files and blanks the four video_*
  // columns. slide_kind stays 'video' so the designer can re-upload right
  // after without re-toggling kind. UI reflects the empty state immediately.
  const handleVideoClear = async () => {
    if (!selectedId) return;
    if (!window.confirm('Remove the background video from this slide?')) return;
    setVideoUploading(true);
    try {
      await api.delete(`/brand-slides/${selectedId}/video`);
      setForm(f => ({ ...f, video_path: '', video_preview_path: '' }));
      setSlides(prev => prev.map(s => s.id === selectedId
        ? { ...s, video_path: '', video_preview_path: '', video_hash: '', overlay_hash: '' }
        : s));
      toast.success('Video removed');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Remove failed';
      toast.error(`Could not remove video: ${msg}`);
    } finally {
      setVideoUploading(false);
    }
  };

  // ── Slide operations ──────────────────────────────────────────────────────
  const selectSlide = (slide) => {
    if (selectedId && selectedId !== slide.id) {
      const payload = buildPayload(slides.findIndex(s => s.id === selectedId));
      api.put(`/menuboard/slides/${selectedId}`, payload)
        .then(() => setSlides(prev => prev.map(s => s.id === selectedId ? { ...s, ...payload } : s)))
        .catch(() => {});
    }
    setSelectedId(slide.id);
    const sc = allScreens.find(s => s.id === parseInt(screenId));
    populateForm(slide, sc?.orientation || 'H');
  };

  // Save to DB + render JPEG + save to filesystem
  const handleSaveSlide = async () => {
    if (isBrandMode) {
      const suffix = form.slide_name.trim();
      if (!suffix) {
        toast.error('Please enter a slide name before saving.');
        return;
      }
      const brandSlug = sanitize(restaurant?.name || '');
      const fullName = brandSlug ? `${brandSlug}-${suffix}` : suffix;
      const duplicate = allBrandSlides.find(s => s.id !== selectedId &&
        (s.slide_name === fullName || s.slide_name === suffix));
      if (duplicate) {
        toast.error(`Slide name "${suffix}" already exists for this brand. Please use a unique name.`);
        return;
      }
    }
    setSaving(true);
    try {
      if (isBrandMode) {
        const brandSlug = sanitize(restaurant?.name || '');
        const fullSlideName = brandSlug ? `${brandSlug}-${form.slide_name.trim()}` : form.slide_name.trim();
        // Brand-slide mode ────────────────────────────────────────────────
        const brandPayload = {
          slide_name: fullSlideName,
          bg_color: form.bg_color, accent_color: form.accent_color,
          zone_cols: form.zone_cols, zone_rows: form.zone_rows,
          zone_mode: form.zone_mode || 'grid',
          zones: form.zones, orientation: form.orientation,
          slide_order: 0,
          slide_bg_image: form.slide_bg_image || '',
          slide_bg_overlay: form.slide_bg_overlay ?? 50,
          // Phase 1 (2026-05-14) — per-slide output resolution.
          // Phase A (2026-05-16) — defaults bumped to 1280×720 / 720×1280.
          width_px:  parseInt(form.width_px,  10) || (form.orientation === 'V' ? 720  : 1280),
          height_px: parseInt(form.height_px, 10) || (form.orientation === 'V' ? 1280 : 720),
          // Phase 3 (2026-05-14) — slide-level free text layers
          text_layers: form.text_layers || [],
          lang,
          // Phase A (2026-05-16) — video-slide controls. video_path /
          // video_preview_path are managed by /upload-video and never carried
          // by Save (server preserves them on partial UPDATE).
          slide_kind:   form.slide_kind === 'video' ? 'video' : 'image',
          duration_sec: parseInt(form.duration_sec, 10) || 15,
          // 2026-05-18 — when bg duration matches slide duration, force
          // 'reset' regardless of the form state. Belt-and-suspenders with
          // the useEffect that auto-sets it: this catches the case where the
          // user-clicked-loop save races a fresh metadata-load update.
          loop_mode:    durationsMatch ? 'reset'
                       : ((form.loop_mode === 'freeze' || form.loop_mode === 'reset') ? form.loop_mode : 'loop'),
          has_audio:    !!form.has_audio,
          auto_darken:  parseInt(form.auto_darken, 10) || 0,
        };
        let currentId = selectedId;
        if (!currentId) {
          // New slide — create it first
          const res = await api.post('/brand-slides', { brand_id: parseInt(brandId), ...brandPayload });
          currentId = res.data.id;
          setSelectedId(currentId);
          setSlides([res.data]);
          // Update URL to reflect saved slide
          navigate(`/brands/${brandId}/slide-builder/${currentId}`, { replace: true });
        } else {
          const putRes = await api.put(`/brand-slides/${currentId}`, brandPayload);
          // Sync bg URLs from server response — PUT may have renamed files.
          // Use the server value when defined; fall back to local only if server omitted the field.
          if (putRes.data) {
            const saved = putRes.data;
            const savedZones = Array.isArray(saved.zones) ? saved.zones : [];
            // CRITICAL: append a cache-buster to every bg URL we write back into form
            // state. The backend rename step often OVERWRITES an existing file at the
            // exact URL the browser had cached (e.g., when the slide name didn't change),
            // so without a cache-buster the <img> would show the previously-cached
            // (old) image content even though the disk file now holds the new content.
            // Stamp once with a single timestamp so all zones share a cache version.
            const ts = Date.now();
            const bust = (url) => {
              if (!url) return url;
              const clean = url.replace(/\?.*$/, '');  // drop any prior ?t= or ?v=
              return clean + '?t=' + ts;
            };
            // slide_bg_image: use server value (even if ''); fall back only if undefined
            const rawSlideBg = saved.slide_bg_image !== undefined ? saved.slide_bg_image : brandPayload.slide_bg_image;
            const newSlideBg = bust(rawSlideBg);
            brandPayload.slide_bg_image = newSlideBg;
            brandPayload.zones = Array.isArray(brandPayload.zones)
              ? brandPayload.zones.map((z, i) => {
                  const sv = savedZones[i];
                  // Only update bg_image from server if server zone exists; keeps '' when server cleared it
                  if (sv === undefined) return z;
                  const raw = sv.bg_image ?? z.bg_image;
                  return { ...z, bg_image: bust(raw) };
                })
              : brandPayload.zones;
            // CRITICAL: update slides[] in-memory state so populateForm() on slide-switch
            // reads the correct (possibly cleared) bg URLs, not the stale pre-save values.
            setSlides(prev => prev.map(s => s.id === currentId
              ? { ...s, slide_bg_image: newSlideBg, zones: brandPayload.zones }
              : s));
            setForm(f => ({
              ...f,
              slide_bg_image: newSlideBg,
              zones: f.zones.map((z, i) => {
                const sv = savedZones[i];
                if (sv === undefined) return z;
                const raw = sv.bg_image ?? z.bg_image;
                return { ...z, bg_image: bust(raw) };
              }),
            }));
          }
        }
        // Phase B (2026-05-16): video-kind slides route to /compose-video,
        // which produces a final MP4 from the uploaded bg.mp4 + the rendered
        // transparent overlay. Image-kind keeps the legacy /save-jpeg path.
        // Video slides without an uploaded video skip the render entirely —
        // designer can save the row's metadata and upload later.
        const isVideoSlide = brandPayload.slide_kind === 'video';
        const hasVideoFile = !!form.video_path;
        if (isVideoSlide && !hasVideoFile) {
          toast('Slide saved. Upload a video and save again to render.', { icon: 'ℹ️' });
        } else {
          setExportOverlay(isVideoSlide ? 'Composing video…' : 'Saving slide image…');
          try {
            const slideData = { ...brandPayload, id: currentId };
            // Phase C-β: for video slides we render the STATIC base with
            // animated text layers excluded, then render each animated text
            // layer alone for fade-in/out + time-gating via ffmpeg. Animated
            // zone bgs are NOT composited separately (reverted 2026-05-16:
            // multi-input ffmpeg compose was too slow + corrupting).
            // Image slides keep the all-in-one snapshot (save-jpeg path).
            const baseOpts  = isVideoSlide ? { excludeAnimated: true } : {};
            const canvas    = await doRenderSlide(slideData, form.orientation, baseOpts);
            const imageData = canvas.toDataURL('image/webp', 0.92);
            const animLayers = isVideoSlide
              ? await renderAnimatedLayers(slideData, form.orientation)
              : [];
            const endpoint  = isVideoSlide ? 'compose-video' : 'save-jpeg';
            const body = isVideoSlide
              ? { image_data: imageData, brand_id: parseInt(brandId), anim_layers: animLayers }
              : { image_data: imageData, brand_id: parseInt(brandId) };
            // 2026-05-17 — poll compose progress every 1s while the POST is
            // in flight so the user sees a real % bar instead of an opaque
            // spinner. Cleared in finally below.
            let composePoll = null;
            if (isVideoSlide) {
              setComposePct(0);
              composePoll = setInterval(async () => {
                try {
                  const r = await api.get(`/brand-slides/${currentId}/compose-progress`);
                  if (typeof r.data?.percent === 'number') setComposePct(r.data.percent);
                } catch {}
              }, 1000);
            }
            let imgRes;
            try {
              imgRes = await api.post(`/brand-slides/${currentId}/${endpoint}`, body);
            } finally {
              if (composePoll) clearInterval(composePoll);
              setComposePct(null);
            }
            // Store jpeg_path, thumb_path AND updated_at so cache-bust works on return to SlideManager
            setSlides(prev => prev.map(s => s.id === currentId ? { ...s, ...imgRes.data } : s));
            setAllBrandSlides(prev => prev.some(s => s.id === currentId)
              ? prev.map(s => s.id === currentId ? { ...s, slide_name: fullSlideName } : s)
              : [...prev, { id: currentId, slide_name: fullSlideName }]);
            toast.success(isVideoSlide
              ? (imgRes.data?.cached ? 'No changes — using cached video' : 'Video composed! ✓')
              : 'Slide saved! ✓');
          } catch (imgErr) {
            const what = isVideoSlide ? 'compose' : 'image';
            toast.error(`Data saved, ${what} failed: ` + (imgErr.response?.data?.error || imgErr.message));
          }
        }
      } else {
        // Legacy screen mode ──────────────────────────────────────────────
        if (!selectedId) { setSaving(false); return; }
        const payload = buildPayload(slides.findIndex(s => s.id === selectedId));
        await api.put(`/menuboard/slides/${selectedId}`, payload);
        setSlides(prev => prev.map(s => s.id === selectedId ? { ...s, ...payload } : s));
        if (restaurant?.id) {
          setExportOverlay('Saving slide image…');
          try {
            const slideData = { ...slides.find(s => s.id === selectedId), ...payload };
            const canvas    = await doRenderSlide(slideData, form.orientation);
            const imageData = canvas.toDataURL('image/jpeg', 0.95);
            await api.post(`/menuboard/slides/${selectedId}/save-jpeg`, { image_data: imageData, brand_id: restaurant.id });
            toast.success('Saved!');
          } catch (imgErr) {
            toast.error('Slide data saved, but image failed: ' + (imgErr.response?.data?.error || imgErr.message));
          }
        } else {
          toast.success('Saved!');
        }
      }
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); setExportOverlay(''); }
  };

  const handleAddSlide = async () => {
    try {
      const sc = allScreens.find(s => s.id === parseInt(screenId));
      const ori = sc?.orientation || 'H';
      const res = await api.post('/menuboard/slides', {
        screen_id: parseInt(screenId), slide_order: slides.length,
        bg_color: branchTheme.bg, accent_color: branchTheme.ac,
        zone_cols: 1, zone_rows: 1, zones: [EMPTY_ZONE(0)],
        custom_label_en: '', slide_orientation: ori,
        template: 'list-view', item_ids: [], grid_cols: 1, grid_rows: 1,
      });
      setSlides(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      populateForm(res.data, ori);
      setShowHamburger(false);
      toast.success('Slide added!');
    } catch { toast.error('Error adding slide'); }
  };

  // Brand-mode equivalent of handleAddSlide. Creates a fresh empty brand-slide
  // record and selects it, so the user can edit + save without first saving the
  // currently-loaded slide (which would otherwise overwrite existing data).
  const handleBrandAddSlide = async () => {
    try {
      const ori = form.orientation || 'H';
      const brandSlug = sanitize(restaurant?.name || '') || 'brand';
      // Pick a default name that won't collide. Slides[].length+1 is fine —
      // user can rename in the editor.
      const defaultName = `${brandSlug}-slide-${(slides?.length || 0) + 1}`;
      const payload = {
        brand_id: parseInt(brandId),
        slide_name: defaultName,
        bg_color: branchTheme.bg, accent_color: branchTheme.ac,
        zone_cols: 1, zone_rows: 1,
        // A template has to be born a template — Save never sends menu_kind, so
        // a row created plain would land in the menu slides and disappear from
        // Info Screens. Free mode too: sign layouts are positioned, not gridded.
        ...(isTemplateCtx
          ? { menu_kind: 'doorsign', zone_mode: 'free', zones: [] }
          : { zone_mode: 'grid',     zones: [EMPTY_ZONE(0)] }),
        orientation: ori,
        slide_order: (slides?.length || 0),
        slide_bg_image: '',
        slide_bg_overlay: 50,
      };
      const res = await api.post('/brand-slides', payload);
      setSlides(prev => [...(prev || []), res.data]);
      setSelectedId(res.data.id);
      populateForm(res.data, ori);
      navigate(`/brands/${brandId}/slide-builder/${res.data.id}`, { replace: true });
      setShowHamburger(false);
      toast.success('Slide added!');
    } catch (e) { toast.error('Error adding slide'); }
  };

  const handleDeleteSlide = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    await api.delete(`/menuboard/slides/${id}`);
    const updated = slides.filter(s => s.id !== id);
    setSlides(updated);
    if (selectedId === id) {
      setSelectedId(updated[0]?.id || null);
      if (updated[0]) { const sc = allScreens.find(s => s.id === parseInt(screenId)); populateForm(updated[0], sc?.orientation || 'H'); }
    }
    setShowHamburger(false);
  };

  const handleDuplicateSlide = async () => {
    if (!selectedSlide) return;
    try {
      const payload = buildPayload(slides.length);
      const res = await api.post('/menuboard/slides', {
        screen_id: parseInt(screenId), ...payload,
        custom_label_en: form.slide_name ? form.slide_name + ' copy' : '',
        slide_order: slides.length,
      });
      setSlides(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      populateForm(res.data, form.orientation);
      setShowHamburger(false);
      toast.success('Duplicated!');
    } catch { toast.error('Duplicate failed'); }
  };

  // ── Brand-mode exclusive actions ─────────────────────────────────────────
  const handleBrandDuplicate = async () => {
    if (!selectedId) return;
    setShowHamburger(false);
    try {
      const res = await api.post(`/brand-slides/${selectedId}/duplicate`);
      toast.success('Duplicated!');
      navigate(`/brands/${brandId}/slide-builder/${res.data.id}`);
    } catch { toast.error('Duplicate failed'); }
  };

  const handleBrandDelete = async () => {
    setShowHamburger(false);
    if (!selectedId) { navigate(`/brands/${brandId}/slides`); return; }
    if (!window.confirm('Delete this slide?')) return;
    try {
      await api.delete(`/brand-slides/${selectedId}`);
      toast.success('Deleted');
      navigate(`/brands/${brandId}/slides`);
    } catch { toast.error('Delete failed'); }
  };

  // Fetches a saved slide image as a blob and triggers a browser download.
  // Using fetch + blob URL instead of <a href={remote}> avoids browser cache
  // serving a stale opaque copy and forces an actual download (the `download`
  // attribute is ignored for cross-origin navigations).
  // Falls back to opening in a new tab if fetch fails (e.g. CORS, missing file).
  const downloadSavedFile = async (fileUrl, slideName) => {
    if (!fileUrl) throw new Error('No file URL — slide image not saved yet.');
    const ext = (fileUrl.replace(/\?.*$/, '').match(/\.(webp|jpe?g|png)$/i) || [, 'webp'])[1].toLowerCase();
    const baseName = (slideName && sanitize(slideName) !== 'slide') ? sanitize(slideName) : `slide-${selectedId || Date.now()}`;
    const downloadName = `${baseName}.${ext}`;
    const cb  = fileUrl.includes('?') ? '&' : '?';
    const cacheBusted = `${fileUrl}${cb}cb=${Date.now()}`;
    try {
      const res = await fetch(cacheBusted, { cache: 'no-store' });
      if (!res.ok) throw new Error(`server returned HTTP ${res.status}`);
      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error('downloaded file is empty');
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      // Last-ditch fallback: open the file in a new tab so the user can right-click → Save As.
      console.error('[downloadSavedFile] fetch path failed for', fileUrl, '→', err);
      window.open(cacheBusted, '_blank', 'noopener');
      throw new Error(`${err.message} — opened in new tab as fallback`);
    }
  };

  const handleBrandDownload = async () => {
    setShowHamburger(false);

    // If slide is already saved, download the saved file directly — no re-render
    const existingJpeg = slides.find(s => s.id === selectedId)?.jpeg_path;
    if (existingJpeg) {
      try {
        await downloadSavedFile(existingJpeg, form.slide_name);
        toast.success('Downloaded!');
      } catch (err) {
        toast.error('Download failed: ' + err.message);
      }
      return;
    }

    // Not yet saved — save first, then download
    setSaving(true);
    setExportOverlay('Saving slide…');
    try {
      const brandPayload = {
        slide_name: form.slide_name,
        bg_color: form.bg_color, accent_color: form.accent_color,
        zone_cols: form.zone_cols, zone_rows: form.zone_rows,
        zone_mode: form.zone_mode || 'grid',
        zones: form.zones, orientation: form.orientation, slide_order: 0,
        width_px:  parseInt(form.width_px,  10) || (form.orientation === 'V' ? 720  : 1280),
        height_px: parseInt(form.height_px, 10) || (form.orientation === 'V' ? 1280 : 720),
        text_layers: form.text_layers || [],
        lang,
      };
      let currentId = selectedId;
      if (!currentId) {
        const res = await api.post('/brand-slides', { brand_id: parseInt(brandId), ...brandPayload });
        currentId = res.data.id;
        setSelectedId(currentId);
        navigate(`/brands/${brandId}/slide-builder/${currentId}`, { replace: true });
      } else {
        await api.put(`/brand-slides/${currentId}`, brandPayload);
      }
      // Phase B (2026-05-16): video slides hit compose-video; image slides keep save-jpeg.
      const isVideoSlide = form.slide_kind === 'video';
      if (isVideoSlide && !form.video_path) {
        toast.error('Upload a video first before downloading a video slide.');
        return;
      }
      setExportOverlay(isVideoSlide ? 'Composing video…' : 'Rendering…');
      const canvas    = await doRenderSlide({ ...brandPayload, id: currentId }, form.orientation);
      const imageData = canvas.toDataURL('image/webp', 0.92);
      setExportOverlay(isVideoSlide ? 'Composing video…' : 'Saving image…');
      const endpoint  = isVideoSlide ? 'compose-video' : 'save-jpeg';
      const imgRes = await api.post(`/brand-slides/${currentId}/${endpoint}`, { image_data: imageData, brand_id: parseInt(brandId) });
      setSlides(prev => prev.map(s => s.id === currentId ? { ...s, ...imgRes.data } : s));
      await downloadSavedFile(imgRes.data.jpeg_path, form.slide_name);
      toast.success('Downloaded!');
    } catch (err) {
      toast.error('Download failed: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); setExportOverlay(''); }
  };

  const handleZoneLayoutChange = (newCols, newRows) => {
    const newTotal = newCols * newRows;
    if (newTotal < form.zone_cols * form.zone_rows) {
      const removing = form.zones.slice(newTotal).map((z, i) => z.name || `Zone ${newTotal + i + 1}`).join(', ');
      if (!window.confirm(`Reducing zones will remove: ${removing}. Continue?`)) return;
    }
    setForm(f => ({ ...f, zone_cols: newCols, zone_rows: newRows, zones: adjustZones(f.zones, newCols, newRows) }));
    setActiveZoneIdx(prev => Math.min(prev, newTotal - 1));
  };

  // ── Resolve items ─────────────────────────────────────────────────────────
  const resolveItems = useCallback((itemIds) => {
    if (!itemIds?.length) return [];
    return itemIds.map(id => {
      const item = menuData.allItems?.find(i => i.id === id);
      if (!item) return null;
      const cat = menuData.categories.find(c => c.id === item.category_id);
      return { ...item, cat_name: cat?.name || '', cat_name_ar: cat?.name_ar || '' };
    }).filter(Boolean);
  }, [menuData]);

  const zonesWithItems = form.zones.map(z => ({ ...z, items: resolveItems(z.item_ids) }));

  const _az = form.zones[activeZoneIdx] || form.zones[0];
  useEffect(() => { if (_az) { setRawZoneCols(String(_az.cols ?? 1)); setRawZoneRows(String(_az.rows ?? 4)); } setExpandedLayerIdx(null); setTextLayersExpanded(false); }, [activeZoneIdx, selectedId]); // eslint-disable-line
  // Reset slide-level text selection when the active slide changes
  useEffect(() => { setSelectedSlideTextIdx(null); setExpandedSlideTextIdx(null); }, [selectedId]);
  // Phase 4: forget per-section manual toggles when the slide changes — fresh slide → fresh auto-fold behavior
  useEffect(() => { setUserTouchedSections(new Set()); }, [selectedId]);

  // ── Canvas export — delegates to shared utility, passing live menuData ──────
  const doRenderSlide = (slideData, orientStr, renderOpts) =>
    renderSlideToCanvas(slideData, orientStr, menuData, restaurant, lang, renderOpts);

  // Phase C-β-2 (2026-05-16) — reverted. The multi-input filter graph with
  // stream_loop on animated WebPs was making compose take >5min and produce
  // corrupted output. Animated zone bgs now show only in the editor preview;
  // saved MP4 captures the static first frame. To revive zone-bg animation in
  // the saved MP4, the more practical path is server-side Puppeteer rendering
  // (full slide playback recorded to MP4), not chained ffmpeg overlays.

  // Phase C-β (2026-05-17) — render every animated overlay (text layer OR
  // zone) in isolation and return a flat array of { image_data, time_in,
  // time_out, fade_in, fade_out } the compose endpoint fades + time-gates on
  // top of the static base. Text + zone share one pipeline: backend doesn't
  // know or care which is which — it just sees N transparent overlays with
  // timing metadata.
  //
  // Compose-time effect support: only fade-in / fade-out are translated to
  // ffmpeg alpha fades. Slide-in / zoom-in / pulse / glow stay editor-only
  // because they need per-frame spatial expressions (a bigger filter graph).
  // The layer still appears + disappears at its time_in/time_out correctly;
  // the spatial portion of the effect just doesn't show in the MP4.
  const renderAnimatedLayers = async (slideData, orientStr) => {
    const dur = parseInt(slideData.duration_sec, 10) || 15;
    const isAnimated = (item) => {
      if (!item) return false;
      const tIn  = Number.isFinite(item.time_in)  ? item.time_in  : 0;
      const tOut = Number.isFinite(item.time_out) ? item.time_out : dur;
      if (item.effect_in   && item.effect_in   !== 'none') return true;
      if (item.effect_stay && item.effect_stay !== 'none') return true;
      if (item.effect_out  && item.effect_out  !== 'none') return true;
      if (tIn !== 0 || tOut !== dur) return true;
      return false;
    };
    const animTexts = (slideData.text_layers || []).filter(isAnimated);
    const animZones = (slideData.zones       || []).filter(isAnimated);
    const out = [];
    // ORDER MATTERS — backend chains overlays in the order received. Zones
    // first so text layers (rendered on top in the normal preview's z-stack)
    // also overlay on top in the compose.
    for (const item of [...animZones, ...animTexts]) {
      if (!item.id) continue;
      const canvas    = await doRenderSlide(slideData, orientStr, { onlyLayerId: item.id });
      const imageData = canvas.toDataURL('image/webp', 0.92);
      out.push({
        image_data:  imageData,
        layer_id:    item.id,
        time_in:     Number.isFinite(item.time_in)  ? item.time_in  : 0,
        time_out:    Number.isFinite(item.time_out) ? item.time_out : dur,
        // 2026-05-17 — send raw effect names. Backend translates each to the
        // appropriate ffmpeg expression (fade=alpha for fade-in/out; overlay
        // x expression for slide-in/out; overlay y sine for gentle-bob; etc.).
        effect_in:   item.effect_in   || 'none',
        effect_stay: item.effect_stay || 'none',
        effect_out:  item.effect_out  || 'none',
      });
    }
    return out;
  };

  // Download: use saved JPEG from backend (no re-render)
  const capturePreview = async () => {
    if (!selectedSlide) return;
    setShowHamburger(false);
    try {
      const response = await api.get(`/menuboard/slides/${selectedId}/jpeg`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = slideFileName;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded!');
    } catch (err) {
      if (err.response?.status === 404) toast.error('Save the slide first, then download');
      else toast.error('Download failed');
    }
  };

  // Send to TV: auto-save first, then open modal
  const openSendToTV = async () => {
    if (!selectedSlide) return toast.error('No slide selected');
    if (!restaurant?.id) return toast.error('Brand data not loaded');
    setShowHamburger(false);
    setSaving(true);
    setExportOverlay('Saving slide…');
    try {
      const payload   = buildPayload(slides.findIndex(s => s.id === selectedId));
      await api.put(`/menuboard/slides/${selectedId}`, payload);
      setSlides(prev => prev.map(s => s.id === selectedId ? { ...s, ...payload } : s));
      const canvas    = await doRenderSlide({ ...selectedSlide, ...payload }, form.orientation);
      const imageData = canvas.toDataURL('image/jpeg', 0.95);
      await api.post(`/menuboard/slides/${selectedId}/save-jpeg`, { image_data: imageData, brand_id: restaurant.id });
      setShowSendTV(true);
    } catch (err) {
      toast.error('Failed to prepare slide: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); setExportOverlay(''); }
  };

  // ── Brand-mode Backup / Restore ───────────────────────────────────────────
  const handleBrandBackup = () => {
    if (!selectedId) return toast.error('No slide selected');
    setShowHamburger(false);
    const slide = slides.find(s => s.id === selectedId);
    if (!slide) return;
    const data = JSON.stringify({
      _type: 'slide-backup', _version: 1,
      backed_up_at: new Date().toISOString(),
      slide_name:       form.slide_name       ?? slide.slide_name,
      orientation:      form.orientation      ?? slide.orientation,
      zone_cols:        form.zone_cols        ?? slide.zone_cols,
      zone_rows:        form.zone_rows        ?? slide.zone_rows,
      zone_mode:        form.zone_mode        ?? slide.zone_mode ?? 'grid',
      bg_color:         form.bg_color         ?? slide.bg_color,
      accent_color:     form.accent_color     ?? slide.accent_color,
      slide_bg_overlay: form.slide_bg_overlay ?? slide.slide_bg_overlay ?? 50,
      slide_bg_image:   '',
      width_px:         form.width_px         ?? slide.width_px,
      height_px:        form.height_px        ?? slide.height_px,
      zones: (form.zones || slide.zones || []).map(z => ({ ...z, bg_image: '' })),
      text_layers:      form.text_layers      ?? parseZones(slide.text_layers),
      // Without these a template restored from this file came back as a plain
      // menu slide with no fields to fill in.
      menu_kind:        slide.menu_kind || (isTemplateCtx ? 'doorsign' : 'products'),
      ...(slide.sign_fields ? { sign_fields: slide.sign_fields } : {}),
    }, null, 2);
    const fname = (form.slide_name || slide.slide_name || `slide-${selectedId}`).replace(/[<>:"/\\|?*]/g, '_');
    const url = URL.createObjectURL(new Blob([data], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = `${fname}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Backup saved!');
  };

  const handleBrandRestoreFile = async (file) => {
    if (!file || !selectedId) return;
    try {
      const text   = await file.text();
      const parsed = JSON.parse(text);
      const d      = Array.isArray(parsed) ? parsed[0] : parsed;
      if (!d?.zones && !d?.zone_cols) { toast.error('Invalid backup file'); return; }
      const res = await api.put(`/brand-slides/${selectedId}`, {
        slide_name:       d.slide_name       || 'Restored Slide',
        orientation:      d.orientation      || 'H',
        zone_cols:        d.zone_cols        || 1,
        zone_rows:        d.zone_rows        || 1,
        zone_mode:        d.zone_mode        || 'grid',
        bg_color:         d.bg_color         || '#111827',
        accent_color:     d.accent_color     || '#F59E0B',
        slide_bg_image:   '',
        slide_bg_overlay: d.slide_bg_overlay ?? 50,
        width_px:         d.width_px,
        height_px:        d.height_px,
        zones:            (d.zones || []).map(z => ({ ...z, bg_image: '' })),
        text_layers:      d.text_layers || [],
      });
      setSlides(prev => prev.map(s => s.id === selectedId ? { ...s, ...res.data } : s));
      // Reload form state from restored slide
      const r = res.data;
      setForm(prev => ({ ...prev,
        slide_name: r.slide_name, orientation: r.orientation,
        zone_cols: r.zone_cols, zone_rows: r.zone_rows,
        bg_color: r.bg_color, accent_color: r.accent_color,
        slide_bg_image: '', slide_bg_overlay: r.slide_bg_overlay,
        width_px: r.width_px, height_px: r.height_px,
        zones: r.zones || [],
        text_layers: parseZones(r.text_layers),
      }));
      toast.success('Slide restored from backup!');
    } catch { toast.error('Restore failed'); }
  };

  // ── Brand-mode Send to TV: save JPEG first if needed, then open new modal ──
  const openBrandSendToTV = async () => {
    if (!selectedId) return toast.error('No slide selected');
    setShowHamburger(false);
    const existingJpeg = slides.find(s => s.id === selectedId)?.jpeg_path;
    if (existingJpeg) {
      // Already saved — open modal directly
      setShowBrandSendTV(true);
      return;
    }
    // Need to save first — show frozen overlay like a normal save
    setSaving(true);
    setExportOverlay('Saving slide…');
    try {
      const idx       = slides.findIndex(s => s.id === selectedId);
      const payload   = buildPayload(idx);
      let currentId   = selectedId;
      if (!currentId) {
        const res = await api.post('/brand-slides', { ...payload, brand_id: parseInt(brandId) });
        currentId = res.data.id;
        setSelectedId(currentId);
        navigate(`/brands/${brandId}/slide-builder/${currentId}`, { replace: true });
      } else {
        await api.put(`/brand-slides/${currentId}`, payload);
      }
      // Phase B (2026-05-16): video-kind slides hit compose-video.
      const isVideoSlide = form.slide_kind === 'video';
      if (isVideoSlide && !form.video_path) {
        toast.error('Upload a video first before sending a video slide to TV.');
        return;
      }
      const slideData = { ...payload, id: currentId, slide_kind: form.slide_kind, video_preview_path: form.video_preview_path, auto_darken: form.auto_darken };
      const canvas    = await doRenderSlide(slideData, form.orientation);
      const imageData = canvas.toDataURL('image/webp', 0.92);
      const endpoint  = isVideoSlide ? 'compose-video' : 'save-jpeg';
      const imgRes    = await api.post(`/brand-slides/${currentId}/${endpoint}`, { image_data: imageData, brand_id: parseInt(brandId) });
      setSlides(prev => prev.map(s => s.id === currentId ? { ...s, ...imgRes.data } : s));
      setShowBrandSendTV(true);
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
      setExportOverlay('');
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;

  // Legacy mode: No screenId → screen selector
  const screen = allScreens.find(s => s.id === parseInt(screenId));
  if (!isBrandMode && !screenId) {
    return <ScreenSelector brandId={brandId} screens={allScreens} restaurant={restaurant} navigate={navigate} />;
  }

  const selectedSlide = slides.find(s => s.id === selectedId);
  const slideIdx      = slides.findIndex(s => s.id === selectedId);
  const orientation   = form.orientation || screen?.orientation || 'H';
  // dim = half-resolution "design units" used by the preview canvas + renderer.
  // When the slide has a custom width_px/height_px, dim follows it (so the
  // preview, zone-handle math and labels all reflect the actual slide size).
  // Falls back to legacy 960×540 / 540×960 if the form values are missing or
  // mid-edit (empty string while user is retyping).
  const _wPx          = parseInt(form.width_px,  10);
  const _hPx          = parseInt(form.height_px, 10);
  const dim           = (_wPx > 0 && _hPx > 0)
    ? { w: _wPx / 2, h: _hPx / 2 }
    : DESIGN[orientation];
  const isMobile      = winW < 768;
  const editorMaxW    = isMobile ? '100%' : 360;

  // Preview pane size.
  // Two independent measurements — take the smaller (more conservative) so we never
  // overflow even if one channel hasn't updated yet:
  //   1. paneSize from ResizeObserver on .sb-preview (works for genuine box-size changes)
  //   2. visiblePaneW computed from winW − sidebarW − editor − gaps (catches sidebar toggles
  //      that DON'T resize .sb-preview's box, only its visible-on-screen area)
  const PANE_PAD_X = 28;     // 14px card padding on each side
  const PANE_PAD_Y = 70;     // card padding + zoom header row
  const PAGE_PAD_X = 16;     // page padding 8 each side
  const LAYOUT_GAP = 12;     // gap between editor and preview
  const visiblePaneW = isMobile
    ? Math.max(200, winW - PAGE_PAD_X - 24)
    : Math.max(200, winW - sidebarW - PAGE_PAD_X - editorMaxW - LAYOUT_GAP - PANE_PAD_X);
  const observedW = paneSize.w > 0 ? Math.max(200, paneSize.w - PANE_PAD_X) : Infinity;
  const measuredW = Math.min(observedW, visiblePaneW);
  const fallbackH = Math.max(320, winH - (isMobile ? 360 : (headerH + 60)));
  const measuredH = paneSize.h > 0 ? Math.max(200, paneSize.h - PANE_PAD_Y) : fallbackH;
  const scaleW = measuredW / dim.w;
  const scaleH = measuredH / dim.h;
  // No 100% cap — let preview scale beyond 1:1 on large monitors to fill the whole preview area.
  const baseScale = Math.max(Math.min(scaleW, scaleH), 0.12);
  const scale  = baseScale * (zoomPct / 100);
  const previewW = Math.round(dim.w * scale);
  const previewH = Math.round(dim.h * scale);

  const activeZone = form.zones[activeZoneIdx] || form.zones[0] || EMPTY_ZONE(0);
  const getDisplayName = (s, i) => computeSlideDisplayName(screen?.screen_label || 'slide', s.slide_name || s.custom_label_en, i);
  const slideFileName = selectedSlide ? computeSlideFileName(screen?.screen_label, form.slide_name, slideIdx) : '';

  const openFullPreview = () => { setShowHamburger(false); setShowFullPreview(true); };

  // Brand mode: full slide actions menu.
  // Order rationale: ➕ Add Slide is the primary entry-point (creating new
  // slides is the most common reason to open this menu), so it sits at the
  // top. Preview is paired with Save (preview-then-save is the natural
  // edit-flow), so we slot Preview right above Save.
  // Download and Send to TV move a PICTURE of the slide to a TV. A template is
  // published as a live page by its Info Screen, so both are dropped there
  // rather than left to quietly do the wrong thing.
  const HAMBURGER_RAW = isBrandMode ? [
    { icon: '➕', label: isTemplateCtx ? 'Add Template' : 'Add Slide', action: handleBrandAddSlide, disabled: false },
    null,
    { icon: '👁',  label: 'Preview',    action: openFullPreview,     disabled: false },
    { icon: '💾', label: 'Save',       action: handleSaveSlide,     disabled: saving },
    isTemplateCtx ? null : { icon: '⬇️', label: 'Download', action: handleBrandDownload, disabled: saving },
    { icon: '⧉',  label: 'Duplicate',  action: handleBrandDuplicate, disabled: saving || !selectedId },
    null,
    isTemplateCtx ? null : { icon: '📺', label: 'Send to TV', action: openBrandSendToTV, disabled: saving || !selectedId },
    null,
    { icon: '🗄️', label: 'Backup',     action: handleBrandBackup,   disabled: saving || !selectedId },
    { icon: '♻️', label: 'Restore',    action: () => { setShowHamburger(false); sbRestoreFileRef.current?.click(); }, disabled: saving || !selectedId },
    null,
    { icon: '🗑',  label: 'Delete',    action: handleBrandDelete,   disabled: false, danger: true },
  ] : [
    { icon: '👁',  label: 'Preview',      action: openFullPreview,                               disabled: !selectedSlide },
    null,
    { icon: '➕', label: 'Add Slide',     action: handleAddSlide,                                disabled: false },
    { icon: '⧉',  label: 'Duplicate',     action: handleDuplicateSlide,                          disabled: !selectedSlide },
    { icon: '🗑',  label: 'Delete Slide',  action: () => selectedId && handleDeleteSlide(selectedId), disabled: !selectedSlide, danger: true },
    null,
    { icon: '💾', label: 'Save',          action: handleSaveSlide,                               disabled: !selectedSlide || saving },
    { icon: '⬇️', label: 'Download',      action: capturePreview,                                disabled: !selectedSlide || saving },
    { icon: '📺', label: 'Send to TV',    action: openSendToTV,                                  disabled: !selectedSlide || saving },
  ];
  // Dropping an item can leave two separators touching, or one dangling at an
  // end. Collapse them so the menu never shows an empty gap.
  const HAMBURGER = HAMBURGER_RAW
    .filter((item, i, arr) => !(item === null && (i === 0 || arr[i - 1] === null)))
    .filter((item, i, arr) => !(item === null && i === arr.length - 1));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page" style={{ padding: isMobile ? 8 : '12px 8px', maxWidth: 'none' }}>
      <style>{`
        @media (max-width:767px){.sb-layout{flex-direction:column!important}.sb-preview{order:2}.sb-editor{order:1}}
        .sb-zone-tab:hover{background:#F0F0F0!important}
        .hbtn:hover{background:#F5F5F5!important}
        .sb-section-banner{display:flex;align-items:center;gap:8px;padding:9px 14px;border-radius:10px;font-size:12px;font-weight:800;letter-spacing:1.2px;text-transform:uppercase;color:#fff;margin:0 -12px 0;box-shadow:0 2px 6px rgba(0,0,0,0.10);border:none;width:calc(100% + 24px);cursor:pointer;font-family:inherit;text-align:left;transition:filter 0.15s,transform 0.1s}
        .sb-section-banner:hover{filter:brightness(1.08)}
        .sb-section-banner:active{transform:translateY(1px)}
        .sb-section-banner.slide-banner{background:linear-gradient(135deg,#1976D2,#42A5F5)}
        .sb-section-banner.text-banner {background:linear-gradient(135deg,#E65100,#FB8C00)}
        .sb-section-banner.zones-banner{background:linear-gradient(135deg,#6A1B9A,#AB47BC)}
        .sb-section-banner.items-banner{background:linear-gradient(135deg,#2E7D32,#66BB6A)}
        .sb-section-banner .sb-section-chevron{margin-left:auto;font-size:10px;transition:transform 0.2s;display:inline-block}
        .sb-section-banner.is-open .sb-section-chevron{transform:rotate(90deg)}
        /* Tinted content cards — each section's body sits in a light wash that matches its banner */
        .sb-section-body{margin:0 -12px 10px;padding:12px;border-radius:0 0 10px 10px;border:1px solid;border-top:none}
        .sb-section-body.slide-body{background:#F0F6FC;border-color:#BBDEFB}
        .sb-section-body.text-body {background:#FFF7ED;border-color:#FFCC80}
        .sb-section-body.zones-body{background:#FAF4FB;border-color:#E1BEE7}
        .sb-section-body.items-body{background:#F1F8F2;border-color:#C8E6C9}
        /* Banner-to-body seam: when open, banner's bottom corners square up so it merges with the body card */
        .sb-section-banner.is-open{border-radius:10px 10px 0 0;margin-bottom:0}
        .sb-editor .card{padding-left:14px!important;padding-right:14px!important}
        .hbtn.danger:hover{background:#FEE2E2!important;color:#DC2626!important}
      `}</style>

      {/* ── Header (frozen at top of viewport on desktop while user scrolls editor) ──
          2026-05-16: collapsed to a single row — back/brand-switcher on the
          far left, then the 🎨 title + slide-name input, then the right-side
          undo/redo/dropdown/hamburger cluster. Saves an entire vertical row
          which on smaller screens was eating preview space. The inner
          `.pm-header-brandnav-inline` rule zeroes out BrandNav's own
          marginBottom so it lines up with the input. */}
      <style>{`
        .pm-header-brandnav-inline > div { margin-bottom: 0 !important; flex-wrap: nowrap; }
      `}</style>
      <div ref={pageHeaderRef} className="page-header" style={{
        display: 'flex', flexDirection: 'row', alignItems: 'center',
        marginBottom: 12, gap: 10, flexWrap: 'wrap',
        position: isMobile ? 'static' : 'sticky',
        top: 0,
        background: '#F5F7FA',
        zIndex: 50,
        paddingTop: isMobile ? 0 : 8,
        paddingBottom: isMobile ? 0 : 8,
        marginTop: isMobile ? 0 : -12,
        boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <div className="pm-header-brandnav-inline" style={{ flexShrink: 0 }}>
          <BrandNav brandId={brandId} subPath={BACK.subPath}
            backLabel={isBrandMode ? BACK.label : `${restaurant?.name || ''} — ${BACK.label}`}
            backPath={BACK.path} backKey={BACK.key} />
        </div>
        {!isBrandMode && <span style={{ fontWeight: 400, color: '#999', fontSize: 13 }}>{screen?.screen_label}</span>}
        {isBrandMode && (
          <>
            <input value={form.slide_name}
              onChange={e => setForm(f => ({ ...f, slide_name: e.target.value }))}
              placeholder="slide-name"
              style={{
                width: 220, flex: '0 0 auto',
                border: `1.5px solid ${!form.slide_name.trim() ? '#EF4444' : '#E0E0E0'}`,
                borderRadius: 8, padding: '4px 10px',
                fontSize: 14, fontWeight: 600, boxSizing: 'border-box',
              }} />
          </>
        )}

        {/* Header right: undo/redo + slide chooser + hamburger.
            `margin-left: auto` pushes this cluster to the far right so the
            left cluster (icon · back · brand · slide-name) stays stuck. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>

          {/* Undo / Redo */}
          <button onClick={doUndo} disabled={!canUndo} title="Undo (Ctrl+Z)"
            style={{ width: 30, height: 30, borderRadius: 7, border: '1.5px solid #E0E0E0', background: '#fff', cursor: canUndo ? 'pointer' : 'not-allowed', opacity: canUndo ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#555' }}>↶</button>
          <button onClick={doRedo} disabled={!canRedo} title="Redo (Ctrl+R or Ctrl+Y)"
            style={{ width: 30, height: 30, borderRadius: 7, border: '1.5px solid #E0E0E0', background: '#fff', cursor: canRedo ? 'pointer' : 'not-allowed', opacity: canRedo ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#555' }}>↷</button>

          {/* Slide chooser dropdown — legacy + brand mode */}
          {(!isBrandMode || allBrandSlides.length > 0) && (
            <div style={{ position: 'relative' }} ref={dropRef}>
              <button onClick={() => setShowSlidesDrop(p => !p)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #E0E0E0', borderRadius: 10, padding: '6px 10px 6px 8px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', maxWidth: Math.min(210, winW - 100) }}>
                {selectedSlide ? <SlideMiniThumb slide={selectedSlide} orientation={orientation} /> : <div style={{ width: 44, height: 28, background: '#F0F0F0', borderRadius: 4 }} />}
                <span style={{ fontSize: 12, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {selectedSlide ? getDisplayName(selectedSlide, slideIdx) : 'No slide'}
                </span>
                <span style={{ background: '#1565C0', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{isBrandMode ? allBrandSlides.length : slides.length}</span>
              </button>
              {showSlidesDrop && (() => {
                const dropList = isBrandMode ? allBrandSlides : slides;
                const curId    = isBrandMode ? (slideId ? parseInt(slideId) : null) : selectedId;
                return (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 1000, background: "#fff", borderRadius: 10, boxShadow: "0 6px 24px rgba(0,0,0,0.14)", border: "1px solid #E8E8E8", minWidth: 230, maxWidth: Math.min(290, winW - 16), maxHeight: 360, overflowY: "auto" }}>
                  {dropList.length === 0 && <div style={{ padding: "16px 12px", textAlign: "center", color: "#aaa", fontSize: 13 }}>No slides yet</div>}
                  {dropList.map((s, idx) => {
                    const name = getDisplayName(s, idx), isAct = s.id === curId;
                    return (
                      <div key={s.id} onClick={() => {
                        if (isBrandMode) navigate("/brands/" + brandId + "/slide-builder/" + s.id);
                        else selectSlide(s);
                        setShowSlidesDrop(false);
                      }}
                        style={{ padding: "8px 10px", cursor: "pointer", background: isAct ? "#E3F2FD" : "transparent", borderBottom: "1px solid #F5F5F5", display: "flex", alignItems: "center", gap: 9 }}
                        onMouseEnter={e => { if (!isAct) e.currentTarget.style.background = "#F8F9FA"; }}
                        onMouseLeave={e => { if (!isAct) e.currentTarget.style.background = "transparent"; }}>
                        <SlideMiniThumb slide={s} orientation={isBrandMode ? (s.orientation || "H") : (s.slide_orientation || screen?.orientation || "H")} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: isAct ? 700 : 500, color: isAct ? "#1565C0" : "#222", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                          <div style={{ fontSize: 10, color: "#999", marginTop: 1 }}>{s.zone_cols || 1}x{s.zone_rows || 1} · {parseZones(s.zones).reduce((sum, z) => sum + (z.item_ids || []).length, 0)} items</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                );
              })()}
            </div>
          )}

          {/* ☰ Hamburger — always shown */}
          <div style={{ position: 'relative' }} ref={hamburgerRef}>
            <button onClick={() => setShowHamburger(p => !p)} title="Actions"
              style={{ width: 36, height: 36, borderRadius: 9, border: '1.5px solid #E0E0E0', background: showHamburger ? '#E3F2FD' : '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ display: 'block', width: 16, height: 2, background: '#444', borderRadius: 1 }} />
              <span style={{ display: 'block', width: 16, height: 2, background: '#444', borderRadius: 1 }} />
              <span style={{ display: 'block', width: 16, height: 2, background: '#444', borderRadius: 1 }} />
            </button>
            {showHamburger && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000, background: '#fff', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.16)', border: '1px solid #E8E8E8', minWidth: 190, padding: '6px 0' }}>
                {HAMBURGER.map((item, i) => item === null
                  ? <div key={i} style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
                  : (
                    <button key={i} onClick={item.disabled ? undefined : item.action} disabled={item.disabled}
                      className={`hbtn${item.danger ? ' danger' : ''}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', border: 'none', background: 'transparent', cursor: item.disabled ? 'not-allowed' : 'pointer', fontSize: 13, color: item.danger ? '#EF4444' : (item.disabled ? '#ccc' : '#333'), textAlign: 'left', opacity: item.disabled ? 0.5 : 1, transition: 'background 0.1s' }}>
                      <span style={{ fontSize: 15, width: 20 }}>{item.icon}</span>{item.label}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main layout — editor flush to left nav, preview gets the rest ── */}
      <div className="sb-layout" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

        {/* ── Editor ── */}
        <div className="sb-editor" style={{ width: editorMaxW, flexShrink: 0, minWidth: 0 }}>
          {!selectedSlide && !isBrandMode ? (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
              <p style={{ marginBottom: 16 }}>Add a slide to get started</p>
              <button className="btn btn-primary" onClick={handleAddSlide}>+ Add Slide</button>
            </div>
          ) : (
            <div className="card" style={{ padding: isMobile ? 12 : 14 }}>

              {/* ════════ SLIDE SECTION ════════ */}
              <button type="button" onClick={() => toggleSection('slide')}
                className={`sb-section-banner slide-banner${openSections.slide ? ' is-open' : ''}`}>
                <span style={{ fontSize: 14 }}>📄</span> Slide
                <HelpTip text="Slide-level settings: orientation, name, color theme, layout mode, and full-slide background image." />
                <span className="sb-section-chevron">▶</span>
              </button>
              {openSections.slide && (<div className="sb-section-body slide-body">

              {/* ── Row 0 (Phase A, 2026-05-16): Kind toggle — Image | Video ──
                  Default 'image' for back-compat. Toggling to 'video' shows
                  the video-specific controls (uploader, duration, loop, audio,
                  auto-darken) between Row 1 and Row 2 below. The Kind value
                  persists in form.slide_kind and is sent on Save. Uploading
                  a video also auto-flips slide_kind to 'video' server-side. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 0.3 }}>KIND</span>
                <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
                  {[{ val: 'image', label: '🖼 Image' }, { val: 'video', label: '🎬 Video' }].map(({ val, label }) => {
                    const act = (form.slide_kind || 'image') === val;
                    return (
                      <button key={val} onClick={() => setForm(f => {
                          if (f.slide_kind === val) return f;
                          // Toggling to Video snaps the slide to 720p / landscape —
                          // the standard mux target for the editor's <video>
                          // preview and the eventual TV playback. Designer can
                          // still override the W×H inputs after.
                          if (val === 'video') {
                            return { ...f, slide_kind: 'video', orientation: 'H', width_px: 1280, height_px: 720 };
                          }
                          return { ...f, slide_kind: val };
                        })}
                        style={{ padding: '5px 12px', border: 'none', background: act ? '#6D28D9' : '#fff', color: act ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: act ? 700 : 400 }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Row 1: Orientation · Resolution (W × H px) · Lang ──
                  Slide name lives in the page-header now (next to the title)
                  so the most-edited field is always visible regardless of
                  which section is folded. Resolution defaults to 1280×720
                  (H) or 720×1280 (V); flips automatically on orientation
                  toggle. Both fields are free-form numeric inputs. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                  {[{ val: 'H', label: '⟺ H' }, { val: 'V', label: '⇕ V' }].map(({ val, label }) => {
                    const act = orientation === val;
                    return (
                      <button key={val} onClick={() => setForm(f => {
                        // Flip W/H along with orientation so user gets the
                        // expected default for the new direction without
                        // having to retype. Preserves any custom resolution
                        // by simply swapping the values.
                        if (val === f.orientation) return f;
                        return { ...f, orientation: val, width_px: f.height_px, height_px: f.width_px };
                      })}
                        style={{ padding: '5px 10px', border: 'none', background: act ? '#1565C0' : '#fff', color: act ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: act ? 700 : 400 }}>
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Resolution input: W [____] × H [____] px */}
                <div title="Output resolution in pixels. Preview scales proportionally." style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  border: '1.5px solid #E0E0E0', borderRadius: 8,
                  padding: '0 8px', background: '#fff',
                  flex: 1, minWidth: 0,
                }}>
                  <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>RES</span>
                  <input type="number" min="100" max="7680" step="1"
                    value={form.width_px}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v > 0) setForm(f => ({ ...f, width_px: v }));
                      else if (e.target.value === '') setForm(f => ({ ...f, width_px: '' }));
                    }}
                    onBlur={e => {
                      if (!e.target.value || parseInt(e.target.value, 10) <= 0) {
                        setForm(f => ({ ...f, width_px: f.orientation === 'V' ? 720 : 1280 }));
                      }
                    }}
                    style={{ width: 64, border: 'none', outline: 'none', fontSize: 13, fontWeight: 600, color: '#222', textAlign: 'center', padding: '5px 0', background: 'transparent' }} />
                  <span style={{ color: '#888', fontWeight: 600 }}>×</span>
                  <input type="number" min="100" max="7680" step="1"
                    value={form.height_px}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v > 0) setForm(f => ({ ...f, height_px: v }));
                      else if (e.target.value === '') setForm(f => ({ ...f, height_px: '' }));
                    }}
                    onBlur={e => {
                      if (!e.target.value || parseInt(e.target.value, 10) <= 0) {
                        setForm(f => ({ ...f, height_px: f.orientation === 'V' ? 1280 : 720 }));
                      }
                    }}
                    style={{ width: 64, border: 'none', outline: 'none', fontSize: 13, fontWeight: 600, color: '#222', textAlign: 'center', padding: '5px 0', background: 'transparent' }} />
                  <span style={{ fontSize: 10, color: '#aaa', fontWeight: 600 }}>px</span>
                </div>

                <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                  {['ar', 'en'].map(l => (
                    <button key={l} onClick={() => setLang(l)}
                      style={{ padding: '5px 9px', border: 'none', background: lang === l ? '#1565C0' : '#fff', color: lang === l ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Row 1.5 (Phase A, 2026-05-16): Video-specific controls ──
                  Visible only when kind === 'video'. The Background uploader
                  hits POST /brand-slides/:id/upload-video which ffmpeg-encodes
                  the slide-sized bg.mp4 + a 640-edge preview file the editor
                  uses for fast DOM editing. Duration / loop / audio / darken
                  are persisted via the regular Save (PUT). */}
              {form.slide_kind === 'video' && (
                <div style={{ padding: 8, background: '#F5F0FB', border: '1px solid #DCC9F2', borderRadius: 8, marginBottom: 12 }}>
                  {/* 2026-05-17 — small grey label per control group, mirroring
                      the BG IMG / BG COLOR / BORDER labels in the Zone row.
                      Row 1: BG VIDEO (thumb · replace · delete) · DURATION ·
                      LOOP MODE. Row 2: AUDIO · DIM BG. */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>BG Video</span>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {form.video_preview_path
                          ? <video src={form.video_preview_path} muted autoPlay loop playsInline
                              onLoadedMetadata={e => {
                                const d = e.currentTarget.duration;
                                if (Number.isFinite(d) && d > 0) setBgVideoDuration(d);
                              }}
                              style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4, border: '1.5px solid #E0E0E0', background: '#000', flexShrink: 0 }} />
                          : <div style={{ width: 56, height: 32, borderRadius: 4, border: '1.5px dashed #DCC9F2', background: '#FAF7FE',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#A78BFA', flexShrink: 0 }}>🎬</div>}
                        <input ref={videoFileRef} type="file" accept="video/*" onChange={handleVideoUpload} style={{ display: 'none' }} />
                        <button onClick={() => {
                            if (!selectedId) { toast.error('Save the slide first, then upload the video'); return; }
                            videoFileRef.current?.click();
                          }}
                          disabled={videoUploading}
                          title={videoUploading ? 'Encoding…' : (form.video_path ? 'Replace video' : 'Upload video')}
                          style={{ width: 28, height: 28, padding: 0, border: '1.5px solid #6D28D9', borderRadius: 5,
                                   background: videoUploading ? '#EEE' : '#fff', color: '#6D28D9',
                                   cursor: videoUploading ? 'wait' : 'pointer', fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {videoUploading ? '…' : (form.video_path ? '🔄' : '⬆')}
                        </button>
                        {form.video_path && !videoUploading && (
                          <button onClick={handleVideoClear} title="Remove video"
                            style={{ width: 28, height: 28, padding: 0, border: '1.5px solid #FCA5A5', borderRadius: 5,
                                     background: '#FEF2F2', color: '#DC2626',
                                     cursor: 'pointer', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            🗑
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Duration</span>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <input type="number" min="5" max="300" step="1"
                          value={form.duration_sec}
                          onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v > 0) setForm(f => ({ ...f, duration_sec: v }));
                            else if (e.target.value === '') setForm(f => ({ ...f, duration_sec: '' }));
                          }}
                          onBlur={e => {
                            const v = parseInt(e.target.value, 10);
                            if (!v || v < 5)   setForm(f => ({ ...f, duration_sec: 15  }));
                            else if (v > 300)  setForm(f => ({ ...f, duration_sec: 300 }));
                          }}
                          style={{ width: 42, padding: '3px 4px', border: '1.5px solid #D0D0D0', borderRadius: 4, fontSize: 11, fontWeight: 600, textAlign: 'center', height: 28, boxSizing: 'border-box' }} />
                        <span style={{ fontSize: 9, color: '#999' }}>s</span>
                      </label>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Loop Mode{durationsMatch ? ' · auto' : ''}
                      </span>
                      <div title={durationsMatch
                          ? 'Slide duration matches the bg video — auto-set to Reset (play once, no loop/freeze).'
                          : "Loop = repeat the source until duration ends. Freeze = play once, then hold the last frame. Reset = play once at the bg's natural duration, no loop / no freeze."}
                           style={{ display: 'flex', border: '1.5px solid ' + (durationsMatch ? '#A78BFA' : '#E0E0E0'), borderRadius: 5, overflow: 'hidden', flexShrink: 0, opacity: durationsMatch ? 0.95 : 1 }}>
                        {[{ val: 'loop', label: '🔁' }, { val: 'freeze', label: '⏸' }, { val: 'reset', label: '▶' }].map(({ val, label }) => {
                          const effectiveMode = durationsMatch ? 'reset' : (form.loop_mode || 'loop');
                          const act = effectiveMode === val;
                          const ttl = val === 'loop' ? 'Loop' : val === 'freeze' ? 'Freeze' : 'Reset (play once at bg duration)';
                          return (
                            <button key={val}
                              onClick={() => setForm(f => ({ ...f, loop_mode: val }))}
                              disabled={durationsMatch && val !== 'reset'}
                              title={durationsMatch && val !== 'reset' ? 'Auto-set to Reset because bg duration matches slide duration' : ttl}
                              style={{ width: 28, height: 28, padding: 0, border: 'none',
                                       background: act ? '#6D28D9' : '#fff',
                                       color: act ? '#fff' : (durationsMatch && val !== 'reset' ? '#CBD5E1' : '#555'),
                                       cursor: (durationsMatch && val !== 'reset') ? 'not-allowed' : 'pointer',
                                       fontSize: 12, fontWeight: act ? 700 : 400 }}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {!selectedId && (
                    <div style={{ fontSize: 10, color: '#B45309', marginBottom: 4 }}>Save the slide first to upload a video</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#555', fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!form.has_audio}
                        onChange={e => setForm(f => ({ ...f, has_audio: e.target.checked }))} />
                      🔊 Audio
                    </label>
                    <span title="Black overlay above the video, below text/zones — improves contrast on busy footage."
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#555', fontWeight: 600 }}>
                      DIM bg
                      <PctInput value={form.auto_darken || 0}
                        onChange={v => setForm(f => ({ ...f, auto_darken: v }))} max={80} />
                    </span>
                  </div>
                </div>
              )}

              {/* ── Row 2: Preset · BG · Accent · Zones — all same height ── */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #F0F0F0' }}>
                {/* Shared label style */}
                {[
                  {
                    key: 'preset', label: 'Preset',
                    control: (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button onClick={() => setShowPresets(p => !p)}
                          style={{ width: 32, height: 32, borderRadius: 7, border: showPresets ? '2.5px solid #1565C0' : '1.5px solid #D0D0D0', cursor: 'pointer', padding: 0, display: 'block', background: `linear-gradient(135deg, ${form.bg_color} 50%, ${form.accent_color} 50%)` }} />
                        {showPresets && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowPresets(false)} />
                            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1000, background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: 14, width: Math.min(280, winW - 24), border: '1px solid #E0E0E0' }}>
                              {['Light', 'Dark'].map(mode => (
                                <div key={mode}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 8 }}>{mode}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                                    {COLOR_PRESETS.filter(p => mode === 'Light' ? isLightBg(p.bg) : !isLightBg(p.bg)).map((p, i) => (
                                      <button key={i} onClick={() => { setForm(f => ({ ...f, bg_color: p.bg, accent_color: p.ac })); setShowPresets(false); }}
                                        style={{ width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', padding: 0, background: `linear-gradient(135deg,${p.bg} 50%,${p.ac} 50%)`, border: (form.bg_color === p.bg && form.accent_color === p.ac) ? '3px solid #1565C0' : '2px solid #ccc' }} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'bg', label: 'BG',
                    control: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 7, border: '1.5px solid #E0E0E0', overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0,
                          ...(!form.bg_color ? {
                            backgroundColor: '#fff',
                            backgroundImage: 'linear-gradient(45deg,#ccc 25%,transparent 25%),linear-gradient(-45deg,#ccc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ccc 75%),linear-gradient(-45deg,transparent 75%,#ccc 75%)',
                            backgroundSize: '8px 8px',
                            backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
                          } : { background: form.bg_color }) }}>
                          <input type="color" value={form.bg_color || '#ffffff'} onChange={e => setForm(f => ({ ...f, bg_color: e.target.value }))}
                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', padding: 0, border: 'none' }} />
                        </div>
                        <button onClick={() => setForm(f => ({ ...f, bg_color: f.bg_color ? '' : '#ffffff' }))}
                          title={form.bg_color ? 'Clear (make slide BG transparent)' : 'Set a color'}
                          style={{ width: 18, height: 32, border: '1.5px solid #E0E0E0', borderRadius: 5, padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: form.bg_color ? '#888' : '#1565C0', background: form.bg_color ? '#fff' : '#E3F2FD', lineHeight: 1 }}>
                          {form.bg_color ? '✕' : '●'}
                        </button>
                      </div>
                    ),
                  },
                  {
                    key: 'accent', label: 'Accent',
                    control: (
                      <div style={{ width: 32, height: 32, borderRadius: 7, border: '1.5px solid #E0E0E0', overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
                        <div style={{ position: 'absolute', inset: 0, background: form.accent_color }} />
                        <input type="color" value={form.accent_color} onChange={e => setForm(f => ({ ...f, accent_color: e.target.value }))}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', padding: 0, border: 'none' }} />
                      </div>
                    ),
                  },
                  {
                    key: 'mode', label: 'Mode',
                    control: (
                      <div style={{ height: 32, display: 'flex', alignItems: 'center', border: '1.5px solid #E0E0E0', borderRadius: 7, overflow: 'hidden', flexShrink: 0 }}>
                        {[
                          { val: 'grid', label: '🔲 Grid', title: 'Grid layout — zones arranged in a cols×rows grid' },
                          { val: 'free', label: '✏️ Free', title: 'Free layout — zones can be placed and resized anywhere' },
                        ].map(({ val, label: ml, title }) => {
                          const act = (form.zone_mode || 'grid') === val;
                          return (
                            <button key={val} onClick={() => switchZoneMode(val)} title={title}
                              style={{ padding: '4px 10px', border: 'none', background: act ? '#1565C0' : '#fff', color: act ? '#fff' : '#555', cursor: 'pointer', fontSize: 11, fontWeight: act ? 700 : 500 }}>
                              {ml}
                            </button>
                          );
                        })}
                      </div>
                    ),
                  },
                  // Zones picker is only shown in Grid mode — in Free mode the + Zone button
                  // lives next to the Slide Background DIM slider below.
                  ...(form.zone_mode === 'free' ? [] : [{
                    key: 'zones', label: 'Zones',
                    control: (
                      <div style={{ height: 32, display: 'flex', alignItems: 'center' }}>
                        <ZoneLayoutPicker cols={form.zone_cols} rows={form.zone_rows} onChange={handleZoneLayoutChange} />
                      </div>
                    ),
                  }]),
                ].map(({ key, label, control }) => {
                  const tipMap = {
                    preset: 'Quick color palette — picks both BG and Accent colors at once. Pre-styled for light or dark themes.',
                    bg: 'Slide background color (used when no BG image is uploaded).',
                    accent: 'Highlight color used for prices, headers, and accents on items.',
                    mode: 'Grid: zones in equal cells (cols × rows). Free: drag/resize zones anywhere on the canvas. Switching converts coordinates automatically.',
                    zones: form.zone_mode === 'free'
                      ? 'Add a new zone at canvas center. Drag zones in the preview to reposition.'
                      : 'Zone grid — cols × rows. Click the icon for presets or type the numbers.',
                  };
                  return (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {label}
                        <HelpTip text={tipMap[key] || ''} width={220} />
                      </span>
                      {control}
                    </div>
                  );
                })}
              </div>
              {/* Slide background image — hidden for video slides where bg.mp4
                  IS the background (2026-05-16). Phase D will reuse the image
                  bg as a fallback when no video is uploaded yet. */}
              {form.slide_kind !== 'video' && (
              <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #F0F0F0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center' }}>
                  🖼 Slide Background
                  <HelpTip text="A photo behind the entire slide (under all zones). DIM darkens it so foreground text stays readable. Leave empty to use the solid BG color." />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {form.slide_bg_image && (
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <img src={form.slide_bg_image} alt="slide-bg" style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 5, border: '1.5px solid #E0E0E0' }} />
                      <button onClick={() => setForm(f => ({ ...f, slide_bg_image: '' }))} title="Remove"
                        style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, borderRadius: '50%', background: '#EF4444', border: 'none', color: '#fff', fontSize: 9, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
                    </div>
                  )}
                  <button onClick={() => slideBgFileRef.current?.click()} disabled={slideBgUploading}
                    style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #1565C0', background: '#E3F2FD', color: '#1565C0', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                    {slideBgUploading ? 'Uploading…' : form.slide_bg_image ? '⟳ Change' : '+ Upload'}
                  </button>
                  <input ref={slideBgFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSlideBgUpload} />
                  {form.slide_bg_image && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>DIM</span>
                      <PctInput value={form.slide_bg_overlay ?? 50}
                        onChange={v => setForm(f => ({ ...f, slide_bg_overlay: v }))}
                        max={95} />
                    </span>
                  )}
                </div>
              </div>
              )}

              </div>)}

              {/* ════════ TEXT LAYERS SECTION (slide-level) ════════ */}
              {/* 2026-05-16 — the +Text button now lives on the banner header
                  (parallel to the Zones +Zone placement), inside the section
                  body just shows the cards. */}
              <div style={{ position: 'relative', marginTop: 6 }}>
                <button type="button" onClick={() => toggleSection('text')}
                  className={`sb-section-banner text-banner${openSections.text ? ' is-open' : ''}`}
                  style={{ width: '100%' }}>
                  <span style={{ fontSize: 14 }}>📝</span> Text Layers{(form.text_layers || []).length > 0 ? ` (${(form.text_layers || []).length})` : ''}
                  <HelpTip text="Free text drawn on top of the slide (titles, captions). Independent of zones — drag in preview to position." />
                  <span className="sb-section-chevron">▶</span>
                </button>
                <button onClick={(e) => {
                    e.stopPropagation();
                    const newIdx = addSlideTextLayer();
                    setExpandedSlideTextIdx(newIdx);
                    setSelectedSlideTextIdx(newIdx);
                    if (!openSections.text) toggleSection('text');
                  }}
                  title={`Add a new text layer (${(form.text_layers || []).length} so far)`}
                  style={{ position: 'absolute', right: 38, top: '50%', transform: 'translateY(-50%)',
                           padding: '4px 10px', borderRadius: 6, border: '1.5px solid #fff',
                           background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 11,
                           fontWeight: 700, cursor: 'pointer' }}>
                  + Text <span style={{ fontWeight: 400, opacity: 0.85 }}>({(form.text_layers || []).length})</span>
                </button>
              </div>
              {openSections.text && (<div className="sb-section-body text-body">
                {(form.text_layers || []).length === 0 && (
                  <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic', padding: '4px 0' }}>
                    No slide text yet.
                  </div>
                )}
                {(form.text_layers || []).map((layer, li) => renderTextLayerCard({
                  layer, li,
                  isOpen: expandedSlideTextIdx === li,
                  onToggleOpen: () => {
                    const open = expandedSlideTextIdx === li;
                    setExpandedSlideTextIdx(open ? null : li);
                    setSelectedSlideTextIdx(open ? null : li);
                  },
                  onPatch: (patch) => updateSlideTextLayer(li, patch),
                  onDelete: () => deleteSlideTextLayer(li),
                  onDuplicate: () => duplicateSlideTextLayer(li),
                }))}
              </div>)}

              {/* ════════ ZONES SECTION ════════ */}
              {/* 2026-05-16 — the +Add Zone button (was inside Slide section)
                  now sits inline with the Zones section header; clicking it
                  also unfolds Zones so the new zone is immediately editable. */}
              <div style={{ position: 'relative', marginTop: 6 }}>
                <button type="button" onClick={() => toggleSection('zones')}
                  className={`sb-section-banner zones-banner${openSections.zones ? ' is-open' : ''}`}
                  style={{ width: '100%' }}>
                  <span style={{ fontSize: 14 }}>🔲</span> Zones
                  <HelpTip text="Per-zone editing: select a tab, then position/resize it, add text layers, set its background color/image and border." />
                  <span className="sb-section-chevron">▶</span>
                </button>
                {form.zone_mode === 'free' && (
                  <button onClick={(e) => {
                      e.stopPropagation();
                      addFreeZone();
                      if (!openSections.zones) toggleSection('zones');
                    }}
                    title={`Add a new zone (${form.zones.length} so far)`}
                    style={{ position: 'absolute', right: 38, top: '50%', transform: 'translateY(-50%)',
                             padding: '4px 10px', borderRadius: 6, border: '1.5px solid #fff',
                             background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 11,
                             fontWeight: 700, cursor: 'pointer' }}>
                    + Zone <span style={{ fontWeight: 400, opacity: 0.85 }}>({form.zones.length})</span>
                  </button>
                )}
              </div>
              {openSections.zones && (<div className="sb-section-body zones-body">

              {/* Zone tabs — drag to reorder (left = back layer · right = front layer), double-click to rename */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Zones
                  <HelpTip text="One tab per zone. Click to select, double-click to rename, drag left/right to reorder. Leftmost tab = bottom layer; rightmost = top." />
                </div>
                <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 4 }}>
                  {form.zones.map((zone, idx) => {
                    const name = lang === 'ar' ? (zone.name_ar || zone.name) : (zone.name || zone.name_ar);
                    const isAct = idx === activeZoneIdx;
                    const isEdit = tabEditIdx === idx;
                    const isDropTarget = tabDropIdx === idx && tabDragIdx !== null && tabDragIdx !== idx;
                    if (isEdit) {
                      return (
                        <input key={zone.id || idx} autoFocus defaultValue={zone.name || ''}
                          onBlur={e => { updateZone('name', e.target.value); setTabEditIdx(null); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { updateZone('name', e.target.value); setTabEditIdx(null); }
                            if (e.key === 'Escape') setTabEditIdx(null);
                          }}
                          placeholder={`Zone ${idx + 1}`}
                          style={{ padding: '4px 10px', borderRadius: 8, border: '2px solid #1565C0', background: '#fff', fontSize: 12, fontWeight: 700, color: '#1565C0', minWidth: 100, outline: 'none' }} />
                      );
                    }
                    return (
                      <button key={zone.id || idx}
                        draggable
                        onClick={() => setActiveZoneIdx(idx)}
                        onDoubleClick={() => { setActiveZoneIdx(idx); setTabEditIdx(idx); }}
                        onDragStart={e => { setTabDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); }}
                        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (tabDropIdx !== idx) setTabDropIdx(idx); }}
                        onDragLeave={() => { if (tabDropIdx === idx) setTabDropIdx(null); }}
                        onDrop={e => { e.preventDefault(); reorderZones(tabDragIdx, idx); setTabDragIdx(null); setTabDropIdx(null); }}
                        onDragEnd={() => { setTabDragIdx(null); setTabDropIdx(null); }}
                        className="sb-zone-tab"
                        title="Click to select · double-click to rename · drag to reorder"
                        style={{
                          padding: '5px 12px', borderRadius: 8, whiteSpace: 'nowrap',
                          border: isAct ? '2px solid #1565C0' : '1.5px solid #E0E0E0',
                          background: isAct ? '#E3F2FD' : (isDropTarget ? '#FFF8E1' : '#fff'),
                          color: isAct ? '#1565C0' : '#555',
                          fontSize: 12, fontWeight: isAct ? 700 : 400,
                          cursor: 'grab', transition: 'all 0.1s', flexShrink: 0,
                          opacity: tabDragIdx === idx ? 0.4 : 1,
                          boxShadow: isDropTarget ? 'inset 3px 0 0 #FF9800' : 'none',
                        }}>
                        {zone.locked && '🔒 '}{name || `Zone ${idx + 1}`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Active zone editor */}
              <div style={{ background: '#F8F9FA', borderRadius: 10, padding: '12px 12px 10px', marginBottom: 12 }}>

                {/* Zone name is edited inline in the zone tab (double-click) — no separate input here. */}

                {/* Free-mode position & tools */}
                {form.zone_mode === 'free' && (
                  <div style={{ background: '#fff', border: '1.5px solid #E0E0E0', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        📐 Position &amp; Size (px)
                        <HelpTip text="Drag the zone in preview to move · corner/edge handles to resize · or type pixel coords here. Snaps to canvas edges + other zones' edges/centers. Hold Shift while resizing to keep proportions; hold Shift while moving to lock to one axis." />
                      </span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#666', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!activeZone.locked} onChange={e => updateZone('locked', e.target.checked)} style={{ accentColor: '#1565C0' }} />
                        🔒 Lock
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                      {[
                        { key: 'x', label: 'X', allowNeg: true },
                        { key: 'y', label: 'Y', allowNeg: true },
                        { key: 'w', label: 'W', allowNeg: false },
                        { key: 'h', label: 'H', allowNeg: false },
                      ].map(({ key, label, allowNeg }) => (
                        <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: 9, color: '#999', fontWeight: 700 }}>{label}</span>
                          {/* Inputs are shown in real slide-pixels (×2 of the half-res
                              "design unit" used internally). When the user types,
                              we divide by 2 to keep storage in design units. */}
                          <input type="number" step="1" min={allowNeg ? undefined : 1}
                            value={Math.round((activeZone[key] || 0) * 2)}
                            disabled={!!activeZone.locked}
                            onChange={e => {
                              const n = parseInt(e.target.value);
                              updateZone(key, isNaN(n) ? 0 : n / 2);
                            }}
                            style={{ width: 60, border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '4px 6px', fontSize: 12, background: activeZone.locked ? '#f5f5f5' : '#fff' }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={() => setZoneZOrder(activeZoneIdx, 'forward')} title="Bring forward"
                        style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer' }}>↑</button>
                      <button onClick={() => setZoneZOrder(activeZoneIdx, 'back')} title="Send back"
                        style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer' }}>↓</button>
                      <button onClick={() => setZoneZOrder(activeZoneIdx, 'front')} title="Bring to front"
                        style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer' }}>⊤</button>
                      <button onClick={() => setZoneZOrder(activeZoneIdx, 'bottom')} title="Send to back"
                        style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer' }}>⊥</button>
                      <button onClick={() => duplicateZoneAt(activeZoneIdx)} title="Duplicate zone"
                        style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer' }}>⧉ Dup</button>
                      <button onClick={() => deleteSelectedZones()} title={`Delete ${selectedZoneIds.size > 1 ? `${selectedZoneIds.size} selected zones` : 'zone'} (Del key)`}
                        style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #FCA5A5', background: '#FEF2F2', color: '#DC2626', cursor: 'pointer' }}>✕ Delete{selectedZoneIds.size > 1 ? ` (${selectedZoneIds.size})` : ''}</button>
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#999', alignSelf: 'center' }}>z: {activeZone.z_index ?? 0}</span>
                    </div>
                  </div>
                )}

              </div>{/* close active zone editor (gray box) */}

              {/* 2026-05-16 — BG image · BG color · Border all on one compact row.
                  2026-05-17 — small grey label above each cell ("BG IMG",
                  "BG COLOR", "BORDER") matching the section-label style used
                  elsewhere in the editor. */}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #EBEBEB' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {/* BG Image */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>BG Img</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontSize: 11 }}>🖼</span>
                    {activeZone.bg_image && (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <img src={activeZone.bg_image} alt="" style={{ width: 30, height: 22, objectFit: 'cover', borderRadius: 4, border: '1.5px solid #E0E0E0' }} />
                        <button onClick={() => updateZone('bg_image', '')} title="Remove"
                          style={{ position: 'absolute', top: -5, right: -5, width: 13, height: 13, borderRadius: '50%', background: '#EF4444', border: 'none', color: '#fff', fontSize: 8, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
                      </div>
                    )}
                    <button onClick={() => bgFileRef.current?.click()} disabled={bgUploading}
                      title={activeZone.bg_image ? 'Replace image' : 'Upload image'}
                      style={{ padding: '3px 7px', borderRadius: 5, border: '1.5px solid #1565C0', background: '#E3F2FD', color: '#1565C0', fontSize: 11, fontWeight: 600, cursor: 'pointer', height: 24, lineHeight: 1 }}>
                      {bgUploading ? '…' : activeZone.bg_image ? '⟳' : '+'}
                    </button>
                    <input ref={bgFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleZoneBgUpload} />
                    {activeZone.bg_image && (
                      <PctInput value={activeZone.bg_overlay ?? 50}
                        onChange={v => updateZone('bg_overlay', v)} max={95} label="% dim" />
                    )}
                    </span>
                  </div>
                  {/* BG Color */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>BG Color</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontSize: 11 }}>🎨</span>
                    <ColorPicker value={activeZone.bg_color}
                      onChange={c => updateZone('bg_color', c)}
                      width={26} height={22} title="Zone background fill color" />
                    {activeZone.bg_color && (
                      <button onClick={() => updateZone('bg_color', '')} title="Clear color"
                        style={{ width: 18, height: 22, padding: 0, borderRadius: 4, border: '1.5px solid #E0E0E0', background: '#fff', color: '#555', fontSize: 10, cursor: 'pointer' }}>✕</button>
                    )}
                    {activeZone.bg_color && (
                      <PctInput value={activeZone.bg_color_opacity ?? 100}
                        onChange={v => updateZone('bg_color_opacity', v)} />
                    )}
                    </span>
                  </div>
                  {/* Border */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Border</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontSize: 11 }}>▢</span>
                    <ColorPicker value={activeZone.border_color}
                      onChange={c => updateZone('border_color', c)}
                      width={26} height={22} title="Zone border color" />
                    {activeZone.border_color && (
                      <button onClick={() => updateZone('border_color', '')} title="Clear color"
                        style={{ width: 18, height: 22, padding: 0, borderRadius: 4, border: '1.5px solid #E0E0E0', background: '#fff', color: '#555', fontSize: 10, cursor: 'pointer' }}>✕</button>
                    )}
                    <span style={{ fontSize: 9, color: '#999', fontWeight: 700 }}>W</span>
                    <input type="number" min="0" step="1" placeholder="0"
                      value={activeZone.border_width ?? ''}
                      onChange={e => updateZone('border_width', e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                      title="Border width (px)"
                      style={{ width: 36, height: 22, border: '1.5px solid #E0E0E0', borderRadius: 4, padding: '0 4px', fontSize: 11, textAlign: 'center', boxSizing: 'border-box' }} />
                    <span style={{ fontSize: 9, color: '#999', fontWeight: 700 }}>R</span>
                    <input type="number" min="0" step="1" placeholder="0"
                      value={activeZone.border_radius ?? ''}
                      onChange={e => updateZone('border_radius', e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                      title="Corner radius (px)"
                      style={{ width: 36, height: 22, border: '1.5px solid #E0E0E0', borderRadius: 4, padding: '0 4px', fontSize: 11, textAlign: 'center', boxSizing: 'border-box' }} />
                    </span>
                  </div>
                </div>
              </div>

              {/* Phase 3 legacy zone-nested text layers — UI removed 2026-05-16.
                  Slide-level Text Layers section is the canonical home now.
                  Renderer still draws old zone-nested data for back-compat on
                  any un-migrated slides; designers can no longer add new ones
                  from this surface (use the slide-level Text Layers section). */}

              {/* Phase C — per-zone animation (free mode only; grid zones share
                  their fate with the implicit grid and aren't individually
                  animated). Falls quietly through when no zone is active. */}
              {form.zone_mode === 'free' && activeZone && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #EBEBEB', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5, justifyContent: 'space-between' }}>
                    <span>🎞 Animation · z{activeZoneIdx + 1}</span>
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      <button onClick={() => updateZone('hidden', !activeZone.hidden)}
                        title={activeZone.hidden ? 'Zone hidden — click to show' : 'Hide zone'}
                        style={{ padding: '2px 5px', fontSize: 11, borderRadius: 5, border: '1.5px solid #E0E0E0', background: activeZone.hidden ? '#FEF3C7' : '#fff', color: activeZone.hidden ? '#92400E' : '#666', cursor: 'pointer' }}>
                        {activeZone.hidden ? '🙈' : '👁'}
                      </button>
                      <button onClick={() => updateZone('locked', !activeZone.locked)}
                        title={activeZone.locked ? 'Zone locked — click to unlock' : 'Lock position/size'}
                        style={{ padding: '2px 5px', fontSize: 11, borderRadius: 5, border: '1.5px solid #E0E0E0', background: activeZone.locked ? '#E0E7FF' : '#fff', color: activeZone.locked ? '#3730A3' : '#666', cursor: 'pointer' }}>
                        {activeZone.locked ? '🔒' : '🔓'}
                      </button>
                    </span>
                  </div>
                  <AnimSection layer={activeZone}
                    onPatch={(patch) => Object.entries(patch).forEach(([k, v]) => updateZone(k, v))}
                    onApplyToAll={null}
                    onReset={() => Object.entries(animDefaults()).forEach(([k, v]) => updateZone(k, v))}
                    slideDuration={parseInt(form.duration_sec, 10) || 15} />
                </div>
              )}

              </div>)}

              {/* ════════ ITEMS SECTION ════════ */}
              <button type="button" onClick={() => toggleSection('items')}
                className={`sb-section-banner items-banner${openSections.items ? ' is-open' : ''}`}
                style={{ marginTop: 14 }}>
                <span style={{ fontSize: 14 }}>🍴</span> Items
                <HelpTip text="Menu items inside this zone: layout (List/Grid + cols×rows), per-attribute font size and color, and the items checklist." />
                <span className="sb-section-chevron">▶</span>
              </button>
              {openSections.items && (<div className="sb-section-body items-body">

              {/* ── Items control: layout (List/Grid + cols×rows) + visibility checkboxes ── */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #EBEBEB' }}>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  📋 Items Layout
                  <HelpTip text="How menu items are arranged INSIDE this zone. Choose List (vertical rows) or Grid (cards). Cols × Rows sets how many items fit. Toggle which fields show on each item — Photos, Name, Price, etc. The 🟦 placeholder card background can be dimmed with the slider." />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 7, overflow: 'hidden', flexShrink: 0 }}>
                    {[{ mode: 'list', label: '≡ List' }, { mode: 'grid', label: '⊞ Grid' }].map(({ mode, label }) => {
                      const active = (activeZone.display_mode || 'list') === mode;
                      return (
                        <button key={mode} onClick={() => updateZone('display_mode', mode)}
                          style={{ padding: '4px 10px', border: 'none', background: active ? '#1565C0' : '#fff', color: active ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 400 }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={rawZoneCols}
                    onChange={e => setRawZoneCols(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={() => { const v = Math.max(1, Math.min(10, parseInt(rawZoneCols) || 1)); setRawZoneCols(String(v)); updateZone('cols', v); }}
                    style={{ width: 42, textAlign: 'center', border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '4px 2px', fontSize: 13, background: '#fff' }} />
                  <span style={{ fontSize: 12, color: '#999' }}>cols ×</span>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={rawZoneRows}
                    onChange={e => setRawZoneRows(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={() => { const v = Math.max(1, parseInt(rawZoneRows) || 1); setRawZoneRows(String(v)); updateZone('rows', v); }}
                    style={{ width: 42, textAlign: 'center', border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '4px 2px', fontSize: 13, background: '#fff' }} />
                  <span style={{ fontSize: 12, color: '#999' }}>rows</span>
                  <span style={{ fontSize: 11, color: '#bbb' }}>{activeZone.cols * activeZone.rows} items</span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  {[
                    { key: 'show_photos',       icon: '📷', label: 'Photos'   },
                    { key: 'show_name',         icon: '🏷', label: 'Name'     },
                    { key: 'show_price',        icon: '💰', label: 'Price'    },
                    { key: 'show_variant_name', icon: '🔤', label: 'Var.Name' },
                    { key: 'show_desc',         icon: '📝', label: 'Desc'     },
                  ].map(({ key, icon, label }) => (
                    <label key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'pointer', userSelect: 'none', flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <input type="checkbox" checked={activeZone[key] !== false} onChange={e => updateZone(key, e.target.checked)} style={{ width: 13, height: 13, cursor: 'pointer', accentColor: '#1565C0', margin: 0 }} />
                        <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 300, color: '#888', textAlign: 'center', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{label}</span>
                    </label>
                  ))}
                  {/* Placeholder toggle + opacity slider */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 2, minWidth: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', userSelect: 'none' }}>
                      <input type="checkbox" checked={activeZone.show_placeholder !== false} onChange={e => updateZone('show_placeholder', e.target.checked)} style={{ width: 13, height: 13, cursor: 'pointer', accentColor: '#1565C0', margin: 0 }} />
                      <span style={{ fontSize: 14, lineHeight: 1 }}>🟦</span>
                    </label>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: activeZone.show_placeholder === false ? 0.45 : 1 }}>
                      <span style={{ fontSize: 9, fontWeight: 600, color: '#888', whiteSpace: 'nowrap' }}>BG</span>
                      <PctInput value={activeZone.placeholder_opacity ?? 100}
                        onChange={v => activeZone.show_placeholder !== false && updateZone('placeholder_opacity', v)} />
                    </span>
                  </div>
                </div>
              </div>

              {/* Item Font & Color — Name Font on top, then per-attribute (size + color + revert) cells */}
              {(() => {
                const tkE = makeTokens(form.bg_color || '#111827', form.accent_color);
                const themeColor = {
                  name_color:  tkE.text,
                  price_color: form.accent_color || '#F59E0B',
                  desc_color:  isLightBg(form.bg_color || '#111827') ? '#555555' : '#aaaaaa',
                  header_color: form.accent_color || '#F59E0B',
                };
                const sizeFields = [
                  { sizeKey: "name_font",   colorKey: "name_color",   label: "Name" },
                  { sizeKey: "price_font",  colorKey: "price_color",  label: "Price" },
                  { sizeKey: "desc_font",   colorKey: "desc_color",   label: "Desc" },
                  { sizeKey: "header_font", colorKey: "header_color", label: "Header" },
                ];
                return (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #EBEBEB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, color: "#757575", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      🔤 Item Font &amp; Color
                      <HelpTip text="Per-attribute size and color for menu items in this zone. Size: leave blank to auto-fit. Color: defaults to theme (BG/Accent); pick a swatch to override, or click ↺ to revert to theme." />
                    </div>
                    {/* Row A — Name Font family (full width) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 9, color: "#999", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, minWidth: 64 }}>Name Font</span>
                      <FontSelect
                        value={activeZone.name_font_family || ''}
                        onChange={f => updateZone('name_font_family', f)}
                        fonts={fontIndex}
                        preferredScript={lang === 'ar' ? 'arabic' : 'latin'}
                        width="100%"
                      />
                    </div>
                    {/* Row B — per-attribute size + color + revert (compact, aligned) */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {sizeFields.map(({ sizeKey, colorKey, label }) => {
                        const customColor = activeZone[colorKey];
                        const effectiveColor = customColor || themeColor[colorKey] || '#000000';
                        return (
                          <div key={sizeKey} style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: 'stretch', minWidth: 0 }}>
                            <span style={{ fontSize: 9, color: "#999", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, textAlign: 'center' }}>{label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
                              <input
                                type="number" min="1" step="1"
                                placeholder="auto"
                                value={activeZone[sizeKey] || ""}
                                onChange={e => updateZone(sizeKey, e.target.value)}
                                style={{ width: 40, border: "1.5px solid #E0E0E0", borderRadius: 5, padding: "4px 3px", fontSize: 12, background: "#fff", color: "#222", textAlign: 'center', boxSizing: 'border-box' }}
                              />
                              <input type="color"
                                value={effectiveColor}
                                onChange={e => updateZone(colorKey, e.target.value)}
                                title={customColor ? `Custom: ${customColor}` : `Theme: ${effectiveColor}`}
                                style={{ width: 22, height: 22, border: '1.5px solid #E0E0E0', borderRadius: 4, padding: 0, cursor: 'pointer', background: 'none', flexShrink: 0 }} />
                              <button onClick={() => updateZone(colorKey, '')}
                                disabled={!customColor}
                                title={customColor ? 'Reset to theme color' : 'Using theme color'}
                                style={{ width: 16, height: 22, border: '1.5px solid ' + (customColor ? '#1565C0' : '#E0E0E0'), background: customColor ? '#E3F2FD' : '#fff', borderRadius: 4, cursor: customColor ? 'pointer' : 'default', fontSize: 10, color: customColor ? '#1565C0' : '#ccc', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>↺</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <ItemPicker categories={menuData.categories} items={menuData.items}
                selectedIds={activeZone.item_ids || []} onChange={ids => updateZone('item_ids', ids)} lang={lang} />

              </div>)}
            </div>
          )}
        </div>

        {/* ── Preview (sticky on desktop so it stays visible while scrolling editor) ── */}
        <div ref={previewPaneRef} className="sb-preview" style={{
          flex: 1, minWidth: 0,
          position: isMobile ? 'static' : 'sticky',
          top: isMobile ? 'auto' : (headerH + 8),
          alignSelf: 'flex-start',
          zIndex: 1,
          overflow: 'hidden',  // safety net — never let canvas escape the pane during sidebar transitions
        }}>
          <div className="card" style={{ padding: isMobile ? 10 : 14, overflow: 'hidden', maxWidth: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#757575', textTransform: 'uppercase' }}>Preview</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Phase C MVP — playback head. ⏵ toggles a virtual clock that
                    feeds every layer's animation timing. The scrubber lets the
                    designer jump to a specific moment in the slide.  */}
                <button onClick={() => setIsPlaying(p => !p)}
                  title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
                  style={{ width: 28, height: 24, borderRadius: 6, border: '1.5px solid #6D28D9', background: isPlaying ? '#6D28D9' : '#fff', color: isPlaying ? '#fff' : '#6D28D9', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isPlaying ? '⏸' : '⏵'}
                </button>
                <input type="range" min="0" max={parseInt(form.duration_sec, 10) || 15} step="0.1"
                  value={Math.min(currentTime, parseInt(form.duration_sec, 10) || 15)}
                  onChange={e => { setIsPlaying(false); setCurrentTime(parseFloat(e.target.value)); }}
                  style={{ width: 110, accentColor: '#6D28D9' }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: '#666', minWidth: 38, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {currentTime.toFixed(1)}/{(parseInt(form.duration_sec, 10) || 15)}s
                </span>
                <button onClick={() => setZoomPct(p => Math.max(30, p - 10))} style={{ width: 24, height: 24, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>−</button>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#555', minWidth: 34, textAlign: 'center' }}>{zoomPct}%</span>
                <button onClick={() => setZoomPct(p => Math.min(200, p + 10))} style={{ width: 24, height: 24, borderRadius: 6, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>+</button>
                <button onClick={() => setZoomPct(100)} title="Reset zoom" style={{ width: 24, height: 24, borderRadius: 6, border: '1.5px solid #E0E0E0', background: zoomPct === 100 ? '#fff' : '#E3F2FD', cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#1565C0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↺</button>
                <span style={{ fontSize: 10, color: '#bbb' }}>{dim.w * 2}×{dim.h * 2}</span>
              </div>
            </div>
            <div ref={previewWrapRef} style={{ width: previewW, height: previewH, maxWidth: '100%', borderRadius: 8, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.22)', border: '1px solid rgba(0,0,0,0.08)' }}>
              {/* 2026-05-16: background-click on the slide canvas deselects all
                  layers and reopens the SLIDE section (mirrors the auto-fold
                  behaviour from layer-select but in the opposite direction).
                  Right-click on the background opens a paste menu. The handler
                  uses the data-slide-background tag (set by SlidePreview's root)
                  to tell empty-area clicks from clicks on a zone/text overlay. */}
              <div
                onClick={e => {
                  // Detect a click on the slide bg by walking up from e.target.
                  // The bg image / dim overlay / video element are children of
                  // SlidePreview's root (which carries data-slide-background)
                  // and don't carry their own pointer-event guards. closest()
                  // finds the bg ancestor for all those cases. Zone + text
                  // overlays call stopPropagation in their own contextmenu
                  // handlers so events never reach here from them.
                  const isBg = e.target === e.currentTarget
                    || (e.target && typeof e.target.closest === 'function'
                        && e.target.closest('[data-slide-background]'));
                  if (!isBg) return;
                  // Clear multi-select + slide-text selection, then reopen the
                  // SLIDE section and fold the others. The activeZoneIdx is
                  // left alone (the inspector still has a "primary" zone in
                  // mind even when nothing in the canvas is highlighted).
                  setSelectedZoneIds(new Set());
                  setSelectedSlideTextIdx(null);
                  setExpandedSlideTextIdx(null);
                  setOpenSections(s => ({
                    ...s,
                    slide: userTouchedSections.has('slide') ? s.slide : true,
                    text:  userTouchedSections.has('text')  ? s.text  : false,
                    zones: userTouchedSections.has('zones') ? s.zones : false,
                    items: userTouchedSections.has('items') ? s.items : false,
                  }));
                }}
                onContextMenu={e => {
                  // Detect a click on the slide bg by walking up from e.target.
                  // The bg image / dim overlay / video element are children of
                  // SlidePreview's root (which carries data-slide-background)
                  // and don't carry their own pointer-event guards. closest()
                  // finds the bg ancestor for all those cases. Zone + text
                  // overlays call stopPropagation in their own contextmenu
                  // handlers so events never reach here from them.
                  const isBg = e.target === e.currentTarget
                    || (e.target && typeof e.target.closest === 'function'
                        && e.target.closest('[data-slide-background]'));
                  if (!isBg) return;
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, kind: 'background' });
                }}
                style={{ position: 'relative', width: dim.w, height: dim.h, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                <SlidePreview restaurant={restaurant} zones={zonesWithItems}
                  zone_cols={form.zone_cols} zone_rows={form.zone_rows}
                  zone_mode={form.zone_mode || 'grid'}
                  bgColor={form.bg_color} accentColor={form.accent_color}
                  orientation={orientation} lang={lang}
                  slideBgImage={form.slide_bg_image}
                  slideBgOverlay={form.slide_bg_overlay ?? 50}
                  widthPx={parseInt(form.width_px, 10) || undefined}
                  heightPx={parseInt(form.height_px, 10) || undefined}
                  textLayers={form.text_layers || []}
                  slideKind={form.slide_kind}
                  videoPreviewPath={form.video_preview_path}
                  autoDarken={form.auto_darken}
                  currentTime={currentTime}
                  slideDuration={parseInt(form.duration_sec, 10) || 15} />
                {/* Helper: zone bounding rect in canvas px (depends on mode) */}
                {(() => null)()}
                {/* Text layer overlays for the active zone — drag in preview to move, corners to resize */}
                {(() => {
                  const az = form.zones[activeZoneIdx];
                  if (!az) return null;
                  const layers = az.text_layers || [];
                  if (layers.length === 0) return null;
                  // Compute zone canvas rect
                  let zRect;
                  if (form.zone_mode === 'free') {
                    zRect = { x: az.x || 0, y: az.y || 0, w: az.w || dim.w, h: az.h || dim.h };
                  } else {
                    const cols = Math.max(1, form.zone_cols || 1);
                    const rows = Math.max(1, form.zone_rows || 1);
                    const col = activeZoneIdx % cols;
                    const row = Math.floor(activeZoneIdx / cols);
                    zRect = { x: col * (dim.w / cols), y: row * (dim.h / rows), w: dim.w / cols, h: dim.h / rows };
                  }
                  const onLayerMouseDown = (e, layerIdx, action) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Click on a text layer in the preview = focus it everywhere:
                    //  open the ZONES editor section, the Text Layers list, and the layer's own card.
                    setOpenSections(s => s.zones ? s : { ...s, zones: true });
                    setTextLayersExpanded(true);
                    setExpandedLayerIdx(layerIdx);
                    if (action === 'select') return;
                    const rect = previewWrapRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    const startCx = (e.clientX - rect.left) / scale;
                    const startCy = (e.clientY - rect.top) / scale;
                    const orig = { ...layers[layerIdx] };
                    // Resolve numeric width/height from current values (account for '100%' string)
                    const origW = typeof orig.w === 'number' ? orig.w : Math.round(zRect.w - (orig.x || 0));
                    const origH = typeof orig.h === 'number' && orig.h > 0 ? orig.h : Math.round((orig.font_size || 24) * 1.6);
                    setDragState({
                      type: 'text', action, layerIdx,
                      startCx, startCy,
                      orig: { ...orig, w: origW, h: origH },
                      zRect,
                    });
                  };
                  return layers.map((layer, li) => {
                    const layerW = typeof layer.w === 'number' ? layer.w
                      : Math.max(20, zRect.w - (layer.x || 0));
                    const layerH = typeof layer.h === 'number' && layer.h > 0 ? layer.h
                      : Math.round((layer.font_size || 24) * 1.6);
                    const cLeft = zRect.x + (layer.x || 0);
                    const cTop = zRect.y + (layer.y || 0);
                    const isSel = expandedLayerIdx === li;
                    // Scale-compensate text-layer handles + outline so they stay
                    // a constant SCREEN size regardless of the slide scale.
                    const _scTl     = Math.max(scale, 0.01);
                    const handleSz  = 8 / _scTl;
                    const hh        = handleSz / 2;
                    return (
                      <div key={layer.id || `tl-${li}`}
                        onMouseDown={e => onLayerMouseDown(e, li, 'move')}
                        title={layer.name || (layer.text || '').split('\n')[0] || 'Text layer'}
                        style={{
                          position: 'absolute',
                          left: cLeft, top: cTop, width: layerW, height: layerH,
                          border: isSel ? `${1.5 / _scTl}px dashed #FF9800` : `${1 / _scTl}px dashed rgba(255,152,0,0.55)`,
                          background: isSel ? 'rgba(255,152,0,0.06)' : 'transparent',
                          cursor: 'move', zIndex: 30, boxSizing: 'border-box',
                        }}>
                        {isSel && (
                          <>
                            {[
                              { a: 'tl-nw', s: { left: -hh, top: -hh, cursor: 'nwse-resize' } },
                              { a: 'tl-ne', s: { right: -hh, top: -hh, cursor: 'nesw-resize' } },
                              { a: 'tl-sw', s: { left: -hh, bottom: -hh, cursor: 'nesw-resize' } },
                              { a: 'tl-se', s: { right: -hh, bottom: -hh, cursor: 'nwse-resize' } },
                              { a: 'tl-e',  s: { right: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' } },
                              { a: 'tl-w',  s: { left: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' } },
                              { a: 'tl-n',  s: { left: '50%', top: -hh, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
                              { a: 'tl-s',  s: { left: '50%', bottom: -hh, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
                            ].map(({ a, s }) => (
                              <div key={a}
                                onMouseDown={e => onLayerMouseDown(e, li, a)}
                                style={{
                                  position: 'absolute', width: handleSz, height: handleSz,
                                  background: '#fff', border: `${2 / _scTl}px solid #FF9800`, borderRadius: 2 / _scTl,
                                  boxSizing: 'border-box', zIndex: 31, ...s,
                                }} />
                            ))}
                          </>
                        )}
                      </div>
                    );
                  });
                })()}
                {/* Slide-level text-layer overlays — drag/resize handles (Phase 3) */}
                {(form.text_layers || []).map((layer, li) => {
                  const layerW = typeof layer.w === 'number' ? layer.w
                    : Math.max(20, dim.w - (layer.x || 0));
                  const layerH = typeof layer.h === 'number' && layer.h > 0 ? layer.h
                    : Math.round((layer.font_size || 24) * 1.6);
                  const cLeft = layer.x || 0;
                  const cTop  = layer.y || 0;
                  const isSel = selectedSlideTextIdx === li;
                  const _scTl    = Math.max(scale, 0.01);
                  const handleSz = 8 / _scTl;
                  const hh       = handleSz / 2;
                  const onSlMouseDown = (e, action) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Phase 4: auto-unfold Text, auto-fold Slide + Zones + Items — unless user manually toggled them.
                    setOpenSections(s => ({
                      ...s,
                      slide: userTouchedSections.has('slide') ? s.slide : false,
                      text:  userTouchedSections.has('text')  ? s.text  : true,
                      zones: userTouchedSections.has('zones') ? s.zones : false,
                      items: userTouchedSections.has('items') ? s.items : false,
                    }));
                    setSelectedSlideTextIdx(li);
                    setExpandedSlideTextIdx(li);
                    if (action === 'select') return;
                    const r = previewWrapRef.current?.getBoundingClientRect();
                    if (!r) return;
                    const startCx = (e.clientX - r.left) / scale;
                    const startCy = (e.clientY - r.top)  / scale;
                    const orig = { ...layer };
                    const origW = typeof orig.w === 'number' ? orig.w : Math.round(dim.w - (orig.x || 0));
                    const origH = typeof orig.h === 'number' && orig.h > 0 ? orig.h : Math.round((orig.font_size || 24) * 1.6);
                    setDragState({
                      type: 'sl-text', action, layerIdx: li,
                      startCx, startCy,
                      orig: { ...orig, w: origW, h: origH },
                      slideRect: { w: dim.w, h: dim.h },
                    });
                  };
                  return (
                    <div key={`sl-${layer.id || li}`}
                      onMouseDown={e => onSlMouseDown(e, 'move')}
                      onContextMenu={e => {
                        e.preventDefault(); e.stopPropagation();
                        setSelectedSlideTextIdx(li);
                        setExpandedSlideTextIdx(li);
                        setContextMenu({ x: e.clientX, y: e.clientY, kind: 'text', idx: li });
                      }}
                      title={layer.label || (layer.text || '').split('\n')[0] || 'Text layer'}
                      style={{
                        position: 'absolute',
                        left: cLeft, top: cTop, width: layerW, height: layerH,
                        border: isSel ? `${1.5 / _scTl}px dashed #1565C0` : `${1 / _scTl}px dashed rgba(21,101,192,0.45)`,
                        background: isSel ? 'rgba(21,101,192,0.06)' : 'transparent',
                        cursor: 'move', zIndex: 28, boxSizing: 'border-box',
                      }}>
                      <div style={{ position: 'absolute', top: -16 / _scTl, left: 0, fontSize: 10 / _scTl, fontWeight: 700, color: isSel ? '#1565C0' : '#888', background: 'rgba(255,255,255,0.92)', padding: `${1 / _scTl}px ${5 / _scTl}px`, borderRadius: 4 / _scTl, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                        {layer.label || 't?'}
                      </div>
                      {isSel && (
                        <>
                          {[
                            { a: 'tl-nw', s: { left: -hh, top: -hh, cursor: 'nwse-resize' } },
                            { a: 'tl-ne', s: { right: -hh, top: -hh, cursor: 'nesw-resize' } },
                            { a: 'tl-sw', s: { left: -hh, bottom: -hh, cursor: 'nesw-resize' } },
                            { a: 'tl-se', s: { right: -hh, bottom: -hh, cursor: 'nwse-resize' } },
                            { a: 'tl-e',  s: { right: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' } },
                            { a: 'tl-w',  s: { left: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' } },
                            { a: 'tl-n',  s: { left: '50%', top: -hh, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
                            { a: 'tl-s',  s: { left: '50%', bottom: -hh, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
                          ].map(({ a, s }) => (
                            <div key={a}
                              onMouseDown={e => onSlMouseDown(e, a)}
                              style={{
                                position: 'absolute', width: handleSz, height: handleSz,
                                background: '#fff', border: `${2 / _scTl}px solid #1565C0`, borderRadius: 2 / _scTl,
                                boxSizing: 'border-box', zIndex: 29, ...s,
                              }} />
                          ))}
                        </>
                      )}
                    </div>
                  );
                })}
                {/* Zone overlays — grid mode: simple click selection. Free mode: drag/resize handles. */}
                {form.zone_mode === 'free' ? (() => {
                  const onZoneMouseDown = (e, idx, action) => {
                    if (form.zones[idx]?.locked && action !== 'select') return;
                    e.preventDefault();
                    e.stopPropagation();
                    // Selecting a zone clears any slide-level text selection
                    setSelectedSlideTextIdx(null);
                    // Phase 4: auto-unfold Zones, auto-fold Slide + Text — unless user manually toggled them.
                    // Items stays as-is (often used together with zones to pick items into them).
                    setOpenSections(s => ({
                      ...s,
                      slide: userTouchedSections.has('slide') ? s.slide : false,
                      zones: userTouchedSections.has('zones') ? s.zones : true,
                      text:  userTouchedSections.has('text')  ? s.text  : false,
                    }));
                    // Shift+click toggles multi-selection without dragging
                    if (e.shiftKey && action !== 'move' /* corner/edge resize is single-zone */) {
                      // For non-move actions with shift, treat as plain (resize is per-zone)
                    }
                    if (e.shiftKey && (action === 'move' || action === 'select')) {
                      toggleZoneSelection(idx);
                      return; // don't start a drag on shift+click
                    }
                    // Plain click on an unselected zone replaces the selection
                    const z = form.zones[idx];
                    const k = zoneKey(z, idx);
                    if (!selectedZoneIds.has(k)) {
                      // If this zone is NOT in the existing multi-selection, replace selection
                      if (selectedZoneIds.size > 0) {
                        replaceZoneSelection(idx);
                      } else {
                        setActiveZoneIdx(idx);
                      }
                    } else {
                      setActiveZoneIdx(idx); // keep multi-selection but make this the primary editor target
                    }
                    if (action === 'select') return;
                    const rect = previewWrapRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    const startCx = (e.clientX - rect.left) / scale;
                    const startCy = (e.clientY - rect.top)  / scale;
                    // For multi-zone MOVE: capture origZone for every selected zone so we can
                    // translate them all by the same delta. Resize stays single-zone.
                    let multiOrig = null;
                    if (action === 'move') {
                      const sel = selectedZoneIds.has(k) && selectedZoneIds.size > 1
                        ? [...selectedZoneIds]
                        : null;
                      if (sel && sel.length > 1) {
                        multiOrig = form.zones.map((z2, i2) => ({
                          idx: i2, key: zoneKey(z2, i2),
                          orig: { x: z2.x || 0, y: z2.y || 0, w: z2.w || 0, h: z2.h || 0 },
                          included: sel.includes(zoneKey(z2, i2)) && !z2.locked,
                        })).filter(o => o.included);
                      }
                    }
                    setDragState({
                      action, idx,
                      startCx, startCy,
                      origZone: { ...form.zones[idx] },
                      multiOrig, // null for single-zone, or array for multi-move
                    });
                  };
                  return form.zones.map((zone, idx) => {
                    const isActive = idx === activeZoneIdx;
                    const isMultiSel = selectedZoneIds.has(zoneKey(zone, idx));
                    const isHighlighted = isActive || isMultiSel;
                    // Clamp zone box inside slide so previously-saved out-of-bounds
                    // zones (e.g. from older grid→free conversion rounding) display
                    // correctly. We only clamp for visual rendering; the saved data
                    // is corrected when the user moves/resizes the zone next.
                    const rawW = zone.w || dim.w;
                    const rawH = zone.h || dim.h;
                    const rawX = zone.x || 0;
                    const rawY = zone.y || 0;
                    const zx = Math.max(0, Math.min(rawX, dim.w - 1));
                    const zy = Math.max(0, Math.min(rawY, dim.h - 1));
                    const zw = Math.max(1, Math.min(rawW, dim.w - zx));
                    const zh = Math.max(1, Math.min(rawH, dim.h - zy));
                    const locked = !!zone.locked;
                    // Scale-compensate visual decorations so they keep a constant
                    // SCREEN size regardless of how small/large the slide is. The
                    // wrapper at line ~2817 has `transform: scale(${scale})` which
                    // would otherwise blow up handles to 40px+ on a small slide,
                    // covering the zone interior and eating click events.
                    const _sc       = Math.max(scale, 0.01);
                    const handleSz  = 10 / _sc;
                    const hh        = handleSz / 2;
                    const bw2       = 2 / _sc;
                    const bw3       = 3 / _sc;
                    const bw15      = 1.5 / _sc;
                    const handleStyle = (cur) => ({
                      position: 'absolute', width: handleSz, height: handleSz,
                      background: '#fff', border: `${bw2}px solid #2196F3`, borderRadius: 2 / _sc,
                      boxSizing: 'border-box', cursor: cur, zIndex: 12,
                    });
                    // Border styling: solid blue for active, dashed orange for multi-selected (not active),
                    // dashed grey for locked, faint blue otherwise.
                    const borderStr = locked
                      ? `${(isHighlighted ? 3 : 2) / _sc}px dashed #9E9E9E`
                      : isActive
                        ? `${bw2}px solid #2196F3`
                        : isMultiSel
                          ? `${bw2}px dashed #FF9800`
                          : `${bw15}px solid rgba(33,150,243,0.35)`;
                    return (
                      <div key={zone.id || idx}
                        onMouseDown={e => onZoneMouseDown(e, idx, locked ? 'select' : 'move')}
                        onClick={e => { if (!isActive) { setActiveZoneIdx(idx); setOpenSections(s => s.zones ? s : { ...s, zones: true }); } }}
                        onContextMenu={e => {
                          e.preventDefault(); e.stopPropagation();
                          setActiveZoneIdx(idx);
                          setContextMenu({ x: e.clientX, y: e.clientY, kind: 'zone', idx });
                        }}
                        style={{
                          position: 'absolute',
                          left: zx, top: zy, width: zw, height: zh,
                          border: borderStr,
                          boxSizing: 'border-box', zIndex: 11,
                          cursor: locked ? 'not-allowed' : 'move',
                          background: isMultiSel && !isActive ? 'rgba(255,152,0,0.06)' : 'transparent',
                        }}>
                        <div style={{ position: 'absolute', top: -22 / _sc, left: 0, fontSize: 11 / _sc, fontWeight: 700, color: isActive ? '#1565C0' : isMultiSel ? '#E65100' : '#888', background: 'rgba(255,255,255,0.92)', padding: `${1 / _sc}px ${6 / _sc}px`, borderRadius: 4 / _sc, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                          {locked && '🔒 '}{isMultiSel && !isActive && '✓ '}{zone.name || `Zone ${idx + 1}`} <span style={{ color: '#aaa', fontWeight: 400 }}>z{zone.z_index ?? 0}</span>
                        </div>
                        {isActive && !locked && (
                          <>
                            {[
                              { a: 'nw', s: { left: -hh, top: -hh, cursor: 'nwse-resize' } },
                              { a: 'n',  s: { left: '50%', top: -hh, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
                              { a: 'ne', s: { right: -hh, top: -hh, cursor: 'nesw-resize' } },
                              { a: 'e',  s: { right: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' } },
                              { a: 'se', s: { right: -hh, bottom: -hh, cursor: 'nwse-resize' } },
                              { a: 's',  s: { left: '50%', bottom: -hh, transform: 'translateX(-50%)', cursor: 'ns-resize' } },
                              { a: 'sw', s: { left: -hh, bottom: -hh, cursor: 'nesw-resize' } },
                              { a: 'w',  s: { left: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' } },
                            ].map(({ a, s }) => (
                              <div key={a}
                                onMouseDown={e => onZoneMouseDown(e, idx, a)}
                                style={{ ...handleStyle(s.cursor), ...s }} />
                            ))}
                          </>
                        )}
                      </div>
                    );
                  });
                })() : form.zones.length > 1 && (() => {
                  const cols = Math.max(1, form.zone_cols || 1);
                  const rows = Math.max(1, form.zone_rows || 1);
                  const zw = dim.w / cols;
                  const zh = dim.h / rows;
                  // Scale-compensate grid-overlay borders so they don't balloon on small slides.
                  const _scG = Math.max(scale, 0.01);
                  return form.zones.map((_, idx) => {
                    const zCol = idx % cols;
                    const zRow = Math.floor(idx / cols);
                    const isActive = idx === activeZoneIdx;
                    return (
                      <div key={idx} onClick={() => {
                          setActiveZoneIdx(idx);
                          setSelectedSlideTextIdx(null);
                          setOpenSections(s => ({
                            ...s,
                            slide: userTouchedSections.has('slide') ? s.slide : false,
                            zones: userTouchedSections.has('zones') ? s.zones : true,
                            text:  userTouchedSections.has('text')  ? s.text  : false,
                          }));
                        }}
                        style={{
                          position: 'absolute', cursor: 'pointer',
                          left: zCol * zw, top: zRow * zh, width: zw, height: zh,
                          border: isActive ? `${4 / _scG}px solid #2196F3` : `${2 / _scG}px solid transparent`,
                          boxShadow: isActive ? `inset 0 0 0 ${2 / _scG}px rgba(255,255,255,0.55)` : 'none',
                          boxSizing: 'border-box', zIndex: 10,
                          transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = '#2196F388'; e.currentTarget.style.borderWidth = `${2 / _scG}px`; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'transparent'; }}
                      />
                    );
                  });
                })()}
              </div>
              {/* Free-mode drag capture overlay (covers viewport during drag for stable mouse tracking) */}
              {dragState && (() => {
                // Helper: pick the closest snap target within threshold
                const pickSnap = (val, targets, threshold) => {
                  let best = null, bestDist = threshold;
                  for (const t of targets) {
                    const d = Math.abs(val - t);
                    if (d < bestDist) { bestDist = d; best = t; }
                  }
                  return best;
                };
                const onDragMove = (e) => {
                  const rect = previewWrapRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  const cx = (e.clientX - rect.left) / scale;
                  const cy = (e.clientY - rect.top)  / scale;
                  let dx = cx - dragState.startCx;
                  let dy = cy - dragState.startCy;
                  const minSz = 20;
                  const SNAP = 6;
                  const isShift = !!e.shiftKey;

                  if (dragState.type === 'text') {
                    // Drag/resize a text layer (coords are zone-relative)
                    const ol = dragState.orig;
                    const a = dragState.action;
                    // Shift = constrain proportional (resize) / lock to axis (move)
                    if (isShift && a === 'move') {
                      if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0;
                    }
                    let nx = ol.x || 0, ny = ol.y || 0, nw = ol.w || 100, nh = ol.h || 24;
                    if (a === 'move') { nx = (ol.x || 0) + dx; ny = (ol.y || 0) + dy; }
                    else if (a === 'tl-n')  { ny = (ol.y || 0) + dy; nh = (ol.h || 0) - dy; }
                    else if (a === 'tl-s')  { nh = (ol.h || 0) + dy; }
                    else if (a === 'tl-w')  { nx = (ol.x || 0) + dx; nw = (ol.w || 0) - dx; }
                    else if (a === 'tl-e')  { nw = (ol.w || 0) + dx; }
                    else if (a === 'tl-nw') { nx = (ol.x || 0) + dx; ny = (ol.y || 0) + dy; nw = (ol.w || 0) - dx; nh = (ol.h || 0) - dy; }
                    else if (a === 'tl-ne') { ny = (ol.y || 0) + dy; nw = (ol.w || 0) + dx; nh = (ol.h || 0) - dy; }
                    else if (a === 'tl-sw') { nx = (ol.x || 0) + dx; nw = (ol.w || 0) - dx; nh = (ol.h || 0) + dy; }
                    else if (a === 'tl-se') { nw = (ol.w || 0) + dx; nh = (ol.h || 0) + dy; }
                    // Shift = constrain aspect ratio for corner resize
                    if (isShift && (a === 'tl-nw' || a === 'tl-ne' || a === 'tl-sw' || a === 'tl-se')) {
                      const ratio = (ol.w || 1) / (ol.h || 1);
                      if (Math.abs(nw - ol.w) > Math.abs(nh - ol.h)) {
                        const newH = nw / ratio;
                        if (a === 'tl-nw' || a === 'tl-ne') ny = (ol.y || 0) + (ol.h || 0) - newH;
                        nh = newH;
                      } else {
                        const newW = nh * ratio;
                        if (a === 'tl-nw' || a === 'tl-sw') nx = (ol.x || 0) + (ol.w || 0) - newW;
                        nw = newW;
                      }
                    }
                    if (nw < minSz) { nx = (ol.x || 0) + ((ol.w || 0) - minSz); nw = minSz; }
                    if (nh < minSz) { ny = (ol.y || 0) + ((ol.h || 0) - minSz); nh = minSz; }
                    // Snap to zone edges
                    const zRect = dragState.zRect || { x: 0, y: 0, w: dim.w, h: dim.h };
                    if (Math.abs(nx) < SNAP) nx = 0;
                    if (Math.abs(ny) < SNAP) ny = 0;
                    if (Math.abs(nx + nw - zRect.w) < SNAP) nx = zRect.w - nw;
                    if (Math.abs(ny + nh - zRect.h) < SNAP) ny = zRect.h - nh;
                    if (a === 'move') {
                      const mx = nx + nw / 2, my = ny + nh / 2;
                      if (Math.abs(mx - zRect.w / 2) < SNAP) nx = zRect.w / 2 - nw / 2;
                      if (Math.abs(my - zRect.h / 2) < SNAP) ny = zRect.h / 2 - nh / 2;
                    }
                    setForm(f => ({
                      ...f,
                      zones: f.zones.map((z, zi) => {
                        if (zi !== activeZoneIdx) return z;
                        const layers = (z.text_layers || []).map((l, li) => li === dragState.layerIdx
                          ? { ...l, x: Math.round(nx), y: Math.round(ny), w: Math.round(nw), h: Math.round(nh) }
                          : l);
                        return { ...z, text_layers: layers };
                      }),
                    }));
                    return;
                  }

                  if (dragState.type === 'sl-text') {
                    // Slide-level text — same math as 'text' but coords are slide-relative
                    // and writes to form.text_layers instead of zone.text_layers.
                    const ol = dragState.orig;
                    const a = dragState.action;
                    if (isShift && a === 'move') {
                      if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0;
                    }
                    let nx = ol.x || 0, ny = ol.y || 0, nw = ol.w || 100, nh = ol.h || 24;
                    if (a === 'move') { nx = (ol.x || 0) + dx; ny = (ol.y || 0) + dy; }
                    else if (a === 'tl-n')  { ny = (ol.y || 0) + dy; nh = (ol.h || 0) - dy; }
                    else if (a === 'tl-s')  { nh = (ol.h || 0) + dy; }
                    else if (a === 'tl-w')  { nx = (ol.x || 0) + dx; nw = (ol.w || 0) - dx; }
                    else if (a === 'tl-e')  { nw = (ol.w || 0) + dx; }
                    else if (a === 'tl-nw') { nx = (ol.x || 0) + dx; ny = (ol.y || 0) + dy; nw = (ol.w || 0) - dx; nh = (ol.h || 0) - dy; }
                    else if (a === 'tl-ne') { ny = (ol.y || 0) + dy; nw = (ol.w || 0) + dx; nh = (ol.h || 0) - dy; }
                    else if (a === 'tl-sw') { nx = (ol.x || 0) + dx; nw = (ol.w || 0) - dx; nh = (ol.h || 0) + dy; }
                    else if (a === 'tl-se') { nw = (ol.w || 0) + dx; nh = (ol.h || 0) + dy; }
                    if (isShift && (a === 'tl-nw' || a === 'tl-ne' || a === 'tl-sw' || a === 'tl-se')) {
                      const ratio = (ol.w || 1) / (ol.h || 1);
                      if (Math.abs(nw - ol.w) > Math.abs(nh - ol.h)) {
                        const newH = nw / ratio;
                        if (a === 'tl-nw' || a === 'tl-ne') ny = (ol.y || 0) + (ol.h || 0) - newH;
                        nh = newH;
                      } else {
                        const newW = nh * ratio;
                        if (a === 'tl-nw' || a === 'tl-sw') nx = (ol.x || 0) + (ol.w || 0) - newW;
                        nw = newW;
                      }
                    }
                    if (nw < minSz) { nx = (ol.x || 0) + ((ol.w || 0) - minSz); nw = minSz; }
                    if (nh < minSz) { ny = (ol.y || 0) + ((ol.h || 0) - minSz); nh = minSz; }
                    const sRect = dragState.slideRect || { w: dim.w, h: dim.h };
                    if (Math.abs(nx) < SNAP) nx = 0;
                    if (Math.abs(ny) < SNAP) ny = 0;
                    if (Math.abs(nx + nw - sRect.w) < SNAP) nx = sRect.w - nw;
                    if (Math.abs(ny + nh - sRect.h) < SNAP) ny = sRect.h - nh;
                    if (a === 'move') {
                      const mx = nx + nw / 2, my = ny + nh / 2;
                      if (Math.abs(mx - sRect.w / 2) < SNAP) nx = sRect.w / 2 - nw / 2;
                      if (Math.abs(my - sRect.h / 2) < SNAP) ny = sRect.h / 2 - nh / 2;
                    }
                    setForm(f => ({
                      ...f,
                      text_layers: (f.text_layers || []).map((l, li) => li === dragState.layerIdx
                        ? { ...l, x: Math.round(nx), y: Math.round(ny), w: Math.round(nw), h: Math.round(nh) }
                        : l),
                    }));
                    return;
                  }

                  // Otherwise: zone drag/resize
                  const oz = dragState.origZone;
                  const action = dragState.action;
                  // Shift = constrain to axis (move) / proportional (corner resize)
                  if (isShift && action === 'move') {
                    if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0;
                  }
                  let nx = oz.x || 0, ny = oz.y || 0, nw = oz.w || 100, nh = oz.h || 100;
                  switch (action) {
                    case 'move': nx = (oz.x || 0) + dx; ny = (oz.y || 0) + dy; break;
                    case 'n':  ny = (oz.y || 0) + dy; nh = (oz.h || 0) - dy; break;
                    case 's':  nh = (oz.h || 0) + dy; break;
                    case 'w':  nx = (oz.x || 0) + dx; nw = (oz.w || 0) - dx; break;
                    case 'e':  nw = (oz.w || 0) + dx; break;
                    case 'nw': nx = (oz.x || 0) + dx; ny = (oz.y || 0) + dy; nw = (oz.w || 0) - dx; nh = (oz.h || 0) - dy; break;
                    case 'ne': ny = (oz.y || 0) + dy; nw = (oz.w || 0) + dx; nh = (oz.h || 0) - dy; break;
                    case 'sw': nx = (oz.x || 0) + dx; nw = (oz.w || 0) - dx; nh = (oz.h || 0) + dy; break;
                    case 'se': nw = (oz.w || 0) + dx; nh = (oz.h || 0) + dy; break;
                    default: break;
                  }
                  // Shift = constrain aspect ratio for corner zone resize
                  if (isShift && (action === 'nw' || action === 'ne' || action === 'sw' || action === 'se')) {
                    const ratio = (oz.w || 1) / (oz.h || 1);
                    if (Math.abs(nw - (oz.w || 0)) > Math.abs(nh - (oz.h || 0))) {
                      const newH = nw / ratio;
                      if (action === 'nw' || action === 'ne') ny = (oz.y || 0) + (oz.h || 0) - newH;
                      nh = newH;
                    } else {
                      const newW = nh * ratio;
                      if (action === 'nw' || action === 'sw') nx = (oz.x || 0) + (oz.w || 0) - newW;
                      nw = newW;
                    }
                  }
                  const minZ = 30;
                  if (nw < minZ) { nx = (oz.x || 0) + ((oz.w || 0) - minZ); nw = minZ; }
                  if (nh < minZ) { ny = (oz.y || 0) + ((oz.h || 0) - minZ); nh = minZ; }

                  // Build snap targets: canvas edges/center + every other zone's edges/center
                  const xTargets = [0, dim.w, dim.w / 2];
                  const yTargets = [0, dim.h, dim.h / 2];
                  if (form.zone_mode === 'free') {
                    form.zones.forEach((z, zi) => {
                      if (zi === dragState.idx) return;
                      const ozx = z.x || 0, ozy = z.y || 0, ozw = z.w || 0, ozh = z.h || 0;
                      xTargets.push(ozx, ozx + ozw, ozx + ozw / 2);
                      yTargets.push(ozy, ozy + ozh, ozy + ozh / 2);
                    });
                  }
                  // For move: snap left/right/center independently
                  if (action === 'move') {
                    const sLeft = pickSnap(nx, xTargets, SNAP);
                    if (sLeft !== null) nx = sLeft;
                    else {
                      const sRight = pickSnap(nx + nw, xTargets, SNAP);
                      if (sRight !== null) nx = sRight - nw;
                      else {
                        const sCenter = pickSnap(nx + nw / 2, xTargets, SNAP);
                        if (sCenter !== null) nx = sCenter - nw / 2;
                      }
                    }
                    const sTop = pickSnap(ny, yTargets, SNAP);
                    if (sTop !== null) ny = sTop;
                    else {
                      const sBot = pickSnap(ny + nh, yTargets, SNAP);
                      if (sBot !== null) ny = sBot - nh;
                      else {
                        const sCenter = pickSnap(ny + nh / 2, yTargets, SNAP);
                        if (sCenter !== null) ny = sCenter - nh / 2;
                      }
                    }
                  } else {
                    // Resize: snap the moving edge(s) to nearest target
                    if (action === 'w' || action === 'nw' || action === 'sw') {
                      const s = pickSnap(nx, xTargets, SNAP);
                      if (s !== null) { nw = nw + (nx - s); nx = s; }
                    }
                    if (action === 'e' || action === 'ne' || action === 'se') {
                      const s = pickSnap(nx + nw, xTargets, SNAP);
                      if (s !== null) nw = s - nx;
                    }
                    if (action === 'n' || action === 'nw' || action === 'ne') {
                      const s = pickSnap(ny, yTargets, SNAP);
                      if (s !== null) { nh = nh + (ny - s); ny = s; }
                    }
                    if (action === 's' || action === 'sw' || action === 'se') {
                      const s = pickSnap(ny + nh, yTargets, SNAP);
                      if (s !== null) nh = s - ny;
                    }
                  }
                  // Compute the actual delta we ended up applying to the primary zone (after snap/clamp).
                  // For multi-zone MOVE: translate every other selected zone by the same delta from its
                  // captured origin. (Resize is single-zone.)
                  const appliedDx = nx - (oz.x || 0);
                  const appliedDy = ny - (oz.y || 0);
                  setForm(f => ({
                    ...f,
                    zones: f.zones.map((z, i) => {
                      if (i === dragState.idx) {
                        return { ...z, x: Math.round(nx), y: Math.round(ny), w: Math.round(nw), h: Math.round(nh) };
                      }
                      // Multi-zone move: apply delta to each other selected zone from its captured origin
                      if (action === 'move' && Array.isArray(dragState.multiOrig)) {
                        const entry = dragState.multiOrig.find(o => o.idx === i);
                        if (entry) {
                          return {
                            ...z,
                            x: Math.round((entry.orig.x || 0) + appliedDx),
                            y: Math.round((entry.orig.y || 0) + appliedDy),
                          };
                        }
                      }
                      return z;
                    }),
                  }));
                };
                const onDragEnd = () => setDragState(null);
                const cursorMap = { move: 'move', n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize',
                  'tl-n': 'ns-resize', 'tl-s': 'ns-resize', 'tl-e': 'ew-resize', 'tl-w': 'ew-resize', 'tl-nw': 'nwse-resize', 'tl-se': 'nwse-resize', 'tl-ne': 'nesw-resize', 'tl-sw': 'nesw-resize' };
                return (
                  <div onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
                    style={{ position: 'fixed', inset: 0, zIndex: 99999, cursor: cursorMap[dragState.action] || 'move', userSelect: 'none' }} />
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* ── Fullscreen live preview ── */}
      {showFullPreview && (() => {
        // Scale slide to fit viewport while preserving aspect ratio (no upscale beyond 100% so it stays crisp)
        const fpScaleW = (winW - 40) / dim.w;
        const fpScaleH = (winH - 80) / dim.h;
        const fpScale  = Math.max(0.1, Math.min(fpScaleW, fpScaleH));
        const fpW = Math.round(dim.w * fpScale);
        const fpH = Math.round(dim.h * fpScale);
        return (
          <div onClick={() => setShowFullPreview(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <button onClick={e => { e.stopPropagation(); setShowFullPreview(false); }}
              style={{ position: 'absolute', top: 16, right: 16, padding: '8px 14px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
              ✕ Close (Esc)
            </button>
            <div style={{ position: 'absolute', top: 18, left: 18, color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 500 }}>
              {form.slide_name || '(unnamed)'} · {dim.w * 2}×{dim.h * 2} · {Math.round(fpScale * 100)}%
            </div>
            <div onClick={e => e.stopPropagation()}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, cursor: 'default' }}>
              <div style={{ width: fpW, height: fpH, boxShadow: '0 12px 48px rgba(0,0,0,0.6)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: dim.w, height: dim.h, transform: `scale(${fpScale})`, transformOrigin: 'top left' }}>
                  <SlidePreview restaurant={restaurant} zones={zonesWithItems}
                    zone_cols={form.zone_cols} zone_rows={form.zone_rows}
                    zone_mode={form.zone_mode || 'grid'}
                    bgColor={form.bg_color} accentColor={form.accent_color}
                    orientation={orientation} lang={lang}
                    slideBgImage={form.slide_bg_image}
                    slideBgOverlay={form.slide_bg_overlay ?? 50}
                    widthPx={parseInt(form.width_px, 10) || undefined}
                    heightPx={parseInt(form.height_px, 10) || undefined}
                    textLayers={form.text_layers || []}
                    slideKind={form.slide_kind}
                    videoPreviewPath={form.video_preview_path}
                    autoDarken={form.auto_darken}
                    currentTime={currentTime}
                    slideDuration={parseInt(form.duration_sec, 10) || 15} />
                </div>
              </div>
              {/* 2026-05-16 — playback toolbar on the fullscreen preview too.
                  Same currentTime that drives the inline preview so toggling
                  fullscreen doesn't reset the playhead. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.06)', padding: '6px 12px', borderRadius: 8, backdropFilter: 'blur(8px)' }}>
                <button onClick={() => setIsPlaying(p => !p)}
                  title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
                  style={{ width: 36, height: 28, borderRadius: 6, border: '1.5px solid rgba(255,255,255,0.4)', background: isPlaying ? '#6D28D9' : 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                  {isPlaying ? '⏸' : '⏵'}
                </button>
                <input type="range" min="0" max={parseInt(form.duration_sec, 10) || 15} step="0.1"
                  value={Math.min(currentTime, parseInt(form.duration_sec, 10) || 15)}
                  onChange={e => { setIsPlaying(false); setCurrentTime(parseFloat(e.target.value)); }}
                  style={{ width: Math.min(360, fpW - 120), accentColor: '#6D28D9' }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.85)', minWidth: 64, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {currentTime.toFixed(1)} / {(parseInt(form.duration_sec, 10) || 15)} s
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Right-click context menu (2026-05-16) ── */}
      {contextMenu && (() => {
        const close = () => setContextMenu(null);
        const clip  = readSlideClipboard();
        const items = [];
        if (contextMenu.kind === 'zone') {
          items.push(
            { label: '📋 Copy',      shortcut: 'Ctrl+C', onClick: () => { copyZoneAt(contextMenu.idx); close(); } },
            { label: '✂ Cut',        shortcut: 'Ctrl+X', onClick: () => { cutZoneAt(contextMenu.idx); close(); } },
            { label: '🧬 Duplicate', shortcut: 'Ctrl+D', onClick: () => { duplicateZoneAt(contextMenu.idx); close(); } },
            { label: '🗑 Delete',    shortcut: 'Del',     danger: true, onClick: () => { close(); deleteSelectedZones(); } },
          );
        } else if (contextMenu.kind === 'text') {
          items.push(
            { label: '📋 Copy',      shortcut: 'Ctrl+C', onClick: () => { copySlideTextAt(contextMenu.idx); close(); } },
            { label: '✂ Cut',        shortcut: 'Ctrl+X', onClick: () => { cutSlideTextAt(contextMenu.idx); close(); } },
            { label: '🧬 Duplicate', shortcut: 'Ctrl+D', onClick: () => { duplicateSlideTextLayer(contextMenu.idx); close(); } },
            { label: '🗑 Delete',    shortcut: 'Del',     danger: true,
              onClick: () => { close(); if (window.confirm('Delete this text layer?')) deleteSlideTextLayer(contextMenu.idx); } },
          );
        }
        // Paste row — always shown on background menu (greyed when clipboard
        // is empty so designers know the feature exists). Zone/text right-click
        // only shows Paste when there's something to paste.
        if (clip) {
          items.push({
            label: clip.kind === 'zone' ? '📎 Paste zone' : '📎 Paste text',
            shortcut: 'Ctrl+V',
            onClick: () => { pasteClipboard(); close(); },
          });
        } else if (contextMenu.kind === 'background') {
          items.push({ label: '📎 Paste (clipboard empty)', shortcut: 'Ctrl+V', disabled: true });
        }
        // 2026-05-16 — right-click on the slide background also offers quick
        // "+ Text" / "+ Zone" creators, plus shortcut access to the slide bg
        // (image or video, depending on slide_kind). Each menu item unfolds
        // its parent section so the new layer is immediately editable.
        if (contextMenu.kind === 'background') {
          items.push({
            label: '➕ Add text',
            onClick: () => {
              addSlideTextLayer();
              setOpenSections(s => ({ ...s, text: true }));
              close();
            },
          });
          if (form.zone_mode === 'free') {
            items.push({
              label: '➕ Add zone',
              onClick: () => {
                addFreeZone();
                setOpenSections(s => ({ ...s, zones: true }));
                close();
              },
            });
          }
          // Slide bg shortcut — splits by kind. Video slides surface the
          // upload/replace/remove flow that lives in the SLIDE section's
          // 🎬 Background row; image slides hit the same handler the
          // slide-bg uploader does.
          if (form.slide_kind === 'video') {
            if (!form.video_path) {
              items.push({
                label: '🎬 Upload video bg',
                onClick: () => {
                  if (!selectedId) { toast.error('Save the slide first, then upload the video'); close(); return; }
                  videoFileRef.current?.click();
                  close();
                },
              });
            } else {
              items.push({ label: '🔄 Replace video bg', onClick: () => { videoFileRef.current?.click(); close(); } });
              items.push({ label: '🗑 Remove video bg', danger: true, onClick: () => { close(); handleVideoClear(); } });
            }
          } else {
            if (!form.slide_bg_image) {
              items.push({
                label: '🖼 Upload bg image',
                onClick: () => { slideBgFileRef.current?.click(); close(); },
              });
            } else {
              items.push({
                label: '🗑 Remove bg image', danger: true,
                onClick: () => { setForm(f => ({ ...f, slide_bg_image: '' })); close(); },
              });
            }
          }
        }
        // Position-clamp so the menu doesn't escape the viewport.
        const menuW = 200, menuH = items.length * 32 + 8;
        const px = Math.min(contextMenu.x, window.innerWidth  - menuW - 8);
        const py = Math.min(contextMenu.y, window.innerHeight - menuH - 8);
        return (
          <>
            <div onClick={close} onContextMenu={e => { e.preventDefault(); close(); }}
              style={{ position: 'fixed', inset: 0, zIndex: 9000 }} />
            <div style={{
              position: 'fixed', left: px, top: py, width: menuW, zIndex: 9001,
              background: '#fff', border: '1px solid #E0E0E0', borderRadius: 8,
              boxShadow: '0 6px 24px rgba(0,0,0,0.18)', padding: 4, fontSize: 13,
            }}>
              {items.map((it, i) => (
                <button key={i} onClick={it.disabled ? undefined : it.onClick} disabled={!!it.disabled}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '6px 10px', border: 'none', background: 'transparent',
                    cursor: it.disabled ? 'default' : 'pointer', borderRadius: 5,
                    color: it.disabled ? '#aaa' : (it.danger ? '#DC2626' : '#222'),
                    fontWeight: it.danger ? 600 : 400, textAlign: 'left',
                  }}
                  onMouseEnter={e => { if (!it.disabled) e.currentTarget.style.background = it.danger ? '#FEF2F2' : '#F3F4F6'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <span>{it.label}</span>
                  {it.shortcut && <span style={{ fontSize: 10, color: '#999' }}>{it.shortcut}</span>}
                </button>
              ))}
            </div>
          </>
        );
      })()}

      {/* ── Export overlay ── */}
      {exportOverlay && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.65)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
          {/* During video compose, show a real progress bar driven by the
              backend's ffmpeg progress events. Other phases (rendering,
              saving JPEG, etc.) keep the indeterminate spinner. */}
          {composePct == null ? (
            <div style={{ width: 52, height: 52, border: '5px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <div style={{ width: 320, maxWidth: 'calc(100vw - 60px)' }}>
              <div style={{ width: '100%', height: 14, borderRadius: 7, background: 'rgba(255,255,255,0.12)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.18)' }}>
                <div style={{ width: `${composePct}%`, height: '100%', background: 'linear-gradient(90deg,#6D28D9,#A78BFA)', transition: 'width 0.4s ease' }} />
              </div>
              <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 600, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{composePct}%</div>
            </div>
          )}
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>{exportOverlay}</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>Please wait — do not close this tab</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* ── Send to TV modal ── */}
      {showSendTV && (
        <SendToTVModal brandId={restaurant?.id} slideId={selectedId} slideFilename={slideFileName}
          orientation={orientation} userPass={userPass} username={user?.username}
          onClose={() => setShowSendTV(false)} />
      )}

      {/* ── Brand mode: Send to TV modal ── */}
      {showBrandSendTV && selectedId && (
        <BrandSendToTVModal
          slides={slides}
          slideIds={[selectedId]}
          brandId={brandId}
          onClose={() => setShowBrandSendTV(false)}
          onSent={(slideId, sendTargets) => setSlides(prev => prev.map(s => s.id === slideId ? { ...s, send_targets: sendTargets } : s))}
        />
      )}

      {/* ── SlideBuilder restore file input ── */}
      <input ref={sbRestoreFileRef} type="file" accept=".json" style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) handleBrandRestoreFile(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
}
